import React, { useState, useEffect, useMemo, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { 
  Wallet, DollarSign, ArrowUpRight, ArrowDownLeft, Plus, Trash2, Edit3, 
  Sparkles, Search, Filter, Calendar, CreditCard, ShoppingBag, PieChart, 
  CheckCircle2, AlertCircle, RefreshCw, ChevronDown, ChevronUp, ChevronRight, ChevronLeft, Tag, FileText, 
  TrendingUp, Target, Landmark, ShieldCheck, Download, Layers, Receipt, ArrowRight, ArrowDownRight, History,
  TrendingDown, Coins, Home, Car, Plane, AlertTriangle, Zap, Shirt, GraduationCap,
  Users, PiggyBank, BarChart3, Calculator, Sliders, Percent, Utensils, HeartPulse,
  CheckSquare, Square, FileSpreadsheet, Upload, Check, Copy, Send, Bot, Brain, MessageSquare, Lightbulb, RotateCcw, ShieldAlert, HelpCircle, Activity,
  Laptop, Armchair, Dog, Wrench, Gift, Globe, Paperclip, Image as ImageIcon, Film, ExternalLink, Eye, X, Video, Presentation, UploadCloud, Play, FileUp, Link as LinkIcon, Loader2
} from 'lucide-react';
import { 
  FinanceTransaction, FinanceTransactionType, FinancePaymentMethod, FinanceCategory, 
  InvoiceItem, FinancialGoal, BudgetConfig, SessionAttachment, SharedFileRecord 
} from '../types';
import { 
  subscribeToFinanceData, addTransaction, addBatchTransactions, updateTransaction, deleteTransaction, 
  deleteTransactionsByMonth, deleteTransactionsByYear, deleteTransactionsByIds, deleteAllTransactions,
  addFinancialGoal, updateFinancialGoal, deleteFinancialGoal, depositToGoal, updateBudgetConfig, updateUserCards,
  getAdvisorChatsFromCloudD1, saveAdvisorChatsToCloudD1
} from '../lib/financeStorage';

import { useLanguage, formatNumberByLang } from '../lib/i18n';
import VoiceInputButton from './VoiceInputButton';
import { formatGregorianDate } from '../lib/dateUtils';
import { sendWorkerAiFullRequest } from '../lib/workerAi';
import { uploadFileToWorker, getWorkerFileUrl, deleteFileFromWorker } from '../lib/workerFileStorage';
import { saveFileRecordToCloud } from '../lib/cloudFileStorage';
import { triggerFileDownload, extractTextFromFile, extractTextFromImage, extractTextFromPdf } from '../lib/attachmentStorage';

// Helper to convert Eastern Arabic and Persian digits to Western digits
function convertFarsiArabicDigitsToEnglish(str: string): string {
  if (!str) return '';
  const farsiDigits = [/۰/g, /۱/g, /۲/g, /۳/g, /۴/g, /۵/g, /۶/g, /۷/g, /۸/g, /۹/g];
  const arabicDigits = [/٠/g, /١/g, /٢/g, /٣/g, /٤/g, /٥/g, /٦/g, /٧/g, /٨/g, /٩/g];
  let res = str;
  for (let i = 0; i < 10; i++) {
    res = res.replace(farsiDigits[i], String(i)).replace(arabicDigits[i], String(i));
  }
  return res;
}

// Helper to robustly parse European & Swedish space-separated numbers (e.g. "8 990,00" -> 8990.00, "9 489,00" -> 9489.00)
function cleanAndParseFloat(val: any): number {
  if (typeof val === 'number') return isNaN(val) ? 0 : val;
  if (!val) return 0;
  let str = convertFarsiArabicDigitsToEnglish(String(val).trim());
  // Strip currency words and symbols
  str = str.replace(/(?:SEK|kr|EUR|USD|€|\$|ریال|تومان|Moms|VAT|%)/gi, '').trim();
  // Strip spaces inside numbers between digits (e.g. "8 990,00" -> "8990,00", "9 489,00" -> "9489,00")
  str = str.replace(/(\d)\s+(\d)/g, '$1$2').replace(/(\d)\s+(\d)/g, '$1$2');

  if (str.includes(',') && !str.includes('.')) {
    str = str.replace(',', '.');
  } else if (str.includes(',') && str.includes('.')) {
    if (str.indexOf(',') < str.indexOf('.')) {
      str = str.replace(/,/g, '');
    } else {
      str = str.replace(/\./g, '').replace(',', '.');
    }
  }
  const match = str.match(/[-+]?\d+(?:\.\d+)?/);
  return match ? parseFloat(match[0]) : 0;
}

// Helper to filter out non-item invoice metadata lines (totals, taxes, payment info, legal disclaimers, addresses)
function isRealLineItem(name: string): boolean {
  if (!name || name.trim().length < 2) return false;
  const lower = name.toLowerCase().trim();

  const blacklist = [
    /totalt|total|summa|subtotal|grand total|att betala|moms|vat|belopp|paid amount|remaining amount|payment status|fully paid|betalt belopp|sum total/i,
    /fakturanummer|datum|butik|valuta|betalningsmetod|kort|mastercard|visa|swish|tid|auktoriseringskod|tender|referens|org\s*nr|moms\s*nr/i,
    /kvittot|orderbekräftelse|villkor|godkänd|juridiskt|öppet köp|artikel\s+beskrivning|vemdalsgatan|vällingby|salim|zarepour|nasrabadi/i
  ];

  for (const pat of blacklist) {
    if (pat.test(lower)) return false;
  }
  return true;
}

// Normalizes and validates extracted invoice object
function normalizeInvoicePayload(raw: any, defaultCurrency = 'SEK', defaultCard = 'Handelsbanken Debit'): any {
  if (!raw || typeof raw !== 'object') return null;

  let title = (raw.title || raw.store || raw.store_name || raw.merchant || raw.shop || 'فاکتور فروشگاه').trim();
  if (title === 'نام فروشگاه یا فاکتور' || title === 'نام فروشگاه' || title === 'فاکتور خرید') {
    title = 'فاکتور خرید';
  }

  let currency = raw.currency || defaultCurrency;
  if (/ریال|rial|irr/i.test(currency)) currency = 'ریال';
  else if (/تومان|toman/i.test(currency)) currency = 'تومان';
  else if (/sek|kr|kronor/i.test(currency)) currency = 'SEK';
  else if (/eur|€/i.test(currency)) currency = 'EUR';
  else if (/usd|\$/i.test(currency)) currency = 'USD';

  const rawItems = Array.isArray(raw.items) ? raw.items : [];
  const normalizedItems: InvoiceItem[] = [];

  rawItems.forEach((it: any, idx: number) => {
    if (!it) return;
    const rawName = (it.name || it.title || it.product || it.item || '').trim();
    if (!rawName || !isRealLineItem(rawName)) return;

    const qty = cleanAndParseFloat(it.quantity || it.qty || it.count) || 1;
    let up = cleanAndParseFloat(it.unitPrice || it.unit_price || it.price_per_unit || it.price);
    let tp = cleanAndParseFloat(it.totalPrice || it.total_price || it.total || (qty * up));

    if (up === 0 && tp > 0 && qty > 0) {
      up = Math.round((tp / qty) * 100) / 100;
    }
    if (tp === 0 && up > 0 && qty > 0) {
      tp = Math.round((qty * up) * 100) / 100;
    }

    normalizedItems.push({
      id: `item_${Date.now()}_${idx}`,
      name: rawName,
      category: it.category || 'سوپرمارکت',
      quantity: qty,
      unitPrice: Math.round(up * 100) / 100,
      totalPrice: Math.round(tp * 100) / 100
    });
  });

  const calculatedItemsSum = normalizedItems.reduce((acc, curr) => acc + curr.totalPrice, 0);
  let totalAmount = cleanAndParseFloat(raw.amount || raw.total || raw.total_amount || raw.summa);
  if (totalAmount === 0 && calculatedItemsSum > 0) {
    totalAmount = Math.round(calculatedItemsSum * 100) / 100;
  }

  let payMethod: FinancePaymentMethod = 'card';
  const rawPm = String(raw.paymentMethod || raw.payment_method || '').toLowerCase();
  if (rawPm.includes('swish')) payMethod = 'swish';
  else if (rawPm.includes('cash') || rawPm.includes('نقدی')) payMethod = 'cash';
  else if (rawPm.includes('transfer') || rawPm.includes('انتقال')) payMethod = 'transfer';

  return {
    title,
    amount: Math.round(totalAmount * 100) / 100,
    currency,
    category: raw.category || 'groceries',
    date: raw.date && /^\d{4}-\d{2}-\d{2}$/.test(raw.date) ? raw.date : new Date().toISOString().split('T')[0],
    paymentMethod: payMethod,
    cardAccount: raw.cardAccount || raw.card_account || raw.card || defaultCard,
    notes: raw.notes || `استخراج هوشمند از فاکتور ${title}`,
    items: normalizedItems
  };
}

// AI JSON Response Parser Helper
function parseAiInvoiceJson(replyText: string, defaultCurrency = 'SEK', defaultCard = 'Handelsbanken Debit'): any {
  if (!replyText || typeof replyText !== 'string') return null;

  // 1. Convert Persian / Arabic numerals to standard digits
  let clean = convertFarsiArabicDigitsToEnglish(replyText.trim());

  // 2. Strip markdown fences like ```json{...}``` or ```{...}```
  clean = clean.replace(/```(?:json)?\s*/gi, '').replace(/```\s*$/gi, '').trim();

  // 3. Locate JSON block enclosed in { ... }
  const firstBrace = clean.indexOf('{');
  const lastBrace = clean.lastIndexOf('}');

  if (firstBrace !== -1 && lastBrace > firstBrace) {
    const jsonCandidate = clean.substring(firstBrace, lastBrace + 1);

    // Try standard JSON.parse first
    try {
      const parsed = JSON.parse(jsonCandidate);
      if (parsed && typeof parsed === 'object') {
        return normalizeInvoicePayload(parsed, defaultCurrency, defaultCard);
      }
    } catch (e) {
      // 4. Try lenient JSON sanitization
      try {
        const sanitized = jsonCandidate
          .replace(/,\s*([}\]])/g, '$1')
          .replace(/([{,]\s*)([a-zA-Z0-9_]+)\s*:/g, '$1"$2":')
          .replace(/:\s*'([^']*)'/g, ':"$1"');

        const parsedSanitized = JSON.parse(sanitized);
        if (parsedSanitized && typeof parsedSanitized === 'object') {
          return normalizeInvoicePayload(parsedSanitized, defaultCurrency, defaultCard);
        }
      } catch (err2) {
        console.warn('Sanitized JSON parse failed, trying regex extractor:', err2);
      }
    }
  }

  // 5. Ultimate Fallback: Regex extraction for critical fields if JSON syntax had broken tags
  try {
    const titleMatch = clean.match(/"(?:title|store|merchant|store_name|shop)"\s*:\s*"([^"]+)"/i);
    const amountMatch = clean.match(/"(?:amount|total|total_amount|summa)"\s*:\s*([0-9]+(?:\.[0-9]+)?)/i);
    const currencyMatch = clean.match(/"(?:currency|unit)"\s*:\s*"([^"]+)"/i);
    const dateMatch = clean.match(/"(?:date|invoice_date)"\s*:\s*"([^"]+)"/i);
    const payMethodMatch = clean.match(/"(?:paymentMethod|payment_method)"\s*:\s*"([^"]+)"/i);
    const cardMatch = clean.match(/"(?:cardAccount|card_account|card)"\s*:\s*"([^"]+)"/i);

    if (titleMatch || amountMatch) {
      return normalizeInvoicePayload({
        title: titleMatch ? titleMatch[1] : 'فاکتور خرید',
        amount: amountMatch ? parseFloat(amountMatch[1]) : 0,
        currency: currencyMatch ? currencyMatch[1] : defaultCurrency,
        date: dateMatch ? dateMatch[1] : new Date().toISOString().split('T')[0],
        paymentMethod: payMethodMatch ? payMethodMatch[1] : 'card',
        cardAccount: cardMatch ? cardMatch[1] : defaultCard,
        items: []
      }, defaultCurrency, defaultCard);
    }
  } catch (err3) {
    console.warn('Regex fallback extraction error:', err3);
  }

  return null;
}

// Deterministic Local Receipt Parser Fallback
function parseReceiptTextLocally(text: string, defaultCurrency = 'SEK', defaultCard = 'Handelsbanken Debit') {
  if (!text || !text.trim()) return null;
  const lines = text.split('\n').map(l => l.trim()).filter(Boolean);
  const lower = text.toLowerCase();

  let store = 'فاکتور خرید';
  if (lower.includes('elgiganten')) store = 'Elgiganten';
  else if (lower.includes('willys')) store = 'Willys';
  else if (lower.includes('ica')) store = 'ICA';
  else if (lower.includes('hemköp') || lower.includes('hemkop')) store = 'Hemköp';
  else if (lower.includes('coop')) store = 'Coop';
  else if (lower.includes('lidl')) store = 'Lidl';
  else if (lower.includes('city gross')) store = 'City Gross';
  else if (lower.includes('systembolaget')) store = 'Systembolaget';
  else if (lower.includes('rusta')) store = 'Rusta';
  else if (lower.includes('ikea')) store = 'IKEA';

  const totalMatch = text.match(/(?:Total ink\. moms|Paid amount|Totalt|TOTAL|KÖP|مبلغ کل|جمع کل|Summa|SUMMA|Att betala|Betalt belopp)\s*(?:SEK|kr)?\s*:?\s*([\d\s]+(?:[\.,]\d+)?)/i);
  let totalAmount = totalMatch ? cleanAndParseFloat(totalMatch[1]) : 0;

  const dateMatch = text.match(/\b(20\d{2}[-/.]\d{2}[-/.]\d{2}|\d{2}\.\d{2}\.20\d{2})\b/);
  let invoiceDate = new Date().toISOString().split('T')[0];
  if (dateMatch) {
    const rawDate = dateMatch[1];
    if (/\d{2}\.\d{2}\.20\d{2}/.test(rawDate)) {
      const parts = rawDate.split('.');
      invoiceDate = `${parts[2]}-${parts[1]}-${parts[0]}`;
    } else {
      invoiceDate = rawDate.replace(/\./g, '-').replace(/\//g, '-');
    }
  }

  let payMethod: FinancePaymentMethod = 'card';
  let cardAcc = defaultCard;
  if (lower.includes('swish')) { payMethod = 'swish'; cardAcc = 'Swish'; }
  else if (lower.includes('kontant') || lower.includes('cash') || lower.includes('نقدی')) { payMethod = 'cash'; cardAcc = 'Cash'; }

  let currency = defaultCurrency;
  if (text.includes('ریال')) currency = 'ریال';
  else if (text.includes('تومان')) currency = 'تومان';
  else if (text.includes('EUR') || text.includes('€')) currency = 'EUR';
  else if (text.includes('USD') || text.includes('$')) currency = 'USD';
  else if (text.includes('SEK') || text.includes('kr')) currency = 'SEK';

  const extractedItems: any[] = [];

  // Footer / Total boundary detection pattern: Once we reach totals or payment details, stop parsing items!
  const FOOTER_BOUNDARY_REGEX = /^(totalt|total\s+ex|total\s+ink|moms|paid\s+amount|remaining\s+amount|betalningsinformation|betalningsstatus|betalt\s+belopp|auktoriseringskod|summa|att\s+betala|subtotal|grand\s+total|payment\s+status|fully\s+paid|sum\s+total)/i;

  let stopItemParsing = false;

  for (let i = 0; i < lines.length; i++) {
    const rawLine = lines[i].trim();
    if (!rawLine) continue;

    const lowerLine = rawLine.toLowerCase();

    // 1. Check if we hit the invoice summary/footer section
    if (FOOTER_BOUNDARY_REGEX.test(lowerLine)) {
      stopItemParsing = true;
      continue;
    }
    if (stopItemParsing) continue;

    // 2. Reject metadata lines (addresses, order numbers, phone, vat number, headers, card info)
    if (!isRealLineItem(rawLine)) continue;
    if (/^(artikel|beskrivning|pris|rabatt|antal|total|moms|fakturanummer|datum|butik|valuta|vemdalsgatan|vällingby|kvitto|org\s*nr)/i.test(lowerLine)) continue;

    // 3. Strategy A: Swedish B2C Invoice Table Row with Article Number (e.g. "1079412 Roborock Saros 20 Sonic 8 990,00 99,00 1 8 891,00 25,00%")
    const articleRowMatch = rawLine.match(/^(\d{5,10})\s+(.+?)\s+([\d\s]+[\.,]\d{2})\s+([\d\s]+[\.,]\d{2})\s+(\d+)\s+([\d\s]+[\.,]\d{2})/);
    if (articleRowMatch) {
      const artNo = articleRowMatch[1];
      let pName = articleRowMatch[2].trim();
      const unitP = cleanAndParseFloat(articleRowMatch[3]);
      const qty = parseInt(articleRowMatch[5], 10) || 1;
      const totalP = cleanAndParseFloat(articleRowMatch[6]);

      // Check if next line is a sub-description line without price (e.g., "S20S02-00 (vit)")
      if (i + 1 < lines.length) {
        const nextLine = lines[i + 1].trim();
        if (nextLine && !/\d+[\.,]\d+/.test(nextLine) && !FOOTER_BOUNDARY_REGEX.test(nextLine.toLowerCase()) && isRealLineItem(nextLine)) {
          pName += ` ${nextLine}`;
          i++; // Consume sub-description line
        }
      }

      extractedItems.push({
        name: pName,
        quantity: qty,
        unitPrice: unitP,
        totalPrice: totalP
      });
      continue;
    }

    // 4. Strategy B: Fee or Service line without Article No (e.g. "Parcel on door 99,00" or "Fraktkostnad 99,00")
    const feeMatch = rawLine.match(/^([A-Za-zÅÄÖåäö\s]{3,40})\s+([\d\s]+[\.,]\d{2})$/);
    if (feeMatch) {
      const pName = feeMatch[1].trim();
      const pPrice = cleanAndParseFloat(feeMatch[2]);
      if (isRealLineItem(pName) && pPrice > 0) {
        extractedItems.push({
          name: pName,
          quantity: 1,
          unitPrice: pPrice,
          totalPrice: pPrice
        });
        continue;
      }
    }

    // 5. Strategy C: Standard Store Receipt Line (e.g. "MJÖLK 1.5% 18,90")
    const receiptLineMatch = rawLine.match(/^([A-Za-zÅÄÖåäö0-9\s\-\.\%\/]{3,50})\s+([\d\s]+[\.,]\d{2})$/);
    if (receiptLineMatch) {
      const pName = receiptLineMatch[1].trim();
      const pPrice = cleanAndParseFloat(receiptLineMatch[2]);

      const qtySubMatch = pName.match(/(.+?)\s+(\d+)\s*st\s*\*\s*([\d\s]+[\.,]\d{2})$/i);
      if (qtySubMatch) {
        const cleanName = qtySubMatch[1].trim();
        const qty = parseInt(qtySubMatch[2], 10) || 1;
        const unitP = cleanAndParseFloat(qtySubMatch[3]);
        if (isRealLineItem(cleanName)) {
          extractedItems.push({
            name: cleanName,
            quantity: qty,
            unitPrice: unitP,
            totalPrice: pPrice
          });
        }
      } else if (isRealLineItem(pName) && pPrice > 0) {
        extractedItems.push({
          name: pName,
          quantity: 1,
          unitPrice: pPrice,
          totalPrice: pPrice
        });
      }
      continue;
    }
  }

  // Pass output through normalizeInvoicePayload for ultimate normalization & filtering!
  return normalizeInvoicePayload({
    title: store,
    amount: totalAmount,
    currency,
    category: 'groceries',
    date: invoiceDate,
    paymentMethod: payMethod,
    cardAccount: cardAcc,
    notes: `استخراج هوشمند از فاکتور ${store}`,
    items: extractedItems
  }, defaultCurrency, defaultCard);
}

const CURRENCY_OPTIONS: { code: string; nameKey: string; defaultLabel: string }[] = [
  { code: 'ریال', nameKey: 'financeCurrencyIRR', defaultLabel: 'ریال (IRR)' },
  { code: 'تومان', nameKey: 'financeCurrencyToman', defaultLabel: 'تومان (Toman)' },
  { code: 'SEK', nameKey: 'financeCurrencySEK', defaultLabel: 'کرون سوئد (SEK)' },
  { code: 'EUR', nameKey: 'financeCurrencyEUR', defaultLabel: 'یورو (EUR)' },
  { code: 'USD', nameKey: 'financeCurrencyUSD', defaultLabel: 'دلار آمریکا (USD)' }
];

const SPENDABLE_CATEGORIES: { id: FinanceCategory; label: string; icon: any; defaultPercent: number }[] = [
  { id: 'housing', label: 'financeCategoryHousing', icon: Home, defaultPercent: 30 },
  { id: 'groceries', label: 'financeCategoryGroceries', icon: ShoppingBag, defaultPercent: 20 },
  { id: 'utilities', label: 'financeCategoryUtilities', icon: Zap, defaultPercent: 7 },
  { id: 'transport', label: 'financeCategoryTransport', icon: Car, defaultPercent: 8 },
  { id: 'restaurants', label: 'financeCategoryRestaurants', icon: Utensils, defaultPercent: 6 },
  { id: 'health', label: 'financeCategoryHealth', icon: HeartPulse, defaultPercent: 5 },
  { id: 'clothing', label: 'financeCategoryClothing', icon: Shirt, defaultPercent: 3 },
  { id: 'electronics', label: 'financeCategoryElectronics', icon: Laptop, defaultPercent: 3 },
  { id: 'home_decor', label: 'financeCategoryHomeDecor', icon: Armchair, defaultPercent: 3 },
  { id: 'car_maintenance', label: 'financeCategoryCarMaintenance', icon: Wrench, defaultPercent: 3 },
  { id: 'subscriptions', label: 'financeCategorySubscriptions', icon: Globe, defaultPercent: 2 },
  { id: 'pets', label: 'financeCategoryPets', icon: Dog, defaultPercent: 2 },
  { id: 'charity_gifts', label: 'financeCategoryCharityGifts', icon: Gift, defaultPercent: 2 },
  { id: 'education', label: 'financeCategoryEducation', icon: GraduationCap, defaultPercent: 2 },
  { id: 'entertainment', label: 'financeCategoryEntertainment', icon: Plane, defaultPercent: 2 },
  { id: 'family', label: 'financeCategoryFamily', icon: Users, defaultPercent: 1 },
  { id: 'other', label: 'financeCategoryOther', icon: Tag, defaultPercent: 1 },
];

export const CARD_PRESETS = [
  { id: 'Handelsbanken Debit', name: 'financeCardHandelsbankenDebit' },
  { id: 'Handelsbanken Credit', name: 'financeCardHandelsbankenCredit' },
  { id: 'Nordea Gold', name: 'financeCardNordeaGold' },
  { id: 'SEB Card', name: 'financeCardSEB' },
  { id: 'Swedbank Card', name: 'financeCardSwedbank' },
  { id: 'Swish', name: 'financeCardSwish' },
  { id: 'Hemköp Card', name: 'financeCardHemkop' },
  { id: 'ICA Banken', name: 'financeCardICA' },
  { id: 'Willys Card', name: 'financeCardWillys' },
  { id: 'Revolut', name: 'financeCardRevolut' },
  { id: 'Klarna', name: 'financeCardKlarna' },
  { id: 'Bank Transfer', name: 'financeCardBankTransfer' },
  { id: 'Cash', name: 'financeCardCash' },
];

// Custom Markdown Renderer Components for AI Financial Advisor
const advisorMarkdownComponents = {
  h1: ({ children }: any) => (
    <h1 className="text-xs sm:text-sm font-black text-emerald-950 dark:text-emerald-200 mt-3 mb-2 pb-1 border-b border-emerald-500/40 flex items-center gap-1.5">
      <Bot className="w-4 h-4 text-emerald-600 shrink-0" />
      <span>{children}</span>
    </h1>
  ),
  h2: ({ children }: any) => (
    <h2 className="text-xs sm:text-xs font-extrabold text-teal-800 dark:text-teal-300 mt-2.5 mb-1.5 flex items-center gap-1.5">
      <span className="w-2 h-2 rounded-full bg-teal-500 inline-block shrink-0"></span>
      <span>{children}</span>
    </h2>
  ),
  h3: ({ children }: any) => (
    <h3 className="text-xs font-bold text-gray-900 dark:text-gray-100 mt-2 mb-1 flex items-center gap-1">
      <ChevronRight className="w-3.5 h-3.5 text-teal-500 shrink-0" />
      <span>{children}</span>
    </h3>
  ),
  strong: ({ children }: any) => (
    <strong className="font-extrabold text-emerald-950 dark:text-emerald-100 bg-emerald-100/80 dark:bg-emerald-950/70 px-1.5 py-0.5 rounded text-[11px] inline-block my-0.5 border border-emerald-200/60 dark:border-emerald-800/60">
      {children}
    </strong>
  ),
  p: ({ children }: any) => (
    <p className="my-1.5 leading-relaxed text-gray-800 dark:text-gray-200 text-xs">
      {children}
    </p>
  ),
  ul: ({ children }: any) => (
    <ul className="list-disc pr-4 space-y-1 my-2 text-xs text-gray-800 dark:text-gray-200">
      {children}
    </ul>
  ),
  ol: ({ children }: any) => (
    <ol className="list-decimal pr-5 space-y-1 my-2 text-xs text-gray-800 dark:text-gray-200">
      {children}
    </ol>
  ),
  li: ({ children }: any) => (
    <li className="leading-relaxed">
      {children}
    </li>
  ),
  blockquote: ({ children }: any) => (
    <blockquote className="border-r-3 border-teal-500 pr-3 py-1.5 my-2 bg-teal-50/60 dark:bg-teal-950/40 rounded-l-xl italic text-xs text-teal-900 dark:text-teal-200">
      {children}
    </blockquote>
  ),
  table: ({ children }: any) => (
    <div className="overflow-x-auto my-3 rounded-xl border border-gray-200 dark:border-gray-700 shadow-xs">
      <table className="w-full text-right text-[11px] border-collapse bg-white dark:bg-gray-900">
        {children}
      </table>
    </div>
  ),
  th: ({ children }: any) => (
    <th className="bg-teal-50 dark:bg-teal-950/60 p-2 font-bold text-teal-900 dark:text-teal-200 border-b border-teal-200 dark:border-teal-800">
      {children}
    </th>
  ),
  td: ({ children }: any) => (
    <td className="p-2 border-b border-gray-100 dark:border-gray-800 text-gray-800 dark:text-gray-200">
      {children}
    </td>
  )
};

export interface BatchImportRow {
  id: string;
  date: string;
  title: string;
  amount: number;
  type: FinanceTransactionType;
  category: FinanceCategory;
  cardAccount: string;
  paymentMethod: FinancePaymentMethod;
  isSelected: boolean;
  notes?: string;
  matchStatus?: 'new' | 'duplicate_skip' | 'match_merge_store' | 'match_merge_bank';
  matchedTxTitle?: string;
  matchedTxId?: string;
}

const formatYearMonth = (ym: string, currentLang: any = 'fa') => {
  if (!ym || !ym.includes('-')) return ym;
  try {
    const [y, m] = ym.split('-').map(Number);
    const date = new Date(y, m - 1, 1);
    const miladiStr = formatGregorianDate(date, currentLang, 'medium');
    return `${miladiStr} (${formatNumberByLang(ym, currentLang)})`;
  } catch {
    return formatNumberByLang(ym, currentLang);
  }
};

export default function FinanceModule() {
  const { lang, t, dir } = useLanguage();
  const [activeTab, setActiveTab] = useState<'overview' | 'budget' | 'batch' | 'scanner' | 'products' | 'goals' | 'advisor'>('overview');
  const [transactions, setTransactions] = useState<FinanceTransaction[]>([]);
  const [goals, setGoals] = useState<FinancialGoal[]>([]);
  const [budgetConfig, setBudgetConfig] = useState<BudgetConfig>({
    monthlyIncomeAfterTax: 0,
    currency: 'ریال',
    expenseLimitRatio: 60,
    savingLimitRatio: 40
  });

  // AI Advisor Chat States
  const [advisorMessages, setAdvisorMessages] = useState<Array<{ id: string; role: 'user' | 'model'; text: string; time: string }>>([
    {
      id: 'welcome_1',
      role: 'model',
      text: t('financeAdvisorWelcome', `سلام و درود! من **مشاور ارشد هوشمند مالی و استراتژیست ثروت** شما هستم. 💼🌟

من آگاهی کامل و لحظه‌ای به **درآمد خالص، هزینه‌ها، سقف بودجه، رادار مصرف اقلام و {t('goalsTab', 'اهداف مالی')} فعال شما** دارم. می‌توانید سوالات تخصصی خود را بپرسید:

- 📊 **تحلیل دقیق رفتار مصرفی و خرج‌کرد این ماه**
- 💡 **راهکارهای علمی افزایش نسبت {t('financeTypeSavings', 'پس‌انداز')} ماهانه به بالای ۳۰٪+**
- 📈 **استراتژی‌های چیدمان سبد سرمایه‌گذاری متناسب با مانده بودجه**
- 🛡️ **برنامه‌ریزی جامع ساخت صندوق ذخیره اضطراری (Emergency Fund)**
- 🎯 **شتاب‌دهی به زمان تحقق {t('goalsTab', 'اهداف مالی')} ثبت‌شده**

سوال خود را تایپ کنید یا یکی از پیشنهادات هوشمند سریع زیر را انتخاب کنید!`),
      time: new Date().toLocaleTimeString('fa-IR', { hour: '2-digit', minute: '2-digit' })
    }
  ]);
  const [advisorInput, setAdvisorInput] = useState('');
  const [advisorLoading, setAdvisorLoading] = useState(false);
  const [copiedMsgId, setCopiedMsgId] = useState<string | null>(null);

  // Selected Budget Month State
  const [selectedBudgetMonth, setSelectedBudgetMonth] = useState<string>(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });

  // Custom User Payment Cards State (Pure Cloudflare D1 persistence)
  const DEFAULT_USER_CARDS = [
    'Swish',
    'Debit Card',
    'Credit Card',
    'Cash'
  ];

  const [userCards, setUserCards] = useState<string[]>(DEFAULT_USER_CARDS);

  const [showCardManagerModal, setShowCardManagerModal] = useState(false);
  const [newCardNameInput, setNewCardNameInput] = useState('');
  const [editingCardIndex, setEditingCardIndex] = useState<number | null>(null);
  const [editingCardText, setEditingCardText] = useState('');

  const saveUserCards = (newCards: string[]) => {
    setUserCards(newCards);
    updateUserCards(newCards).catch(err => console.warn('Error saving cards to D1:', err));
  };

  const handleAddCustomCard = (name?: string) => {
    const textToAdd = typeof name === 'string' ? name : newCardNameInput;
    const trimmed = textToAdd.trim();
    if (!trimmed) return;
    if (userCards.includes(trimmed)) return;
    const updated = [...userCards, trimmed];
    saveUserCards(updated);
    setNewCardNameInput('');
  };

  const handleRemoveCustomCard = (cardToRemove: string) => {
    const updated = userCards.filter(c => c !== cardToRemove);
    saveUserCards(updated);
  };

  // Budget Form States
  const [bgIncome, setBgIncome] = useState<string>('');
  const [bgCurrency, setBgCurrency] = useState<string>('ریال');
  const [bgExpenseRatio, setBgExpenseRatio] = useState<number>(60);
  const [bgSavingRatio, setBgSavingRatio] = useState<number>(40);
  const [bgCategoryCaps, setBgCategoryCaps] = useState<Record<string, string>>({});
  const [bgSaveSuccess, setBgSaveSuccess] = useState(false);

  // Modals & Form States
  const [showAddModal, setShowAddModal] = useState(false);
  const [showGoalModal, setShowGoalModal] = useState(false);
  const [showDepositModal, setShowDepositModal] = useState<FinancialGoal | null>(null);
  const [depositAmount, setDepositAmount] = useState<string>('');

  // Editing
  const [editingTransaction, setEditingTransaction] = useState<FinanceTransaction | null>(null);

  // Form Fields for Transaction
  const [txType, setTxType] = useState<FinanceTransactionType>('expense');
  const [txTitle, setTxTitle] = useState('');
  const [txAmount, setTxAmount] = useState<string>('');
  const [txCurrency, setTxCurrency] = useState('ریال');
  const [txPaymentMethod, setTxPaymentMethod] = useState<FinancePaymentMethod>('swish');
  const [txCardAccount, setTxCardAccount] = useState<string>('Handelsbanken Debit');
  const [customCardInput, setCustomCardInput] = useState<string>('');
  const [txCategory, setTxCategory] = useState<FinanceCategory>('groceries');
  const [txDate, setTxDate] = useState<string>(() => new Date().toISOString().split('T')[0]);
  const [txNotes, setTxNotes] = useState('');
  const [txItems, setTxItems] = useState<InvoiceItem[]>([]);
  const [txAttachments, setTxAttachments] = useState<SessionAttachment[]>([]);
  const [isUploadingAttachments, setIsUploadingAttachments] = useState(false);
  const [uploadProgressText, setUploadProgressText] = useState('');
  const [previewAttachmentModal, setPreviewAttachmentModal] = useState<SessionAttachment | null>(null);
  const [isScanningInvoiceAi, setIsScanningInvoiceAi] = useState(false);
  const [scanStatusMessage, setScanStatusMessage] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Item field helpers
  const [newItemName, setNewItemName] = useState('');
  const [newItemCategory, setNewItemCategory] = useState('سوپرمارکت');
  const [newItemQty, setNewItemQty] = useState<string>('1');
  const [newItemPrice, setNewItemPrice] = useState<string>('');

  // AI Scanner States
  const [factorText, setFactorText] = useState('');
  const [aiParsing, setAiParsing] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);
  const [parsedPreview, setParsedPreview] = useState<Partial<FinanceTransaction> | null>(null);

  // Batch Import States
  const [batchRawText, setBatchRawText] = useState<string>('');
  const [batchRows, setBatchRows] = useState<BatchImportRow[]>([]);
  const [batchSuccessMsg, setBatchSuccessMsg] = useState<string | null>(null);
  const [batchGlobalCard, setBatchGlobalCard] = useState<string>('Handelsbanken Debit');
  const [batchGlobalCategory, setBatchGlobalCategory] = useState<FinanceCategory>('groceries');

  // Goal Form Fields
  const [editingGoal, setEditingGoal] = useState<FinancialGoal | null>(null);
  const [goalTitle, setGoalTitle] = useState('');
  const [goalTargetAmount, setGoalTargetAmount] = useState('');
  const [goalCurrentSavings, setGoalCurrentSavings] = useState('');
  const [goalCurrency, setGoalCurrency] = useState('SEK');
  const [goalTargetDate, setGoalTargetDate] = useState('');
  const [goalCategory, setGoalCategory] = useState<'housing' | 'car' | 'travel' | 'emergency' | 'investment' | 'other'>('housing');
  const [goalNotes, setGoalNotes] = useState('');

  // Search & Filter
  const [searchTerm, setSearchTerm] = useState('');
  const [filterPayment, setFilterPayment] = useState<string>('all');
  const [filterCard, setFilterCard] = useState<string>('all');
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [filterStartDate, setFilterStartDate] = useState<string>('');
  const [filterEndDate, setFilterEndDate] = useState<string>('');
  const [selectedMonthForDetails, setSelectedMonthForDetails] = useState<string | null>(null);
  const [productSearchTerm, setProductSearchTerm] = useState('');

  // Expanded Invoice Accordion
  const [expandedTxId, setExpandedTxId] = useState<string | null>(null);

  // Load AI Advisor Chat History directly from Cloudflare D1
  useEffect(() => {
    getAdvisorChatsFromCloudD1().then(storedMsgs => {
      if (storedMsgs && Array.isArray(storedMsgs) && storedMsgs.length > 0) {
        setAdvisorMessages(storedMsgs);
      }
    }).catch(err => console.warn('Error loading D1 advisor chats:', err));
  }, []);

  // Auto-sync AI Advisor Chat History to Cloudflare D1 database
  useEffect(() => {
    if (advisorMessages && advisorMessages.length > 0) {
      saveAdvisorChatsToCloudD1(advisorMessages).catch(err => console.warn('Error saving chats to Cloudflare D1:', err));
    }
  }, [advisorMessages]);

  // Subscribe to Storage Engine & Cloudflare D1
  useEffect(() => {
    const unsubscribe = subscribeToFinanceData((data) => {
      setTransactions(data.transactions);
      setGoals(data.goals);
      if (Array.isArray(data.userCards) && data.userCards.length > 0) {
        setUserCards(data.userCards);
      }
      if (data.budgetConfig) {
        setBudgetConfig(data.budgetConfig);
        setBgIncome(data.budgetConfig.monthlyIncomeAfterTax > 0 ? data.budgetConfig.monthlyIncomeAfterTax.toString() : '');
        setBgCurrency(data.budgetConfig.currency || 'ریال');
        setTxCurrency(data.budgetConfig.currency || 'ریال');
        setGoalCurrency(data.budgetConfig.currency || 'ریال');
        setBgExpenseRatio(data.budgetConfig.expenseLimitRatio ?? 60);
        setBgSavingRatio(data.budgetConfig.savingLimitRatio ?? 40);

        const caps = data.budgetConfig.categoryCaps || {};
        const capsMap: Record<string, string> = {};
        SPENDABLE_CATEGORIES.forEach(c => {
          capsMap[c.id] = caps[c.id] !== undefined ? caps[c.id]!.toString() : '';
        });
        setBgCategoryCaps(capsMap);
      }
    });
    return () => unsubscribe();
  }, []);

  // Handle Global Base Currency Change Across All Tabs
  const handleBaseCurrencyChange = async (newCurrency: string) => {
    setBgCurrency(newCurrency);
    setTxCurrency(newCurrency);
    setGoalCurrency(newCurrency);
    
    if (parsedPreview) {
      setParsedPreview(prev => prev ? { ...prev, currency: newCurrency } : null);
    }

    const newConfig: BudgetConfig = {
      ...budgetConfig,
      currency: newCurrency
    };
    setBudgetConfig(newConfig);
    await updateBudgetConfig(newConfig);
  };

  // AI Advisor Send Question Handler
  const handleSendAdvisorQuestion = async (customPrompt?: string) => {
    const questionToSend = customPrompt || advisorInput;
    if (!questionToSend.trim() || advisorLoading) return;

    const userMsg = {
      id: `usr_${Date.now()}`,
      role: 'user' as const,
      text: questionToSend.trim(),
      time: new Date().toLocaleTimeString('fa-IR', { hour: '2-digit', minute: '2-digit' })
    };

    setAdvisorMessages(prev => [...prev, userMsg]);
    if (!customPrompt) setAdvisorInput('');
    setAdvisorLoading(true);

    try {
      // Calculate top expense categories
      const monthTxs = transactions.filter(t => t.date && t.date.startsWith(selectedBudgetMonth));
      const categoryTotals: Record<string, number> = {};
      monthTxs.forEach(tx => {
        if (tx.type === 'expense') {
          categoryTotals[tx.category] = (categoryTotals[tx.category] || 0) + tx.amount;
        }
      });

      const topCatEntries = Object.entries(categoryTotals)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5)
        .map(([catId, amt]) => {
          const matched = SPENDABLE_CATEGORIES.find(c => c.id === catId);
          const label = getCategoryMeta(catId as FinanceCategory).name;
          return { category: catId, label, amount: amt };
        });

      const financialContext = {
        income: budgetConfig.monthlyIncomeAfterTax,
        currency: budgetConfig.currency,
        totalExpense: monthBudgetStats.monthlyExpenses,
        expenseCap: monthBudgetStats.expenseCap,
        savingCap: monthBudgetStats.savingCap,
        expenseRatio: budgetConfig.expenseLimitRatio,
        savingRatio: budgetConfig.savingLimitRatio,
        topExpenseCategories: topCatEntries,
        savingsGoals: goals.map(g => ({ title: g.title, target: g.targetAmount, current: g.currentSavings, currency: g.currency })),
        recentTransactionsCount: transactions.length,
        selectedMonth: selectedBudgetMonth
      };

      const chatHistory = advisorMessages.map(m => ({
        role: m.role,
        text: m.text
      }));

      let answerText = '';

      try {
        const res = await fetch('/api/finance/ai-advisor', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            userQuestion: questionToSend.trim(),
            chatHistory,
            financialContext
          })
        });

        if (res.ok) {
          const data = await res.json();
          if (data.success && data.answer) {
            answerText = data.answer;
          }
        }
      } catch (fetchErr) {
        console.warn('Backend /api/finance/ai-advisor call failed, trying direct Worker AI:', fetchErr);
      }

      if (!answerText) {
        try {
          const promptForWorker = t('financeAdvisorPrompt', `شما یک مشاور ارشد و زبده در امور مالی شخصی، بودجه‌بندی و سرمایه‌گذاری هستید.
پاسخ دقیق، ساختاریافته و با بولت‌پوینت به زبان فارسی روان ارائه دهید.

شناسنامه مالی فعلی کاربر:
- درآمد ماهانه پس از مالیات: {{income}} {{currency}}
- هزینه‌های این ماه: {{totalExpense}} {{currency}}
- سقف مجاز هزینه: {{expenseCap}} {{currency}}
- هدف پس‌اند{t('filterFrom', 'از:')} {{savingCap}} {{currency}}
- بیشترین هزینه‌ها: {{topExpenseCategories}}
- اهداف پس‌اند{t('filterFrom', 'از:')} {{savingsGoals}}

سوال کاربر: {{question}}`, {
            income: financialContext.income,
            currency: financialContext.currency,
            totalExpense: financialContext.totalExpense,
            expenseCap: financialContext.expenseCap,
            savingCap: financialContext.savingCap,
            topExpenseCategories: JSON.stringify(financialContext.topExpenseCategories),
            savingsGoals: JSON.stringify(financialContext.savingsGoals),
            question: questionToSend.trim()
          });

          const workerRes = await sendWorkerAiFullRequest('financial_advisor', promptForWorker, []);
          if (workerRes && workerRes.reply) {
            answerText = workerRes.reply;
          }
        } catch (wErr) {
          console.warn('Direct Worker AI call failed:', wErr);
        }
      }

      if (answerText) {
        setAdvisorMessages(prev => [
          ...prev,
          {
            id: `model_${Date.now()}`,
            role: 'model',
            text: answerText,
            time: new Date().toLocaleTimeString('fa-IR', { hour: '2-digit', minute: '2-digit' })
          }
        ]);
      } else {
        throw new Error('پاسخی از سرویس‌های هوش مصنوعی دریافت نشد');
      }
    } catch (err: any) {
      console.error('Advisor error:', err);
      setAdvisorMessages(prev => [
        ...prev,
        {
          id: `err_${Date.now()}`,
          role: 'model',
          text: 'متأسفانه مشکلی در ارتباط با مشاور مالی ایجاد شد. لطفاً مجدداً سوال خود را مطرح کنید.',
          time: new Date().toLocaleTimeString('fa-IR', { hour: '2-digit', minute: '2-digit' })
        }
      ]);
    } finally {
      setAdvisorLoading(false);
    }
  };

  // Handle Auto Allocating Category Caps based on expense budget ratio
  const handleAutoAllocateCategoryCaps = () => {
    const inc = parseFloat(bgIncome) || 0;
    const totalExpenseCap = inc * (bgExpenseRatio / 100);
    const newCaps: Record<string, string> = {};
    SPENDABLE_CATEGORIES.forEach(c => {
      const allocated = Math.round(totalExpenseCap * (c.defaultPercent / 100));
      newCaps[c.id] = allocated.toString();
    });
    setBgCategoryCaps(newCaps);
  };

  // Handle Saving Budget Config
  const handleSaveBudgetSettings = async (e: React.FormEvent) => {
    e.preventDefault();
    const inc = parseFloat(bgIncome) || 0;

    const numericCategoryCaps: Partial<Record<FinanceCategory, number>> = {};
    Object.entries(bgCategoryCaps).forEach(([catKey, valStr]) => {
      if (valStr && !isNaN(parseFloat(valStr)) && parseFloat(valStr) > 0) {
        numericCategoryCaps[catKey as FinanceCategory] = parseFloat(valStr);
      }
    });

    const newConfig: BudgetConfig = {
      monthlyIncomeAfterTax: inc,
      currency: bgCurrency,
      expenseLimitRatio: bgExpenseRatio,
      savingLimitRatio: bgSavingRatio,
      categoryCaps: numericCategoryCaps
    };
    await updateBudgetConfig(newConfig);
    setBgSaveSuccess(true);
    setTimeout(() => setBgSaveSuccess(false), 3000);
  };

  // Expanded Rich Category Translation Helper
  const getCategoryMeta = (cat: FinanceCategory) => {
    switch (cat) {
      case 'groceries': return { name: t('financeCategoryNameGroceries', 'سوپرمارکت و خواربار'), color: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300 border-emerald-200', icon: ShoppingBag };
      case 'housing': return { name: t('financeCategoryNameHousing', 'مسکن و اجاره/رهن'), color: 'bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300 border-blue-200', icon: Home };
      case 'utilities': return { name: t('financeCategoryNameUtilities', 'قبوض، انرژی و اینترنت'), color: 'bg-cyan-50 text-cyan-700 dark:bg-cyan-950/40 dark:text-cyan-300 border-cyan-200', icon: Zap };
      case 'transport': return { name: t('financeCategoryNameTransport', 'حمل‌و‌نقل و سوخت'), color: 'bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300 border-amber-200', icon: Car };
      case 'restaurants': return { name: t('financeCategoryNameRestaurants', 'رستوران، کافه و غذا'), color: 'bg-orange-50 text-orange-700 dark:bg-orange-950/40 dark:text-orange-300 border-orange-200', icon: Utensils };
      case 'health': return { name: t('financeCategoryNameHealth', 'سلامت، درمان و دارو'), color: 'bg-rose-50 text-rose-700 dark:bg-rose-950/40 dark:text-rose-300 border-rose-200', icon: HeartPulse };
      case 'clothing': return { name: t('financeCategoryNameClothing', 'پوشاک، کفش و زیبایی'), color: 'bg-pink-50 text-pink-700 dark:bg-pink-950/40 dark:text-pink-300 border-pink-200', icon: Shirt };
      case 'electronics': return { name: t('financeCategoryNameElectronics', 'کالای دیجیتال و الکترونیک'), color: 'bg-sky-50 text-sky-700 dark:bg-sky-950/40 dark:text-sky-300 border-sky-200', icon: Laptop };
      case 'home_decor': return { name: t('financeCategoryNameHomeDecor', 'لوازم خانگی و دکوراسیون'), color: 'bg-yellow-50 text-yellow-800 dark:bg-yellow-950/40 dark:text-yellow-300 border-yellow-200', icon: Armchair };
      case 'car_maintenance': return { name: t('financeCategoryNameCarMaintenance', 'تعمیرات خودرو و بیمه'), color: 'bg-stone-100 text-stone-800 dark:bg-stone-900/60 dark:text-stone-300 border-stone-300', icon: Wrench };
      case 'subscriptions': return { name: t('financeCategoryNameSubscriptions', 'اشتراک‌های دیجیتال و AI'), color: 'bg-purple-50 text-purple-700 dark:bg-purple-950/40 dark:text-purple-300 border-purple-200', icon: Globe };
      case 'pets': return { name: t('financeCategoryNamePets', 'حیوانات خانگی و پت‌شاپ'), color: 'bg-lime-50 text-lime-800 dark:bg-lime-950/40 dark:text-lime-300 border-lime-200', icon: Dog };
      case 'charity_gifts': return { name: t('financeCategoryNameCharityGifts', 'هدایا، کادو و خیریه'), color: 'bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300 border-red-200', icon: Gift };
      case 'education': return { name: t('financeCategoryNameEducation', 'آموزش و توسعه فردی'), color: 'bg-indigo-50 text-indigo-700 dark:bg-indigo-950/40 dark:text-indigo-300 border-indigo-200', icon: GraduationCap };
      case 'entertainment': return { name: t('financeCategoryNameEntertainment', 'تفریح، ورزش و سفر'), color: 'bg-fuchsia-50 text-fuchsia-700 dark:bg-fuchsia-950/40 dark:text-fuchsia-300 border-fuchsia-200', icon: Plane };
      case 'family': return { name: t('financeCategoryNameFamily', 'امور فرزندان و خانواده'), color: 'bg-violet-50 text-violet-700 dark:bg-violet-950/40 dark:text-violet-300 border-violet-200', icon: Users };
      case 'salary': return { name: t('financeCategoryNameSalary', 'حقوق و درآمد اصلی'), color: 'bg-teal-50 text-teal-700 dark:bg-teal-950/40 dark:text-teal-300 border-teal-200', icon: Wallet };
      case 'investment': return { name: t('financeCategoryNameInvestment', 'پس‌انداز و سرمایه‌گذاری'), color: 'bg-emerald-50 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300 border-emerald-300', icon: TrendingUp };
      default: return { name: t('financeCategoryNameDefault', 'خدمات و متفرقه'), color: 'bg-gray-50 text-gray-700 dark:bg-gray-800 dark:text-gray-300 border-gray-200', icon: Tag };
    }
  };

  const getGoalCategoryMeta = (cat?: string) => {
    switch (cat) {
      case 'housing': return { name: t('financeGoalCategoryHousing', 'مسکن و ملک'), color: 'bg-blue-50 text-blue-700 dark:bg-blue-950/60 dark:text-blue-300', icon: Home };
      case 'car': return { name: t('financeGoalCategoryCar', 'خودرو و نقلیه'), color: 'bg-amber-50 text-amber-700 dark:bg-amber-950/60 dark:text-amber-300', icon: Car };
      case 'travel': return { name: t('financeGoalCategoryTravel', 'سفر و تفریح'), color: 'bg-sky-50 text-sky-700 dark:bg-sky-950/60 dark:text-sky-300', icon: Plane };
      case 'emergency': return { name: t('financeGoalCategoryEmergency', 'صندوق اضطراری'), color: 'bg-rose-50 text-rose-700 dark:bg-rose-950/60 dark:text-rose-300', icon: ShieldCheck };
      case 'investment': return { name: t('financeGoalCategoryInvestment', 'سرمایه‌گذاری و پس‌انداز'), color: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300', icon: TrendingUp };
      default: return { name: t('financeGoalCategoryOther', 'سایر و متفرقه'), color: 'bg-purple-50 text-purple-700 dark:bg-purple-950/60 dark:text-purple-300', icon: Landmark };
    }
  };

  const getPaymentMeta = (method: FinancePaymentMethod) => {
    switch (method) {
      case 'swish': return { name: t('financePaymentSwish', 'سوئیش (Swish)'), badge: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/60 dark:text-indigo-200' };
      case 'cash': return { name: t('financePaymentCash', 'نقد (Cash)'), badge: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/60 dark:text-emerald-200' };
      case 'card': return { name: t('financePaymentCard', 'کارت بانکی'), badge: 'bg-sky-100 text-sky-800 dark:bg-sky-900/60 dark:text-sky-200' };
      case 'transfer': return { name: t('financePaymentTransfer', 'انتقال حساب'), badge: 'bg-purple-100 text-purple-800 dark:bg-purple-900/60 dark:text-purple-200' };
    }
  };

  // Filtered Transactions - Strictly Sorted Newest to Oldest + Date Range Filter
  const filteredTransactions = useMemo(() => {
    // Auto normalize start and end dates if swapped by user input (e.g. 05/31/2026 vs 05/01/2026)
    let minD = filterStartDate ? filterStartDate.trim() : '';
    let maxD = filterEndDate ? filterEndDate.trim() : '';
    if (minD && maxD && minD > maxD) {
      [minD, maxD] = [maxD, minD];
    }

    return transactions
      .filter(t => {
        const matchSearch = searchTerm === '' || 
          t.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (t.notes && t.notes.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (t.cardAccount && t.cardAccount.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (t.items && t.items.some(i => i.name.toLowerCase().includes(searchTerm.toLowerCase())));
        
        const matchPayment = filterPayment === 'all' || t.paymentMethod === filterPayment;
        const matchCard = filterCard === 'all' || (t.cardAccount && t.cardAccount === filterCard);
        const matchCat = filterCategory === 'all' || t.category === filterCategory;

        const txDate = t.date ? t.date.trim() : '';
        const matchStartDate = !minD || (txDate && txDate >= minD);
        const matchEndDate = !maxD || (txDate && txDate <= maxD);

        return matchSearch && matchPayment && matchCard && matchCat && matchStartDate && matchEndDate;
      })
      .sort((a, b) => {
        // Guaranteed Newest to Oldest order
        const dateA = new Date(a.date || a.createdAt || 0).getTime();
        const dateB = new Date(b.date || b.createdAt || 0).getTime();
        if (dateB !== dateA) return dateB - dateA;
        const createdA = new Date(a.createdAt || 0).getTime();
        const createdB = new Date(b.createdAt || 0).getTime();
        return createdB - createdA;
      });
  }, [transactions, searchTerm, filterPayment, filterCard, filterCategory, filterStartDate, filterEndDate]);

  // Aggregation Calculations (Dynamically reacts to Date Range and Filters)
  const stats = useMemo(() => {
    let totalIncome = 0;
    let totalExpense = 0;
    let totalSavingsContrib = 0;

    let swishTotal = 0;
    let cashTotal = 0;
    let cardTotal = 0;

    filteredTransactions.forEach(t => {
      const amt = Number(t.amount) || 0;
      if (t.type === 'income') {
        totalIncome += amt;
      } else if (t.type === 'expense') {
        totalExpense += amt;
        if (t.paymentMethod === 'swish') swishTotal += amt;
        if (t.paymentMethod === 'cash') cashTotal += amt;
        if (t.paymentMethod === 'card') cardTotal += amt;
      } else if (t.type === 'savings') {
        totalSavingsContrib += amt;
      }
    });

    const netSavings = totalIncome - totalExpense;

    return {
      totalIncome,
      totalExpense,
      totalSavingsContrib,
      netSavings,
      swishTotal,
      cashTotal,
      cardTotal
    };
  }, [filteredTransactions]);

  // Handler to filter transactions by month and smoothly scroll down to the bottom transaction table
  const handleFilterMonthAndScroll = (yearMonth: string) => {
    const [y, m] = yearMonth.split('-').map(Number);
    if (y && m) {
      const lastDay = new Date(y, m, 0).getDate();
      setFilterStartDate(`${y}-${String(m).padStart(2, '0')}-01`);
      setFilterEndDate(`${y}-${String(m).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`);
    } else {
      setFilterStartDate(`${yearMonth}-01`);
      setFilterEndDate('');
    }
    setTimeout(() => {
      const el = document.getElementById('transactions-table-section');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth' });
      }
    }, 50);
  };

  // Detect any existing transactions where title or category indicates Lön/Salary/Income but type is 'expense'
  const misclassifiedSalaryTxs = useMemo(() => {
    return transactions.filter(t => 
      t.type === 'expense' && (
        t.category === 'salary' ||
        t.title.toLowerCase().includes('lön') ||
        t.title.toLowerCase().includes('lon ') ||
        t.title.toLowerCase().includes('salary') ||
        t.title.toLowerCase().includes('حقوق') ||
        t.title.toLowerCase().includes('درآمد') ||
        t.title.toLowerCase().includes('insättning') ||
        t.title.toLowerCase().includes('inbetalning') ||
        t.title.toLowerCase().includes('a-kassa') ||
        t.title.toLowerCase().includes('försäkringskassan') ||
        t.title.toLowerCase().includes('csn')
      )
    );
  }, [transactions]);

  const handleFixSalaryTransactionTypes = async () => {
    if (misclassifiedSalaryTxs.length === 0) return;
    for (const tx of misclassifiedSalaryTxs) {
      await updateTransaction({
        ...tx,
        type: 'income',
        category: 'salary'
      });
    }
  };

  // Detect credit card payments and internal account transfers that shouldn't double-count as regular living expenses
  const misclassifiedTransferTxs = useMemo(() => {
    return transactions.filter(t => 
      t.type === 'expense' && (
        t.title.toLowerCase().includes('seb kort') ||
        t.title.toLowerCase().includes('svea bank') ||
        t.title.toLowerCase().includes('klarna bank') ||
        t.title.toLowerCase().includes('revolut') ||
        t.title.toLowerCase().includes('salim zarep') ||
        t.title.toLowerCase().includes('överföring') ||
        t.title.toLowerCase().includes('internt') ||
        t.title.toLowerCase().includes('تسویه کارت') ||
        t.title.toLowerCase().includes('انتقال')
      )
    );
  }, [transactions]);

  const handleFixTransferTransactionTypes = async () => {
    if (misclassifiedTransferTxs.length === 0) return;
    for (const tx of misclassifiedTransferTxs) {
      await updateTransaction({
        ...tx,
        type: 'savings',
        category: 'investment',
        notes: tx.notes ? `${tx.notes} (انتقال بین‌حسابی/تسویه کارت)` : 'انتقال بین‌حسابی/تسویه کارت اعتباری'
      });
    }
  };

  // Bulk Delete Modal States & Handlers
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [bulkDeleteTarget, setBulkDeleteTarget] = useState<'month' | 'year' | 'filtered' | 'all'>('month');
  const [bulkDeleteYearMonth, setBulkDeleteYearMonth] = useState<string>(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [bulkDeleteYear, setBulkDeleteYear] = useState<string>(() => new Date().getFullYear().toString());
  const [bulkDeleteConfirmText, setBulkDeleteConfirmText] = useState<string>('');

  const handleExecuteBulkDelete = async () => {
    if (bulkDeleteTarget === 'all') {
      if (bulkDeleteConfirmText.trim().toUpperCase() !== 'DELETE') {
        alert(t('financeBulkDeleteAlert', 'لطفاً کلمه DELETE را جهت تایید حذف کامل تایپ کنید.'));
        return;
      }
      await deleteAllTransactions();
    } else if (bulkDeleteTarget === 'month') {
      await deleteTransactionsByMonth(bulkDeleteYearMonth);
    } else if (bulkDeleteTarget === 'year') {
      await deleteTransactionsByYear(bulkDeleteYear);
    } else if (bulkDeleteTarget === 'filtered') {
      const ids = filteredTransactions.map(t => t.id);
      await deleteTransactionsByIds(ids);
    }
    setShowBulkDeleteModal(false);
    setBulkDeleteConfirmText('');
  };

  // Handle Month Navigation
  const handleNavigateMonth = (direction: 'prev' | 'next') => {
    const [y, m] = selectedBudgetMonth.split('-').map(Number);
    const date = new Date(y, m - 1 + (direction === 'next' ? 1 : -1), 1);
    const newYm = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    setSelectedBudgetMonth(newYm);
  };

  const handleSelectCurrentMonth = () => {
    const now = new Date();
    setSelectedBudgetMonth(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`);
  };

  // Available Months for Budget View
  const availableBudgetMonths = useMemo(() => {
    const monthsSet = new Set<string>();
    
    // Add current month and past 6 months
    const now = new Date();
    for (let i = 0; i < 6; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      monthsSet.add(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
    }

    // Add months from transactions
    transactions.forEach(t => {
      if (t.date && t.date.length >= 7) {
        monthsSet.add(t.date.slice(0, 7));
      }
    });

    return Array.from(monthsSet).sort().reverse();
  }, [transactions]);

  // Selected Month Expenses vs Budget Cap
  const monthBudgetStats = useMemo(() => {
    let monthlyExpenses = 0;
    let registeredIncome = 0;
    const categoryTotals: Record<string, number> = {};

    transactions.forEach(t => {
      if (t.date && t.date.startsWith(selectedBudgetMonth)) {
        const amt = Number(t.amount) || 0;
        if (t.type === 'expense') {
          monthlyExpenses += amt;
          categoryTotals[t.category] = (categoryTotals[t.category] || 0) + amt;
        } else if (t.type === 'income') {
          registeredIncome += amt;
        }
      }
    });

    const income = registeredIncome > 0 ? registeredIncome : (budgetConfig.monthlyIncomeAfterTax || 0);
    const expenseCap = income * ((budgetConfig.expenseLimitRatio || 60) / 100);
    const savingCap = income * ((budgetConfig.savingLimitRatio || 40) / 100);
    const remainingExpenseBudget = expenseCap - monthlyExpenses;
    const spentPercentage = expenseCap > 0 ? Math.round((monthlyExpenses / expenseCap) * 100) : 0;

    return {
      monthlyExpenses,
      income,
      expenseCap,
      savingCap,
      remainingExpenseBudget,
      spentPercentage,
      categoryTotals,
      registeredIncome,
      selectedMonth: selectedBudgetMonth
    };
  }, [transactions, budgetConfig, selectedBudgetMonth]);

  // Monthly Budget History Summary
  const monthlyBudgetHistory = useMemo(() => {
    return availableBudgetMonths.map(ym => {
      let monthlyExpenses = 0;
      let registeredIncome = 0;

      transactions.forEach(t => {
        if (t.date && t.date.startsWith(ym)) {
          const amt = Number(t.amount) || 0;
          if (t.type === 'expense') {
            monthlyExpenses += amt;
          } else if (t.type === 'income') {
            registeredIncome += amt;
          }
        }
      });

      const income = registeredIncome > 0 ? registeredIncome : (budgetConfig.monthlyIncomeAfterTax || 0);
      const expenseCap = income * ((budgetConfig.expenseLimitRatio || 60) / 100);
      const remaining = expenseCap - monthlyExpenses;
      const spentPct = expenseCap > 0 ? Math.round((monthlyExpenses / expenseCap) * 100) : 0;

      return {
        yearMonth: ym,
        monthlyExpenses,
        income,
        expenseCap,
        remaining,
        spentPct,
        isOverBudget: remaining < 0,
        hasTransactions: monthlyExpenses > 0 || registeredIncome > 0
      };
    });
  }, [availableBudgetMonths, transactions, budgetConfig]);

  // Financial Goals AI Forecast
  const goalsForecast = useMemo(() => {
    const totalRemainingTarget = goals.reduce((sum, g) => sum + Math.max(0, g.targetAmount - g.currentSavings), 0);
    const monthlySavingAllocated = monthBudgetStats.savingCap;
    
    let estimatedMonthsNeeded = 0;
    if (monthlySavingAllocated > 0 && totalRemainingTarget > 0) {
      estimatedMonthsNeeded = Math.ceil(totalRemainingTarget / monthlySavingAllocated);
    }

    return {
      totalRemainingTarget,
      monthlySavingAllocated,
      estimatedMonthsNeeded
    };
  }, [goals, monthBudgetStats.savingCap]);

  // Dynamic Available Cards List & Spending Breakdown
  const availableCardsList = useMemo(() => {
    const cardsSet = new Set<string>();
    userCards.forEach(c => {
      if (c && c.trim()) cardsSet.add(c.trim());
    });
    transactions.forEach(t => {
      if (t.cardAccount && t.cardAccount.trim()) {
        cardsSet.add(t.cardAccount.trim());
      }
    });
    return Array.from(cardsSet);
  }, [userCards, transactions]);

  const cardSpendingBreakdown = useMemo(() => {
    const map: Record<string, { total: number; expenseTotal: number; incomeTotal: number }> = {};
    transactions.forEach(t => {
      const key = t.cardAccount && t.cardAccount.trim() ? t.cardAccount.trim() : (t.paymentMethod === 'swish' ? 'Swish' : 'کارت بانکی');
      if (!map[key]) {
        map[key] = { total: 0, expenseTotal: 0, incomeTotal: 0 };
      }
      const amt = Number(t.amount) || 0;
      if (t.type === 'expense') {
        map[key].expenseTotal += amt;
      } else if (t.type === 'income') {
        map[key].incomeTotal += amt;
      }
    });

    return Object.entries(map).map(([card, data]) => {
      const netTotal = data.expenseTotal - data.incomeTotal;
      return {
        card,
        total: netTotal,
        expenseTotal: data.expenseTotal,
        incomeTotal: data.incomeTotal
      };
    }).sort((a, b) => Math.abs(b.total) - Math.abs(a.total));
  }, [transactions]);

  // Product Analytics
  const productAnalytics = useMemo(() => {
    const map: Record<string, { name: string; category: string; totalQty: number; totalSpent: number; purchaseCount: number }> = {};

    transactions.forEach(t => {
      if (t.items && t.items.length > 0) {
        t.items.forEach(item => {
          const key = item.name.trim().toLowerCase();
          if (!map[key]) {
            map[key] = {
              name: item.name,
              category: item.category || 'عام',
              totalQty: 0,
              totalSpent: 0,
              purchaseCount: 0
            };
          }
          map[key].totalQty += item.quantity || 1;
          map[key].totalSpent += item.totalPrice || 0;
          map[key].purchaseCount += 1;
        });
      }
    });

    const allProducts = Object.values(map);
    const filtered = allProducts.filter(p => 
      productSearchTerm === '' || p.name.toLowerCase().includes(productSearchTerm.toLowerCase())
    ).sort((a, b) => b.totalSpent - a.totalSpent);

    return {
      allProducts,
      filtered
    };
  }, [transactions, productSearchTerm]);

  // Visual Graphs & Reports Analytics Data ("in which cat, when, and which product most or less")
  const visualReportsData = useMemo(() => {
    // 1. Category Expense Distribution
    const catMap: Record<string, number> = {};
    let totalExpenseSum = 0;

    filteredTransactions.forEach(t => {
      if (t.type === 'expense') {
        const amt = Number(t.amount) || 0;
        catMap[t.category] = (catMap[t.category] || 0) + amt;
        totalExpenseSum += amt;
      }
    });

    const categoryBreakdown = Object.entries(catMap)
      .map(([catId, amount]) => {
        const catMeta = SPENDABLE_CATEGORIES.find(c => c.id === catId);
        const label = getCategoryMeta(catId as FinanceCategory).name;
        const IconComp = catMeta ? catMeta.icon : Tag;
        const percentage = totalExpenseSum > 0 ? Math.round((amount / totalExpenseSum) * 100) : 0;
        return { catId, label, IconComp, amount, percentage };
      })
      .sort((a, b) => b.amount - a.amount);

    // 2. Monthly Spending & Savings Comparison ("Which month spent more/less & saved more/less")
    const monthMap: Record<string, { income: number; expense: number; count: number }> = {};

    transactions.forEach(t => {
      if (t.date && t.date.length >= 7) {
        const ym = t.date.slice(0, 7); // YYYY-MM
        if (!monthMap[ym]) {
          monthMap[ym] = { income: 0, expense: 0, count: 0 };
        }
        const amt = Number(t.amount) || 0;
        if (t.type === 'expense') {
          monthMap[ym].expense += amt;
          monthMap[ym].count += 1;
        } else if (t.type === 'income') {
          monthMap[ym].income += amt;
        }
      }
    });

    const monthlyEntries = Object.entries(monthMap)
      .map(([ym, data]) => {
        const income = data.income > 0 ? data.income : (budgetConfig.monthlyIncomeAfterTax || 0);
        const savings = income - data.expense;
        return {
          yearMonth: ym,
          income,
          expense: data.expense,
          savings,
          count: data.count
        };
      })
      .sort((a, b) => b.yearMonth.localeCompare(a.yearMonth));

    let maxExpenseMonth = monthlyEntries.length > 0 ? monthlyEntries[0] : null;
    let minExpenseMonth = monthlyEntries.length > 0 ? monthlyEntries[0] : null;
    let maxSavingsMonth = monthlyEntries.length > 0 ? monthlyEntries[0] : null;
    let minSavingsMonth = monthlyEntries.length > 0 ? monthlyEntries[0] : null;

    monthlyEntries.forEach(m => {
      if (maxExpenseMonth && m.expense > maxExpenseMonth.expense) maxExpenseMonth = m;
      if (minExpenseMonth && m.expense < minExpenseMonth.expense) minExpenseMonth = m;
      if (maxSavingsMonth && m.savings > maxSavingsMonth.savings) maxSavingsMonth = m;
      if (minSavingsMonth && m.savings < minSavingsMonth.savings) minSavingsMonth = m;
    });

    const maxMonthValue = Math.max(...monthlyEntries.map(e => Math.max(e.expense, e.income)), 1);

    // 3. Product Ranking ("Which product most or less")
    const sortedProductsBySpend = [...productAnalytics.allProducts].sort((a, b) => b.totalSpent - a.totalSpent);
    const top5SpentProducts = sortedProductsBySpend.slice(0, 5);
    const least5SpentProducts = [...sortedProductsBySpend].reverse().slice(0, 5);
    const top5QtyProducts = [...productAnalytics.allProducts].sort((a, b) => b.totalQty - a.totalQty).slice(0, 5);

    return {
      totalExpenseSum,
      categoryBreakdown,
      monthlyEntries,
      maxExpenseMonth,
      minExpenseMonth,
      maxSavingsMonth,
      minSavingsMonth,
      maxMonthValue,
      top5SpentProducts,
      least5SpentProducts,
      top5QtyProducts
    };
  }, [filteredTransactions, transactions, productAnalytics, budgetConfig]);

  // Add Item to Invoice Form
  const handleAddItemToInvoice = () => {
    if (!newItemName.trim() || !newItemPrice) return;
    const qty = parseFloat(newItemQty) || 1;
    const price = parseFloat(newItemPrice) || 0;
    
    const newItem: InvoiceItem = {
      id: `item_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`,
      name: newItemName.trim(),
      category: newItemCategory || 'سوپرمارکت',
      quantity: qty,
      unitPrice: price,
      totalPrice: qty * price
    };

    const newTxItems = [...txItems, newItem];
    setTxItems(newTxItems);
    
    // Auto adjust total invoice amount as the sum of all items only
    const itemsSum = newTxItems.reduce((acc, curr) => acc + curr.totalPrice, 0);
    setTxAmount(itemsSum.toString());

    setNewItemName('');
    setNewItemPrice('');
  };

  const handleRemoveItemFromInvoice = (itemId: string) => {
    const updated = txItems.filter(i => i.id !== itemId);
    setTxItems(updated);
    if (updated.length > 0) {
      const itemsSum = updated.reduce((acc, curr) => acc + curr.totalPrice, 0);
      setTxAmount(itemsSum.toString());
    } else {
      setTxAmount('0');
    }
  };

  // Submit Add or Edit Transaction
  const handleSaveTransaction = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!txTitle.trim() || !txAmount) return;

    const numericAmount = parseFloat(txAmount) || 0;
    const finalCardAccount = txCardAccount === 'CUSTOM' ? (customCardInput.trim() || 'کارت بانکی') : txCardAccount;

    if (editingTransaction) {
      await updateTransaction({
        ...editingTransaction,
        type: txType,
        title: txTitle.trim(),
        amount: numericAmount,
        currency: txCurrency,
        paymentMethod: txPaymentMethod,
        cardAccount: finalCardAccount,
        category: txCategory,
        date: txDate,
        notes: txNotes,
        items: txItems,
        attachments: txAttachments
      });
    } else {
      await addTransaction({
        type: txType,
        title: txTitle.trim(),
        amount: numericAmount,
        currency: txCurrency,
        paymentMethod: txPaymentMethod,
        cardAccount: finalCardAccount,
        category: txCategory,
        date: txDate,
        notes: txNotes,
        items: txItems,
        attachments: txAttachments
      });
    }

    setShowAddModal(false);
    resetTxForm();
  };

  const handleOpenEditTransaction = (tx: FinanceTransaction) => {
    setEditingTransaction(tx);
    setTxType(tx.type || 'expense');
    setTxTitle(tx.title || '');
    setTxAmount(tx.amount?.toString() || '');
    setTxCurrency(tx.currency || budgetConfig.currency || 'SEK');
    setTxPaymentMethod(tx.paymentMethod || 'card');
    setTxCardAccount(tx.cardAccount || userCards[0] || 'Handelsbanken Debit');
    setCustomCardInput('');
    setTxCategory(tx.category || 'groceries');
    setTxDate(tx.date || new Date().toISOString().split('T')[0]);
    setTxNotes(tx.notes || '');
    setTxItems(tx.items ? [...tx.items] : []);
    setTxAttachments(tx.attachments ? [...tx.attachments] : []);
    setShowAddModal(true);
  };

  const handleOpenAddTransaction = () => {
    resetTxForm();
    setShowAddModal(true);
  };

  const resetTxForm = () => {
    setEditingTransaction(null);
    setTxType('expense');
    setTxTitle('');
    setTxAmount('');
    setTxCurrency(budgetConfig.currency || 'SEK');
    setTxPaymentMethod('card');
    setTxCardAccount(userCards[0] || 'Handelsbanken Debit');
    setCustomCardInput('');
    setTxCategory('groceries');
    setTxDate(new Date().toISOString().split('T')[0]);
    setTxNotes('');
    setTxItems([]);
    setTxAttachments([]);
  };

  // Upload & Process Attached Files (Pictures, Invoices, PDFs) with Cloudflare R2 & D1 Persistence
  const handleProcessFinanceFiles = async (files: FileList | File[]) => {
    if (!files || files.length === 0) return;
    setIsUploadingAttachments(true);
    setUploadProgressText(t('uploadingToCloudflare', 'در حال بارگذاری فایل در فضای ابری Cloudflare...'));

    const newAttachments: SessionAttachment[] = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      try {
        setUploadProgressText(t('uploadingFileProgress', 'بارگذاری «{{name}}» ({{current}}/{{total}})...', {
          name: file.name,
          current: i + 1,
          total: files.length
        }));

        // 1. Detect file type
        let detectedType: SessionAttachment['type'] = 'other';
        if (file.type.startsWith('image/')) detectedType = 'image';
        else if (file.type === 'application/pdf' || file.name.endsWith('.pdf')) detectedType = 'pdf';
        else if (file.name.endsWith('.doc') || file.name.endsWith('.docx') || file.type.includes('word')) detectedType = 'word';
        else if (file.name.endsWith('.ppt') || file.name.endsWith('.pptx') || file.type.includes('powerpoint')) detectedType = 'ppt';
        else if (file.type.startsWith('video/')) detectedType = 'video';

        // 2. Extract text if doc/pdf/image for OCR analysis
        let extractedText = '';
        try {
          extractedText = await extractTextFromFile(file);
        } catch (e) {
          console.warn('Text extraction error:', e);
        }

        // 3. Upload to Cloudflare R2 Worker
        const uploadResult = await uploadFileToWorker(file);
        const fileId = uploadResult?.fileId || uploadResult?.key || `fin_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`;
        const workerUrl = getWorkerFileUrl(uploadResult?.key || fileId);

        const formatSize = (bytes: number): string => {
          if (!bytes || bytes === 0) return '0 Bytes';
          const k = 1024;
          const sizes = ['Bytes', 'KB', 'MB', 'GB'];
          const idx = Math.floor(Math.log(bytes) / Math.log(k));
          return parseFloat((bytes / Math.pow(k, idx)).toFixed(1)) + ' ' + sizes[idx];
        };

        const attRecord: SessionAttachment = {
          id: fileId,
          fileId: fileId,
          key: uploadResult?.key || fileId,
          name: file.name,
          type: detectedType,
          fileUrl: workerUrl,
          fileSize: formatSize(file.size),
          size: file.size,
          uploadDate: new Date().toISOString(),
          extractedText: extractedText || undefined,
          file: file
        };

        newAttachments.push(attRecord);

        // 4. Save metadata to Cloudflare D1 shared files catalog
        await saveFileRecordToCloud({
          id: fileId,
          fileId: fileId,
          key: uploadResult?.key || fileId,
          name: file.name,
          type: detectedType,
          section: 'finance',
          moduleTitle: txTitle.trim() ? `فاکتور: ${txTitle.trim()}` : 'فاکتور مالی و رسید خرید',
          fileUrl: workerUrl,
          size: file.size,
          fileSize: formatSize(file.size),
          uploadDate: new Date().toISOString(),
          extractedText: extractedText || undefined
        });

      } catch (uploadErr) {
        console.error('Error uploading file to Cloudflare:', uploadErr);
        alert(t('fileUploadError', 'خطا در بارگذاری فایل «{{name}}». لطفاً مجدداً تلاش کنید.', { name: file.name }));
      }
    }

    if (newAttachments.length > 0) {
      setTxAttachments(prev => [...prev, ...newAttachments]);
    }
    setIsUploadingAttachments(false);
    setUploadProgressText('');
  };

  const handleDeleteAttachment = (attId: string) => {
    setTxAttachments(prev => prev.filter(a => a.id !== attId && a.fileId !== attId));
  };

  const handleScanInvoiceWithAi = async (att?: SessionAttachment) => {
    setScanStatusMessage(null);
    setIsScanningInvoiceAi(true);

    try {
      // 1. Determine target attachment & text source
      const targetAtt = att || txAttachments.find(a => a.type === 'pdf' || a.type === 'image' || a.extractedText);
      let rawText = targetAtt?.extractedText || '';

      // 2. If no extracted text yet, extract directly using PDF.js or Tesseract OCR
      if (!rawText && targetAtt) {
        setScanStatusMessage({ type: 'info', text: t('readingInvoiceFile', 'در حال استخراج متن واقعى از سند فاکتور (PDF / تصویر)...') });
        try {
          if (targetAtt.type === 'pdf') {
            rawText = await extractTextFromPdf(targetAtt.file || targetAtt.fileUrl);
          } else {
            rawText = await extractTextFromImage(targetAtt.fileUrl);
          }
          if (rawText && rawText.trim().length > 5) {
            targetAtt.extractedText = rawText;
          }
        } catch (readErr) {
          console.warn('File text extraction warning:', readErr);
        }
      }

      // If still no text from attachment, check if notes has text
      if (!rawText && txNotes.trim().length > 5) {
        rawText = txNotes.trim();
      }

      // STRICT VALIDATION: If rawText is missing or empty, STOP immediately!
      // DO NOT use file name or fake fallbacks!
      if (!rawText || rawText.trim().length < 5) {
        setScanStatusMessage({
          type: 'error',
          text: t('noInvoiceTextFound', 'متن قابل استخراجی در این فایل (PDF/تصویر) یافت نشد. لطفاً مطمئن شوید فایل حاوی متن یا رسید خواناست.')
        });
        setIsScanningInvoiceAi(false);
        return;
      }

      setScanStatusMessage({ type: 'info', text: t('aiAnalyzingInvoice', 'در حال استخراج هوشمند اقلام و مبالغ واقعی با هوش مصنوعی...') });

      // 3. Send AI Request with strict instructions against hallucinating fake items or including metadata
      const prompt = `شما یک حسابدار و سندخوان فوق‌العاده دقیق در خواندن فاکتورهای سوئدی، اروپایی و ایرانی (مانند Elgiganten, Willys, ICA, Coop, Lidl, IKEA) هستید.
وظیفه شما استخراج دقیق نام فروشگاه، تاریخ، جمع کل نهایی، و اقلام واقعی خرید از متن فاکتور زیر است.

متن واقعی فاکتور/رسید:
"""
${rawText}
"""

دستورالعمل‌های بسیار مهم و حیاتی استخراج:
۱. **نام فروشگاه (title)**: نام اصلی فروشگاه یا برند (مثلاً Elgiganten, Willys, ICA, IKEA, Lidl) را استخراج کنید.
۲. **مبلغ کل پرداختی (amount)**: مبلغ نهایی کل پرداختی (مثلاً Total ink. moms, Paid amount, Totalt, Att betala) را استخراج نمایید.
   - نکته بسیار مهم: در فرمت اعداد سوئدی و اروپایی، فاصله‌های بین ارقام نشان‌دهنده هزارگان است (مثلاً "8 990,00" یعنی 8990.00 و "9 489,00" یعنی 9489.00 و "499,00" یعنی 499.00). ویرگول اعشار است.
۳. **اقلام واقعی خرید (items)**:
   - فقط و فقط کالاهای واقعی خریداری‌شده (مانند Roborock Saros 20 Sonic, Bosch Flexpannset, Parcel on door) را به عنوان item استخراج کنید.
   - به هیچ عنوان موارد متادیتا، جمع کل، مالیات (Moms/VAT)، شماره کارت، کد پیگیری، قوانین بازگشت، شماره فاکتور یا توضیحات پرداخت را به عنوان کالا اضافه نکنید.
   - برای هر کالا:
     - name: نام کامل و مشخصات کالا
     - quantity: تعداد (معمولاً 1 یا عدد ستون Antal)
     - unitPrice: قیمت اصلی کالا قبل از تخفیف (مثلاً 8990.00)
     - totalPrice: قیمت نهایی کالا پس از کسر تخفیف (مثلاً 8891.00)
۴. **تاریخ فاکتور (date)**: تاریخ صادر شدن فاکتور به فرمت YYYY-MM-DD.
۵. **واحد پول (currency)**: SEK, EUR, USD, ریال, تومان.
۶. **روش پرداخت (paymentMethod)**: card, swish, cash, transfer.

پاسخ را دقیقاً و صرفاً به صورت یک شیء JSON با ساختار زیر بازگردانید (بدون هرگونه متن یا توضیحات اضافی):
{
  "title": "Elgiganten",
  "amount": 9489.00,
  "currency": "SEK",
  "category": "electronics",
  "date": "2026-05-29",
  "paymentMethod": "card",
  "cardAccount": "Handelsbanken Debit",
  "notes": "فاکتور خرید Elgiganten",
  "items": [
    {
      "name": "Roborock Saros 20 Sonic robotdammsugare S20S02-00 (vit)",
      "category": "الکترونیک",
      "quantity": 1,
      "unitPrice": 8990.00,
      "totalPrice": 8891.00
    },
    {
      "name": "Bosch Flexpannset HEZ9FF030 (3 delar)",
      "category": "لوازم خانگی",
      "quantity": 1,
      "unitPrice": 499.00,
      "totalPrice": 499.00
    },
    {
      "name": "Parcel on door",
      "category": "حمل و نقل",
      "quantity": 1,
      "unitPrice": 99.00,
      "totalPrice": 99.00
    }
  ]
}`;

      let parsedResult: any = null;

      try {
        const aiRes = await sendWorkerAiFullRequest('general', prompt, []);
        if (aiRes && aiRes.reply) {
          parsedResult = parseAiInvoiceJson(aiRes.reply, txCurrency || budgetConfig.currency || 'SEK', userCards[0] || 'Handelsbanken Debit');
        }
      } catch (workerErr) {
        console.warn('Worker AI call failed, trying local parsing fallback:', workerErr);
      }

      // 4. Fallback to local receipt parser ONLY if AI response was not obtained AND text has numeric prices
      if (!parsedResult || !parsedResult.title) {
        parsedResult = parseReceiptTextLocally(rawText, txCurrency || budgetConfig.currency || 'SEK', userCards[0] || 'Handelsbanken Debit');
      }

      if (parsedResult) {
        if (parsedResult.title) setTxTitle(parsedResult.title);
        if (parsedResult.currency) setTxCurrency(parsedResult.currency);
        if (parsedResult.category) setTxCategory(parsedResult.category);
        if (parsedResult.date) setTxDate(parsedResult.date);
        if (parsedResult.paymentMethod) setTxPaymentMethod(parsedResult.paymentMethod);
        if (parsedResult.cardAccount && userCards.includes(parsedResult.cardAccount)) {
          setTxCardAccount(parsedResult.cardAccount);
        }
        if (parsedResult.notes) setTxNotes(parsedResult.notes);

        if (Array.isArray(parsedResult.items) && parsedResult.items.length > 0) {
          const convertedItems: InvoiceItem[] = parsedResult.items.map((it: any, idx: number) => {
            const q = Number(it.quantity) || 1;
            const up = Number(it.unitPrice) || Number(it.totalPrice) || 0;
            const tp = Number(it.totalPrice) || (q * up);
            return {
              id: `item_${Date.now()}_${idx}`,
              name: it.name || `کالا ${idx + 1}`,
              category: it.category || 'سوپرمارکت',
              quantity: q,
              unitPrice: up,
              totalPrice: tp
            };
          });
          setTxItems(convertedItems);
          const totalSum = convertedItems.reduce((sum, item) => sum + item.totalPrice, 0);
          setTxAmount(totalSum > 0 ? (Math.round(totalSum * 100) / 100).toString() : (parsedResult.amount?.toString() || '0'));
        } else {
          setTxItems([]); // Do NOT create fake items if none existed
          if (parsedResult.amount) {
            setTxAmount(parsedResult.amount.toString());
          }
        }

        setScanStatusMessage({
          type: 'success',
          text: t('invoiceScanSuccess', '✅ استخراج با موفقیت انجام شد: {{title}} به مبلغ {{amount}} {{currency}} (شامل {{count}} قلم کالا)', {
            title: parsedResult.title || 'فاکتور',
            count: formatNumberByLang(parsedResult.items?.length || 0, lang),
            amount: formatNumberByLang(parsedResult.amount || txAmount || '0', lang),
            currency: parsedResult.currency || txCurrency
          })
        });
      } else {
        setScanStatusMessage({
          type: 'error',
          text: t('invoiceScanFailed', 'متأسفانه استخراج خودکار فاکتور ممکن نشد. لطفاً مقادیر را دستی وارد نمایید.')
        });
      }

    } catch (err: any) {
      console.error('Invoice AI Scan Error:', err);
      setScanStatusMessage({
        type: 'error',
        text: err?.message || 'خطا در ارتباط با هوش مصنوعی. لطفاً مجدداً امتحان کنید.'
      });
    } finally {
      setIsScanningInvoiceAi(false);
    }
  };

  // CSV line parser that respects quoted strings containing commas
  const parseCSVLine = (text: string): string[] => {
    const result: string[] = [];
    let cur = '';
    let inQuotes = false;
    for (let i = 0; i < text.length; i++) {
      const c = text[i];
      if (c === '"' || c === "'") {
        inQuotes = !inQuotes;
      } else if ((c === ',' || c === ';' || c === '\t') && !inQuotes) {
        result.push(cur.trim().replace(/^["']|["']$/g, ''));
        cur = '';
      } else {
        cur += c;
      }
    }
    if (cur.length > 0 || text.endsWith(',') || text.endsWith(';')) {
      result.push(cur.trim().replace(/^["']|["']$/g, ''));
    }
    return result;
  };

  // Date parser helper for M/D/YYYY, MM/DD/YYYY, YYYY-MM-DD, DD.MM.YYYY
  const parseFlexDate = (dateStr: string): string => {
    if (!dateStr) return new Date().toISOString().split('T')[0];
    const clean = dateStr.trim();
    const mdyMatch = clean.match(/^(\d{1,2})[\/.-](\d{1,2})[\/.-](\d{4})$/);
    if (mdyMatch) {
      const p1 = parseInt(mdyMatch[1], 10);
      const p2 = parseInt(mdyMatch[2], 10);
      const y = mdyMatch[3];
      if (p1 > 12) {
        return `${y}-${String(p2).padStart(2, '0')}-${String(p1).padStart(2, '0')}`;
      }
      return `${y}-${String(p1).padStart(2, '0')}-${String(p2).padStart(2, '0')}`;
    }
    const ymdMatch = clean.match(/^(\d{4})[\/.-](\d{1,2})[\/.-](\d{1,2})$/);
    if (ymdMatch) {
      return `${ymdMatch[1]}-${String(parseInt(ymdMatch[2], 10)).padStart(2, '0')}-${String(parseInt(ymdMatch[3], 10)).padStart(2, '0')}`;
    }
    return new Date().toISOString().split('T')[0];
  };

  // Category auto-mapper from merchant/category/title text
  const detectCategoryFromText = (text: string): FinanceCategory => {
    const lower = text.toLowerCase();

    // 1. Electronics & Gadgets
    if (
      lower.includes('elgiganten') || lower.includes('mediamarkt') || lower.includes('media markt') ||
      lower.includes('kjell') || lower.includes('webhallen') || lower.includes('inet') ||
      lower.includes('netonnet') || lower.includes('apple') || lower.includes('samsung') ||
      lower.includes('komplett') || lower.includes('power.se') || lower.includes('dustin') ||
      lower.includes('computer') || lower.includes('laptop') || lower.includes('mobile') ||
      lower.includes('phone') || lower.includes('دیجیتال') || lower.includes('الکترونیک') ||
      lower.includes('موبایل') || lower.includes('کامپیوتر') || lower.includes('لپ تاپ')
    ) {
      return 'electronics';
    }

    // 2. Home & Living / Furnishing
    if (
      lower.includes('ikea') || lower.includes('bauhaus') || lower.includes('hornbach') ||
      lower.includes('k-rauta') || lower.includes('jula') || lower.includes('clas ohlson') ||
      lower.includes('mio') || lower.includes('jysk') || lower.includes('rusta') ||
      lower.includes('lagerhaus') || lower.includes('hemtex') || lower.includes('lindex home') ||
      lower.includes('مبلمان') || lower.includes('فرش') || lower.includes('لوازم خانگی') || lower.includes('دکوراسیون')
    ) {
      return 'home_decor';
    }

    // 3. Auto Repair, Maintenance & Insurance
    if (
      lower.includes('mekonomen') || lower.includes('mekster') || lower.includes('autoexperten') ||
      lower.includes('biltema') || lower.includes('folksam') || lower.includes('trygg-hansa') ||
      lower.includes('länsförsäkringar') || lower.includes('if skade') || lower.includes('ica försäkring') ||
      lower.includes('bilverkstad') || lower.includes('biltvätt') || lower.includes('besiktning') ||
      lower.includes('auto repair') || lower.includes('car wash') || lower.includes('بیمه خودرو') ||
      lower.includes('تعمیرگاه') || lower.includes('کارواش')
    ) {
      return 'car_maintenance';
    }

    // 4. Digital Subscriptions & AI SaaS
    if (
      lower.includes('openai') || lower.includes('chatgpt') || lower.includes('midjourney') ||
      lower.includes('anthropic') || lower.includes('google one') || lower.includes('icloud') ||
      lower.includes('github') || lower.includes('adobe') || lower.includes('microsoft') ||
      lower.includes('patreon') || lower.includes('nordvpn') || lower.includes('expressvpn') ||
      lower.includes('اشتراک') || lower.includes('سرویس ابری')
    ) {
      return 'subscriptions';
    }

    // 5. Pets & Animal Care
    if (
      lower.includes('arken zoo') || lower.includes('musti') || lower.includes('djuraffär') ||
      lower.includes('veterinär') || lower.includes('veterinar') || lower.includes('zoologiska') ||
      lower.includes('pet shop') || lower.includes('petstore') || lower.includes('پت شاپ') ||
      lower.includes('دامپزشکی') || lower.includes('حیوانات')
    ) {
      return 'pets';
    }

    // 6. Gifts, Donations & Charity
    if (
      lower.includes('röda korset') || lower.includes('rädda barnen') || lower.includes('unicef') ||
      lower.includes('cancerfonden') || lower.includes('hjärnfonden') || lower.includes('swish gåva') ||
      lower.includes('present') || lower.includes('gåva') || lower.includes('gift') ||
      lower.includes('charity') || lower.includes('donation') || lower.includes('خیریه') ||
      lower.includes('کادو') || lower.includes('هدیه') || lower.includes('نذورات')
    ) {
      return 'charity_gifts';
    }

    // 7. Groceries & Supermarkets
    if (
      lower.includes('grocery') || lower.includes('supermarket') || lower.includes('food store') ||
      lower.includes('willys') || lower.includes('hemköp') || lower.includes('hemkop') ||
      lower.includes('ica ') || lower.includes('ica-') || lower.includes('ica_') || lower.includes('ica kvantum') || lower.includes('ica maxi') || lower.includes('ica nära') || lower.includes('ica super') ||
      lower.includes('lidl') || lower.includes('coop') || lower.includes('city gross') ||
      lower.includes('kista grossen') || lower.includes('shami bazar') || lower.includes('oriental') ||
      lower.includes('oob') || lower.includes('oöb') || lower.includes('öoöb') ||
      lower.includes('mataffär') || lower.includes('livs') || lower.includes('bageri') || lower.includes('chark') ||
      lower.includes('snabbgross') || lower.includes('matöppet') || lower.includes('matrebellen') ||
      lower.includes('convenience store') || lower.includes('7-eleven') || lower.includes('pressbyrån') ||
      lower.includes('systembolaget') || lower.includes('سوپرمارکت') || lower.includes('خواربار') ||
      lower.includes('هایپرمارکت') || lower.includes('میوه') || lower.includes('قصابی') || lower.includes('پروتیین') ||
      lower.includes('نانوا') || lower.includes('بقالی') || lower.includes('لبنیات')
    ) {
      return 'groceries';
    }

    // 8. Restaurants, Cafes & Food Delivery
    if (
      lower.includes('restaurant') || lower.includes('eating place') || lower.includes('kafé') || lower.includes('cafe') ||
      lower.includes('mcdonald') || lower.includes('max ') || lower.includes('max_') || lower.includes('espresso') ||
      lower.includes('burger') || lower.includes('pizza') || lower.includes('sushi') || lower.includes('kebab') ||
      lower.includes('bistro') || lower.includes('bar ') || lower.includes('pub ') || lower.includes('diner') ||
      lower.includes('subway') || lower.includes('starbucks') || lower.includes('pizzeria') || lower.includes('gatukök') ||
      lower.includes('lunch') || lower.includes('foodora') || lower.includes('bolt food') || lower.includes('uber eats') ||
      lower.includes('wayne') || lower.includes('joe & the juice') || lower.includes('holy greens') || lower.includes('bastard burgers') ||
      lower.includes('رستوران') || lower.includes('کافه') || lower.includes('فست فود') || lower.includes('پیتزا') || lower.includes('سفارش غذا')
    ) {
      return 'restaurants';
    }

    // 9. Clothing, Fashion & Beauty
    if (
      lower.includes('clothing') || lower.includes('fashion') || lower.includes('apparel') || lower.includes('shoes') ||
      lower.includes('zara') || lower.includes('h&m') || lower.includes('h & m') || lower.includes('stadium') ||
      lower.includes('lindex') || lower.includes('kappahl') || lower.includes('mq ') || lower.includes('dressmann') ||
      lower.includes('zalando') || lower.includes('boozt') || lower.includes('nk ') || lower.includes('åhlens') ||
      lower.includes('åhléns') || lower.includes('intersport') || lower.includes('jd sports') || lower.includes('foot locker') ||
      lower.includes('frisör') || lower.includes('salong') || lower.includes('beauty') || lower.includes('sephora') ||
      lower.includes('rituals') || lower.includes('kicks') || lower.includes('پوشاک') || lower.includes('لباس') ||
      lower.includes('کفش') || lower.includes('آرایشگاه') || lower.includes('سالن زیبایی')
    ) {
      return 'clothing';
    }

    // 10. Transport, Gas & Vehicles
    if (
      lower.includes('transport') || lower.includes('gas ') || lower.includes('fuel') || lower.includes('sl ') || lower.includes('sl.se') ||
      lower.includes('buss') || lower.includes('bensin') || lower.includes('okq8') || lower.includes('circle k') ||
      lower.includes('shell') || lower.includes('preem') || lower.includes('st1') || lower.includes('ingo') ||
      lower.includes('parkering') || lower.includes('easy-park') || lower.includes('easypark') || lower.includes('aimo') ||
      lower.includes('q-park') || lower.includes('stockholm parkering') || lower.includes('taxi') || lower.includes('uber') ||
      lower.includes('bolt') || lower.includes('västtrafik') || lower.includes('skånetrafiken') || lower.includes('sj ') ||
      lower.includes('train') || lower.includes('flyg') || lower.includes('norwegian') || lower.includes('sas ') ||
      lower.includes('mtr') || lower.includes('حمل و نقل') || lower.includes('بنزین') || lower.includes('مترو') ||
      lower.includes('اتوبوس') || lower.includes('تاکسی') || lower.includes('پارکینگ')
    ) {
      return 'transport';
    }

    // 11. Housing & Rent
    if (
      lower.includes('housing') || lower.includes('rent') || lower.includes('hyra') || lower.includes('hsb') ||
      lower.includes('riksbyggen') || lower.includes('wallenstam') || lower.includes('heimstaden') ||
      lower.includes('bostad') || lower.includes('fastighet') || lower.includes('samfällighet') ||
      lower.includes('اجاره') || lower.includes('مسکن') || lower.includes('شارژ ساختمان')
    ) {
      return 'housing';
    }

    // 12. Utilities & Telecom
    if (
      lower.includes('utility') || lower.includes('utilities') || lower.includes('electricity') || lower.includes('ellevio') ||
      lower.includes('vattenfall') || lower.includes('eon') || lower.includes('fortum') || lower.includes('bredband') ||
      lower.includes('internet') || lower.includes('telia') || lower.includes('telenor') || lower.includes('tele2') ||
      lower.includes('tre ') || lower.includes('3 ') || lower.includes('comviq') || lower.includes('hallon') ||
      lower.includes('ownit') || lower.includes('bahnhof') || lower.includes('vattendrag') || lower.includes('avfall') ||
      lower.includes('قبوض') || lower.includes('برق') || lower.includes('آب') || lower.includes('گاز') || lower.includes('اینترنت')
    ) {
      return 'utilities';
    }

    // 13. Health, Pharmacy & Medical
    if (
      lower.includes('health') || lower.includes('medical') || lower.includes('pharmacy') || lower.includes('apotek') ||
      lower.includes('kronans') || lower.includes('hjärtat') || lower.includes('apoteket') || lower.includes('doctolib') ||
      lower.includes('folktandvården') || lower.includes('tandläkare') || lower.includes('sjukvård') || lower.includes('vårdcentral') ||
      lower.includes('kry ') || lower.includes('doktor.se') || lower.includes('gym') || lower.includes('sats') ||
      lower.includes('fitness') || lower.includes('nordic wellness') || lower.includes('24/7') || lower.includes('friskis') ||
      lower.includes('داروخانه') || lower.includes('پزشک') || lower.includes('دندانپزشکی') || lower.includes('درمانگاه') || lower.includes('باشگاه')
    ) {
      return 'health';
    }

    // 14. Education & Books
    if (
      lower.includes('education') || lower.includes('school') || lower.includes('skola') || lower.includes('universitet') ||
      lower.includes('högskola') || lower.includes('sfi') || lower.includes('utbildning') || lower.includes('kurs') ||
      lower.includes('udemy') || lower.includes('coursera') || lower.includes('duolingo') || lower.includes('akademibokhandeln') ||
      lower.includes('bokus') || lower.includes('adlibris') || lower.includes('آموزش') || lower.includes('کتاب') ||
      lower.includes('دانشگاه') || lower.includes('مدرسه')
    ) {
      return 'education';
    }

    // 15. Entertainment & Travel
    if (
      lower.includes('entertainment') || lower.includes('recreation') || lower.includes('cinema') || lower.includes('bio') ||
      lower.includes('filmstaden') || lower.includes('spotify') || lower.includes('netflix') || lower.includes('hbo') ||
      lower.includes('disney') || lower.includes('youtube') || lower.includes('playstation') || lower.includes('steam') ||
      lower.includes('nintendo') || lower.includes('xbox') || lower.includes('konsert') || lower.includes('teater') ||
      lower.includes('museum') || lower.includes('ticketmaster') || lower.includes('gröna lund') || lower.includes('liseberg') ||
      lower.includes('تفریح') || lower.includes('سینما') || lower.includes('تئاتر') || lower.includes('کنسرت') || lower.includes('سفر')
    ) {
      return 'entertainment';
    }

    // 16. Family & Kids
    if (
      lower.includes('family') || lower.includes('kids') || lower.includes('barn') || lower.includes('leksaker') ||
      lower.includes('toys') || lower.includes('baby') || lower.includes('förskola') || lower.includes('dagis') ||
      lower.includes('jollyroom') || lower.includes('babyland') || lower.includes('فرزند') || lower.includes('کودک') ||
      lower.includes('مهدکودک') || lower.includes('اسباب بازی')
    ) {
      return 'family';
    }

    // 17. Income & Salary
    if (
      lower.includes('salary') || lower.includes('lön') || lower.includes('lon ') || lower.includes('حقوق') || lower.includes('درآمد') ||
      lower.includes('payroll') || lower.includes('försäkringskassan') || lower.includes('csn') || lower.includes('a-kassa') ||
      lower.includes('insättning') || lower.includes('inbetalning')
    ) {
      return 'salary';
    }

    // 18. Investment & Savings
    if (
      lower.includes('investment') || lower.includes('savings') || lower.includes('avanza') || lower.includes('nordnet') ||
      lower.includes('aktier') || lower.includes('fond') || lower.includes('crypto') || lower.includes('binance') ||
      lower.includes('coinbase') || lower.includes('پس‌انداز') || lower.includes('سرمایه‌گذاری') || lower.includes('بورس')
    ) {
      return 'investment';
    }

    return 'other';
  };

  // Batch Import Handlers
  const handleProcessBatchText = () => {
    if (!batchRawText.trim()) return;

    const lines = batchRawText.split('\n').map(l => l.trim()).filter(Boolean);
    if (lines.length === 0) return;

    let dateColIdx = -1;
    let titleColIdx = -1;
    let amountColIdx = -1;
    let creditColIdx = -1;
    let debitColIdx = -1;
    let categoryColIdx = -1;

    const firstLineCells = parseCSVLine(lines[0]);
    const lowerHeaderCells = firstLineCells.map(c => c.toLowerCase());

    const isHeaderLine = lowerHeaderCells.some(c => 
      c.includes('date') || c.includes('datum') || c.includes('text') || c.includes('amount') || c.includes('belopp') || c.includes('kategori') || c.includes('merchant')
    );

    let startLineIdx = 0;
    if (isHeaderLine) {
      startLineIdx = 1;

      // 1. Transaction Date column priority: search for transactiondate / transaktionsdatum / txndate / booking date
      let bestDateIdx = lowerHeaderCells.findIndex(c => 
        c.includes('transactiondate') || c.includes('transaction date') || c.includes('transaktionsdatum') || c.includes('txndate') || c.includes('booking date') || c.includes('bokföringsdatum')
      );
      if (bestDateIdx === -1) {
        // Find column containing date/datum/تاریخ that is NOT valuedate, postdate
        bestDateIdx = lowerHeaderCells.findIndex(c => 
          (c.includes('date') || c.includes('datum') || c.includes('تاریخ')) &&
          !c.includes('value') && !c.includes('post')
        );
      }
      if (bestDateIdx === -1) {
        bestDateIdx = lowerHeaderCells.findIndex(c => c.includes('date') || c.includes('datum') || c.includes('تاریخ'));
      }
      if (bestDateIdx !== -1) {
        dateColIdx = bestDateIdx;
      }

      titleColIdx = lowerHeaderCells.findIndex(c => c.includes('text') || c.includes('beskrivning') || c.includes('rubrik') || c.includes('mottagare') || c.includes('merchant area') || c.includes('title') || c.includes('عنوان'));

      amountColIdx = lowerHeaderCells.findIndex(c => c === 'amount' || c === 'belopp' || c.includes('summa') || c.includes('köp') || c.includes('مبلغ'));
      if (amountColIdx === -1) {
        amountColIdx = lowerHeaderCells.findIndex(c => c.includes('amount') || c.includes('belopp'));
      }

      creditColIdx = lowerHeaderCells.findIndex(c => c.includes('credit') || c.includes('kredit') || c.includes('insättning') || c.includes('inbetalning') || c === 'in');
      debitColIdx = lowerHeaderCells.findIndex(c => c.includes('debit') || c.includes('debet') || c.includes('uttag') || c.includes('utbetalning') || c === 'ut');

      categoryColIdx = lowerHeaderCells.findIndex(c => c.includes('category') || c.includes('kategori'));
    }

    const newRows: BatchImportRow[] = [];
    const chosenCard = batchGlobalCard || userCards[0] || 'Debit Card';

    for (let idx = startLineIdx; idx < lines.length; idx++) {
      const line = lines[idx];
      const cells = parseCSVLine(line);
      if (cells.length === 0) continue;

      let itemDate = new Date().toISOString().split('T')[0];
      let itemTitle = '';
      let itemAmount = 0;
      let itemType: FinanceTransactionType = 'expense';
      let itemCategory: FinanceCategory = batchGlobalCategory || 'groceries';

      if (dateColIdx !== -1 && cells[dateColIdx]) {
        itemDate = parseFlexDate(cells[dateColIdx]);
      } else {
        const dateMatch = line.match(/\b(\d{1,2}[\/.-]\d{1,2}[\/.-]\d{4}|\d{4}[\/.-]\d{1,2}[\/.-]\d{1,2})\b/);
        if (dateMatch) {
          itemDate = parseFlexDate(dateMatch[1]);
        }
      }

      if (titleColIdx !== -1 && cells[titleColIdx]) {
        itemTitle = cells[titleColIdx].trim();
      }

      if (!itemTitle) {
        const textCell = cells.find(c => c.length > 2 && !/^\d+$/.test(c) && !c.includes('/') && !c.toLowerCase().includes('köp') && !c.toLowerCase().includes('sek'));
        itemTitle = textCell ? textCell : `تراکنش ${idx + 1}`;
      }

      let catSourceText = itemTitle;
      if (categoryColIdx !== -1 && cells[categoryColIdx]) {
        catSourceText += ' ' + cells[categoryColIdx];
      }
      const detectedCat = detectCategoryFromText(catSourceText + ' ' + line);
      if (detectedCat !== 'other') {
        itemCategory = detectedCat;
      }

      // Income & Salary Detection Rule
      const textToTest = `${itemTitle} ${line}`.toLowerCase();
      const isSalaryOrIncome =
        line.includes('+') ||
        textToTest.includes('lön') ||
        textToTest.includes('lon ') ||
        textToTest.includes('salary') ||
        textToTest.includes('payroll') ||
        textToTest.includes('insättning') ||
        textToTest.includes('inbetalning') ||
        textToTest.includes('försäkringskassan') ||
        textToTest.includes('csn') ||
        textToTest.includes('a-kassa') ||
        textToTest.includes('حقوق') ||
        textToTest.includes('درآمد') ||
        itemCategory === 'salary';

      // 1. Single Amount column check (Negative = Expense, Positive = Income/Incoming)
      if (amountColIdx !== -1 && cells[amountColIdx]) {
        const rawAmtCell = cells[amountColIdx].trim();
        const amtStr = rawAmtCell.replace(/\s/g, '').replace(',', '.');
        const parsedAmt = parseFloat(amtStr);
        if (!isNaN(parsedAmt)) {
          itemAmount = Math.abs(parsedAmt);
          if (rawAmtCell.startsWith('-') || parsedAmt < 0) {
            itemType = 'expense';
          } else if (rawAmtCell.startsWith('+') || parsedAmt > 0) {
            itemType = 'income';
            if (itemCategory === 'groceries' || itemCategory === 'other') {
              itemCategory = 'salary';
            }
          }
        }
      }

      // 2. Separate Credit / Debit columns check if single Amount column gave no amount
      if (itemAmount === 0 && (creditColIdx !== -1 || debitColIdx !== -1)) {
        if (creditColIdx !== -1 && cells[creditColIdx]) {
          const cVal = parseFloat(cells[creditColIdx].replace(/\s/g, '').replace(',', '.'));
          if (!isNaN(cVal) && cVal > 0) {
            itemAmount = cVal;
            itemType = 'income';
            if (itemCategory === 'groceries' || itemCategory === 'other') itemCategory = 'salary';
          }
        }
        if (itemAmount === 0 && debitColIdx !== -1 && cells[debitColIdx]) {
          const dVal = parseFloat(cells[debitColIdx].replace(/\s/g, '').replace(',', '.'));
          if (!isNaN(dVal) && dVal !== 0) {
            itemAmount = Math.abs(dVal);
            itemType = 'expense';
          }
        }
      }

      // 3. Fallback scan across all cells if itemAmount is still 0
      if (itemAmount === 0) {
        for (let cIdx = 0; cIdx < cells.length; cIdx++) {
          if (cIdx === dateColIdx || cIdx === titleColIdx) continue;
          const c = cells[cIdx];
          const numMatch = c.match(/[-+]?\s*\d+(?:[\.,]\d+)?/);
          if (numMatch) {
            const val = parseFloat(numMatch[0].replace(/\s/g, '').replace(',', '.'));
            if (!isNaN(val) && val !== 0 && !c.includes('/') && !c.includes('-202')) {
              itemAmount = Math.abs(val);
              if (c.trim().startsWith('-') || val < 0) {
                itemType = 'expense';
              } else if (c.trim().startsWith('+') || val > 0) {
                itemType = 'income';
                if (itemCategory === 'groceries' || itemCategory === 'other') {
                  itemCategory = 'salary';
                }
              }
              break;
            }
          }
        }
      }

      // 4. Keyword override ONLY if amount was not explicitly negative
      if (isSalaryOrIncome && itemType !== 'expense') {
        itemType = 'income';
        itemCategory = 'salary';
      }

      let payMethod: FinancePaymentMethod = 'card';
      if (chosenCard.toLowerCase().includes('swish') || line.toLowerCase().includes('swish')) {
        payMethod = 'swish';
      }

      newRows.push({
        id: `batch_${Date.now()}_${idx}_${Math.random().toString(36).substring(2, 6)}`,
        date: itemDate,
        title: itemTitle,
        amount: itemAmount > 0 ? itemAmount : 100,
        type: itemType,
        category: itemCategory,
        cardAccount: chosenCard,
        paymentMethod: payMethod,
        isSelected: true
      });
    }

    setBatchRows(newRows);

    // Run Smart Cross-Matching with Existing DB Transactions
    const deduplicatedRows = newRows.map(r => {
      const matchedTx = transactions.find(tx => 
        tx.date === r.date && Math.abs(tx.amount - r.amount) < 0.1
      );

      if (!matchedTx) {
        return { ...r, matchStatus: 'new' as const };
      }

      const isRowStore = /ica|hemköp|hemkop|willys|coop|lidl|city gross|groceries|فروشگاه|خرید از/i.test(r.title + ' ' + r.category);
      const isMatchedTxStore = /ica|hemköp|hemkop|willys|coop|lidl|city gross|groceries|فروشگاه|خرید از/i.test(matchedTx.title + ' ' + matchedTx.category) || (matchedTx.items && matchedTx.items.length > 0);

      const isRowBank = /bank|kort|debit|credit|seb|handelsbanken|nordea|swedbank|swish|revolut|بانک|کارت/i.test(r.title + ' ' + r.cardAccount);
      const isMatchedTxBank = /bank|kort|debit|credit|seb|handelsbanken|nordea|swedbank|swish|revolut|بانک|کارت/i.test(matchedTx.title + ' ' + matchedTx.cardAccount);

      if (isRowStore && isMatchedTxBank && !isMatchedTxStore) {
        return {
          ...r,
          matchStatus: 'match_merge_store' as const,
          matchedTxTitle: matchedTx.title,
          matchedTxId: matchedTx.id,
          cardAccount: matchedTx.cardAccount || r.cardAccount
        };
      } else if (isRowBank && isMatchedTxStore) {
        return {
          ...r,
          matchStatus: 'match_merge_bank' as const,
          matchedTxTitle: matchedTx.title,
          matchedTxId: matchedTx.id,
          isSelected: true
        };
      } else {
        return {
          ...r,
          matchStatus: 'duplicate_skip' as const,
          matchedTxTitle: matchedTx.title,
          matchedTxId: matchedTx.id,
          isSelected: false
        };
      }
    });

    setBatchRows(deduplicatedRows);
    setBatchSuccessMsg(null);
  };

  const handleConfirmBatchInsert = async () => {
    const selectedRows = batchRows.filter(r => r.isSelected && r.amount > 0 && r.title.trim());
    if (selectedRows.length === 0) return;

    const newTxsToAdd: Omit<FinanceTransaction, 'id' | 'createdAt'>[] = [];
    let mergedBankCount = 0;
    let mergedStoreCount = 0;
    let skippedCount = 0;

    for (const r of selectedRows) {
      if (r.matchStatus === 'match_merge_bank' && r.matchedTxId) {
        const existing = transactions.find(t => t.id === r.matchedTxId);
        if (existing) {
          await updateTransaction({
            ...existing,
            cardAccount: r.cardAccount || existing.cardAccount || 'SEB Debit'
          });
          mergedBankCount++;
        }
      } else if (r.matchStatus === 'match_merge_store' && r.matchedTxId) {
        const existing = transactions.find(t => t.id === r.matchedTxId);
        if (existing) {
          await updateTransaction({
            ...existing,
            title: r.title,
            category: r.category,
            cardAccount: existing.cardAccount || r.cardAccount,
            notes: (existing.notes ? existing.notes + ' • ' : '') + t('financeBatchMergeStoreNotes', 'تکمیل‌شده با فاکتور فروشگاه {{store}}', { store: r.title })
          });
          mergedStoreCount++;
        }
      } else if (r.matchStatus === 'duplicate_skip') {
        skippedCount++;
      } else {
        newTxsToAdd.push({
          type: r.type,
          title: r.title.trim(),
          amount: r.amount,
          currency: budgetConfig.currency || 'ریال',
          paymentMethod: r.paymentMethod || 'card',
          cardAccount: r.cardAccount || userCards[0] || 'Handelsbanken Debit',
          category: r.category || 'groceries',
          date: r.date || new Date().toISOString().split('T')[0],
          notes: r.notes || t('financeBatchDefaultNotes', 'ثبت شده از ورود دسته‌ای')
        });
      }
    }

    if (newTxsToAdd.length > 0) {
      await addBatchTransactions(newTxsToAdd);
    }

    let statusParts = [];
    if (newTxsToAdd.length > 0) statusParts.push(t('financeBatchStatusNew', '{{count}} تراکنش جدید ثبت شد', { count: formatNumberByLang(newTxsToAdd.length, lang) }));
    if (mergedBankCount > 0) statusParts.push(t('financeBatchStatusBankMerge', '{{count}} تراکنش بانکی با خرید فروشگاهی (ICA/...) تطبیق و کارت بانک اعمال گردید', { count: formatNumberByLang(mergedBankCount, lang) }));
    if (mergedStoreCount > 0) statusParts.push(t('financeBatchStatusStoreMerge', '{{count}} فاکتور فروشگاه جایگزین تراکنش بانکی شد', { count: formatNumberByLang(mergedStoreCount, lang) }));
    if (skippedCount > 0) statusParts.push(t('financeBatchStatusSkip', '{{count}} مورد تکراری نادیده گرفته شد', { count: formatNumberByLang(skippedCount, lang) }));

    setBatchSuccessMsg(statusParts.join(' • '));
    setBatchRows([]);
    setBatchRawText('');
  };

  // AI Invoice Scanner Parsing Trigger
  const handleParseFactorText = async () => {
    if (!factorText.trim()) return;
    setAiParsing(true);
    setAiError(null);
    setParsedPreview(null);

    const prompt = `شما یک دستیار هوشمند و حسابدار دقیق در خواندن انواع فاکتورها، رسیدهای خرید فروشگاهی اروپایی (ICA, Willys, Lidl, Coop, Hemköp, City Gross, Systembolaget, Rusta, IKEA) و فاکتورهای ایرانی هستید.
لطفاً متن فاکتور/رسید زیر را به دقت تحلیل نموده و تمامی اقلام، مقادیر، قیمت‌ها، تخفیف‌ها، نام فروشگاه، تاریخ، جمع کل و روش پرداخت را استخراج کنید.

متن فاکتور:
"""
${factorText}
"""

دستورالعمل‌های استخراج:
1. نام فروشگاه یا عنوان فاکتور را مشخص کنید (مثلاً Willys، ICA، Coop یا عنوان مناسب).
2. تاریخ فاکتور را به فرمت YYYY-MM-DD استخراج کنید.
3. جمع کل واقعی پرداختی را به عنوان amount استخراج کنید.
4. تمام اقلام و کالاها را با نام (name)، تعداد (quantity)، قیمت واحد (unitPrice) و قیمت کل (totalPrice) استخراج نمایید. در صورت وجود تخفیف (Rabatt/Discount)، مبلغ تخفیف را از قیمت کالا کسر کنید.
5. دسته‌بندی را از بین: (groceries | restaurants | clothing | electronics | utilities | transport | health | home_decor | other) انتخاب کنید.
6. واحد پول را مشخص کنید (SEK یا ریال یا تومان یا EUR یا USD).
7. نوع پرداخت (swish | card | cash | transfer) و نام کارت را تشخیص دهید.

پاسخ را دقیقاً و صرفاً به صورت یک شیء JSON با ساختار زیر بازگردانید (بدون توضیحات اضافی):
{
  "title": "خرید از Willys",
  "amount": 250.50,
  "currency": "SEK",
  "category": "groceries",
  "date": "2026-08-19",
  "paymentMethod": "card",
  "cardAccount": "Handelsbanken Debit",
  "notes": "فاکتور خرید فروشگاهی",
  "items": [
    {
      "name": "Mellanmjölk",
      "category": "سوپرمارکت",
      "quantity": 2,
      "unitPrice": 14.50,
      "totalPrice": 29.00
    }
  ]
}`;

    let parsedResult: any = null;

    try {
      // 1. Direct Cloudflare Worker AI Request (same as rest of app)
      const aiRes = await sendWorkerAiFullRequest('general', prompt, []);
      if (aiRes && aiRes.reply) {
        parsedResult = parseAiInvoiceJson(aiRes.reply, budgetConfig.currency || 'SEK', userCards[0] || 'Handelsbanken Debit');
      }
    } catch (workerErr) {
      console.warn('Direct Cloudflare Worker AI call error:', workerErr);
    }

    // 2. Server API fallback if direct worker had issue
    if (!parsedResult || !parsedResult.title) {
      try {
        const res = await fetch('/api/parse-invoice', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ factorText, rawInvoiceText: factorText })
        });
        if (res.ok) {
          const data = await res.json();
          if (data.parsedInvoice) {
            parsedResult = data.parsedInvoice;
          }
        }
      } catch (fetchErr) {
        console.warn('Backend /api/parse-invoice fallback error:', fetchErr);
      }
    }

    // 3. Deterministic Local Receipt Parser Fallback
    if (!parsedResult || !parsedResult.title) {
      parsedResult = parseReceiptTextLocally(factorText, budgetConfig.currency || 'SEK', userCards[0] || 'Handelsbanken Debit');
    }

    if (parsedResult) {
      const itemsList: InvoiceItem[] = Array.isArray(parsedResult.items)
        ? parsedResult.items.map((it: any, idx: number) => {
            const q = Number(it.quantity) || 1;
            const up = Number(it.unitPrice) || Number(it.totalPrice) || 0;
            const tp = Number(it.totalPrice) || (q * up);
            return {
              id: `item_${Date.now()}_${idx}`,
              name: it.name || `کالا ${idx + 1}`,
              category: it.category || 'سوپرمارکت',
              quantity: q,
              unitPrice: up,
              totalPrice: tp
            };
          })
        : [];

      const totalItemsSum = itemsList.reduce((sum, it) => sum + it.totalPrice, 0);
      const finalAmt = totalItemsSum > 0 ? totalItemsSum : (Number(parsedResult.amount) || 0);

      setParsedPreview({
        type: 'expense',
        title: parsedResult.title || t('financeAiNewInvoiceTitle', 'فاکتور جدید'),
        amount: finalAmt,
        currency: parsedResult.currency || budgetConfig.currency || 'SEK',
        paymentMethod: parsedResult.paymentMethod || 'card',
        cardAccount: parsedResult.cardAccount || userCards[0] || 'Handelsbanken Debit',
        category: parsedResult.category || detectCategoryFromText(parsedResult.title || ''),
        date: parsedResult.date || new Date().toISOString().split('T')[0],
        notes: parsedResult.notes || t('financeAiInvoiceAnalysis', 'تحلیل هوشمند فاکتور {{store}}', { store: parsedResult.title || '' }),
        items: itemsList
      });
    } else {
      setAiError(t('financeAiParseError', 'استخراج اطلاعات فاکتور ناموفق بود.'));
    }

    setAiParsing(false);
  };

  const handleConfirmSaveAiInvoice = async () => {
    if (!parsedPreview) return;

    const calculatedAmount = (parsedPreview.items && parsedPreview.items.length > 0)
      ? Math.round(parsedPreview.items.reduce((sum, item) => sum + (Number(item.totalPrice) || 0), 0) * 100) / 100
      : (parsedPreview.amount || 0);

    const invDate = parsedPreview.date || new Date().toISOString().split('T')[0];
    const invTitle = parsedPreview.title || t('financeAiNewInvoiceTitle', 'فاکتور جدید');

    // Smart deduplication: check if a matching bank transaction exists
    const invTime = new Date(invDate).getTime();
    const lowerInvTitle = invTitle.toLowerCase();
    const storeKeywords = ['willys', 'ica', 'hemköp', 'hemkop', 'lidl', 'coop', 'city gross', 'rusta', 'oob', 'oöb', 'shami', 'zara', 'h&m', 'sl', 'circle k', 'okq8', 'mcdonald', 'max'];
    const matchedKeyword = storeKeywords.find(k => lowerInvTitle.includes(k));

    const existingMatch = transactions.find(tx => {
      const amtDiff = Math.abs(tx.amount - calculatedAmount);
      if (amtDiff > 1.0) return false;

      const txTime = new Date(tx.date).getTime();
      const daysDiff = Math.abs(invTime - txTime) / (1000 * 60 * 60 * 24);
      if (daysDiff > 2) return false;

      const lowerTxTitle = tx.title.toLowerCase();
      if (matchedKeyword && lowerTxTitle.includes(matchedKeyword)) return true;
      if (amtDiff < 0.05 && daysDiff <= 1 && (!tx.items || tx.items.length === 0)) return true;

      return false;
    });

    if (existingMatch) {
      // Replace / upgrade existing raw bank transaction with full itemized AI invoice
      await updateTransaction({
        ...existingMatch,
        title: invTitle,
        amount: calculatedAmount,
        currency: parsedPreview.currency || existingMatch.currency || budgetConfig.currency || 'SEK',
        paymentMethod: parsedPreview.paymentMethod || existingMatch.paymentMethod || 'card',
        cardAccount: parsedPreview.cardAccount || existingMatch.cardAccount || userCards[0] || 'Debit Card',
        category: parsedPreview.category || existingMatch.category || 'groceries',
        date: invDate,
        notes: (existingMatch.notes ? existingMatch.notes + ' • ' : '') + t('financeAiSmartReplaceNote', 'جایگزینی هوشمند تراکنش خام بانک با جزئیات فاکتور اسکن‌شده'),
        items: parsedPreview.items || []
      });
    } else {
      await addTransaction({
        type: parsedPreview.type || 'expense',
        title: invTitle,
        amount: calculatedAmount,
        currency: parsedPreview.currency || budgetConfig.currency || 'SEK',
        paymentMethod: parsedPreview.paymentMethod || 'card',
        cardAccount: parsedPreview.cardAccount || userCards[0] || 'Debit Card',
        category: parsedPreview.category || 'groceries',
        date: invDate,
        notes: parsedPreview.notes || t('financeAiScannerNote', 'ثبت شده توسط اسکنر AI'),
        items: parsedPreview.items || []
      });
    }

    setParsedPreview(null);
    setFactorText('');
    setActiveTab('overview');
  };

  // Financial Goals Handlers
  const openNewGoalModal = () => {
    setEditingGoal(null);
    setGoalTitle('');
    setGoalTargetAmount('');
    setGoalCurrentSavings('');
    setGoalCurrency(budgetConfig.currency || 'SEK');
    setGoalTargetDate('');
    setGoalCategory('housing');
    setGoalNotes('');
    setShowGoalModal(true);
  };

  const openEditGoalModal = (goal: FinancialGoal) => {
    setEditingGoal(goal);
    setGoalTitle(goal.title || '');
    setGoalTargetAmount(goal.targetAmount ? goal.targetAmount.toString() : '');
    setGoalCurrentSavings(goal.currentSavings ? goal.currentSavings.toString() : '0');
    setGoalCurrency(goal.currency || budgetConfig.currency || 'SEK');
    setGoalTargetDate(goal.targetDate || '');
    setGoalCategory(goal.category || 'housing');
    setGoalNotes(goal.notes || '');
    setShowGoalModal(true);
  };

  const handleSaveGoal = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!goalTitle.trim() || !goalTargetAmount) return;

    if (editingGoal) {
      await updateFinancialGoal({
        ...editingGoal,
        title: goalTitle.trim(),
        targetAmount: parseFloat(goalTargetAmount) || 0,
        currentSavings: parseFloat(goalCurrentSavings) || 0,
        currency: goalCurrency,
        targetDate: goalTargetDate || undefined,
        category: goalCategory,
        notes: goalNotes.trim() || undefined
      });
    } else {
      await addFinancialGoal({
        title: goalTitle.trim(),
        targetAmount: parseFloat(goalTargetAmount) || 0,
        currentSavings: parseFloat(goalCurrentSavings) || 0,
        currency: goalCurrency,
        targetDate: goalTargetDate || undefined,
        category: goalCategory,
        notes: goalNotes.trim() || undefined
      });
    }

    setShowGoalModal(false);
    setEditingGoal(null);
    setGoalTitle('');
    setGoalTargetAmount('');
    setGoalCurrentSavings('');
    setGoalTargetDate('');
    setGoalNotes('');
  };

  const handleDepositToGoal = async () => {
    if (!showDepositModal || !depositAmount) return;
    const amt = parseFloat(depositAmount) || 0;
    if (amt > 0) {
      await depositToGoal(showDepositModal.id, amt);
      
      // Auto register a savings transaction
      await addTransaction({
        type: 'savings',
        title: t('financeGoalDepositTitle', 'پس‌انداز برای: {{goalTitle}}', { goalTitle: showDepositModal.title }),
        amount: amt,
        currency: showDepositModal.currency,
        paymentMethod: 'swish',
        category: 'investment',
        date: new Date().toISOString().split('T')[0],
        notes: t('financeGoalDepositNote', 'افزایش موجودی هدف {{goalTitle}}', { goalTitle: showDepositModal.title })
      });
    }
    setShowDepositModal(null);
    setDepositAmount('');
  };

  return (
    <div className="max-w-7xl mx-auto space-y-6 pb-12 font-sans" dir="rtl">
      
      {/* Header Banner */}
      <div className="p-6 bg-gradient-to-r from-emerald-600 via-teal-600 to-indigo-700 rounded-3xl text-white shadow-xl flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div className="space-y-1.5">
          <div className="flex items-center gap-2">
            <div className="p-2.5 bg-white/10 backdrop-blur-md rounded-2xl">
              <Wallet className="w-6 h-6 text-emerald-200" />
            </div>
            <h1 className="text-xl md:text-2xl font-extrabold tracking-tight">
              {t('financeHeaderTitle', 'مدیریت هوشمند فاکتورها، بودجه و اهداف مالی')}
            </h1>
          </div>
          <p className="text-xs text-emerald-100 opacity-90 max-w-2xl leading-relaxed">
            {t('financeHeaderDesc', 'بودجه‌بندی درآمد خالص پس از مالیات، اسکن خودکار فاکتورها با هوش مصنوعی و پیگیری اهداف پس‌انداز')}
          </p>
        </div>

        <button
          onClick={() => {
            resetTxForm();
            setShowAddModal(true);
          }}
          className="px-5 py-3 bg-white text-emerald-800 hover:bg-emerald-50 rounded-2xl font-bold text-xs shadow-lg transition-all flex items-center gap-2 cursor-pointer shrink-0"
        >
          <Plus className="w-4 h-4 text-emerald-600" />
          <span>{t('addInvoiceCostBtn', 'ثبت فاکتور / هزینه جدید')}</span>
        </button>
      </div>

      {/* Global Base Currency Control Bar */}
      <div className="px-5 py-3.5 bg-white dark:bg-gray-900 rounded-2xl border border-emerald-200 dark:border-emerald-900/60 shadow-xs flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <div className="p-2 rounded-xl bg-emerald-50 dark:bg-emerald-950 text-emerald-600 dark:text-emerald-400">
            <Coins className="w-5 h-5" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold text-gray-900 dark:text-white">{t('baseCurrencyLabel', 'ارز پایه پیش‌فرض سیستم (Base Currency):')}</span>
              <span className="text-[10px] px-2.5 py-0.5 bg-emerald-100 text-emerald-800 dark:bg-emerald-900/80 dark:text-emerald-200 rounded-full font-extrabold border border-emerald-300 dark:border-emerald-700">
                {budgetConfig.currency}
              </span>
            </div>
            <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5">
              {t('baseCurrencySyncDesc', 'تغییر این گزینه، تمام محاسبات بودجه‌بندی، کارت‌های شاخص، اسکنر AI و اهداف مالی را همگام‌سازی می‌کند.')}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 w-full sm:w-auto shrink-0 flex-wrap">
          <button
            onClick={() => setShowCardManagerModal(true)}
            className="flex items-center gap-1.5 px-3.5 py-2 bg-indigo-50 dark:bg-indigo-950/60 hover:bg-indigo-100 text-indigo-700 dark:text-indigo-300 rounded-xl text-xs font-bold transition-all border border-indigo-200 dark:border-indigo-800 cursor-pointer shrink-0"
          >
            <CreditCard className="w-4 h-4 text-indigo-500" />
            <span>{t('myCardsAndAccounts', '💳 کارت‌ها و حساب‌های من')} ({formatNumberByLang(userCards.length, lang)})</span>
          </button>

          <label className="text-xs font-bold text-gray-700 dark:text-gray-300 whitespace-nowrap">{t('quickCurrencyChange', 'تغییر سریع ارز پایه:')}</label>
          <select
            value={budgetConfig.currency || 'ریال'}
            onChange={(e) => handleBaseCurrencyChange(e.target.value)}
            className="p-2 bg-emerald-50/90 dark:bg-gray-800 rounded-xl border border-emerald-300 dark:border-gray-700 text-xs font-extrabold text-emerald-900 dark:text-emerald-300 cursor-pointer focus:ring-2 focus:ring-emerald-500 w-full sm:w-auto"
          >
            {CURRENCY_OPTIONS.map((c) => (
              <option key={c.code} value={c.code}>
                {t(c.nameKey, c.defaultLabel)}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Main Navigation Tabs */}
      <div className="flex items-center gap-2 overflow-x-auto p-1.5 bg-gray-100 dark:bg-gray-800/80 rounded-2xl">
        <button
          onClick={() => setActiveTab('overview')}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${
            activeTab === 'overview'
              ? 'bg-white dark:bg-gray-900 text-indigo-600 dark:text-indigo-400 shadow-sm'
              : 'text-gray-600 dark:text-gray-400 hover:text-gray-900'
          }`}
        >
          <Receipt className="w-4 h-4 text-indigo-500" />
          <span>{t('overviewAndTxs', 'نمای کلی و تراکنش‌ها')} ({formatNumberByLang(transactions.length, lang)})</span>
        </button>

        <button
          onClick={() => setActiveTab('budget')}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${
            activeTab === 'budget'
              ? 'bg-white dark:bg-gray-900 text-emerald-600 dark:text-emerald-400 shadow-sm'
              : 'text-gray-600 dark:text-gray-400 hover:text-gray-900'
          }`}
        >
          <Calculator className="w-4 h-4 text-emerald-500" />
          <span>{t('budgetAndNetIncome', 'بودجه‌بندی و درآمد خالص')}</span>
        </button>

        <button
          onClick={() => setActiveTab('batch')}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${
            activeTab === 'batch'
              ? 'bg-white dark:bg-gray-900 text-blue-600 dark:text-blue-400 shadow-sm'
              : 'text-gray-600 dark:text-gray-400 hover:text-gray-900'
          }`}
        >
          <FileSpreadsheet className="w-4 h-4 text-blue-500" />
          <span>{t('batchImportTab', 'ورود دسته‌ای صورت‌حساب')}</span>
        </button>

        <button
          onClick={() => setActiveTab('scanner')}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${
            activeTab === 'scanner'
              ? 'bg-white dark:bg-gray-900 text-purple-600 dark:text-purple-400 shadow-sm'
              : 'text-gray-600 dark:text-gray-400 hover:text-gray-900'
          }`}
        >
          <Sparkles className="w-4 h-4 text-purple-500" />
          <span>{t('aiScannerTab', 'اسکنر هوشمند فاکتور (AI)')}</span>
        </button>

        <button
          onClick={() => setActiveTab('products')}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${
            activeTab === 'products'
              ? 'bg-white dark:bg-gray-900 text-sky-600 dark:text-sky-400 shadow-sm'
              : 'text-gray-600 dark:text-gray-400 hover:text-gray-900'
          }`}
        >
          <ShoppingBag className="w-4 h-4 text-sky-500" />
          <span>{t('productAnalyticsTab', 'تحلیل مصرف کالاها')}</span>
        </button>

        <button
          onClick={() => setActiveTab('goals')}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${
            activeTab === 'goals'
              ? 'bg-white dark:bg-gray-900 text-amber-600 dark:text-amber-400 shadow-sm'
              : 'text-gray-600 dark:text-gray-400 hover:text-gray-900'
          }`}
        >
          <Target className="w-4 h-4 text-amber-500" />
          <span>{t('goalsTab', 'اهداف مالی')} ({formatNumberByLang(goals.length, lang)})</span>
        </button>

        <button
          onClick={() => setActiveTab('advisor')}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${
            activeTab === 'advisor'
              ? 'bg-gradient-to-r from-teal-600 to-emerald-600 text-white shadow-md'
              : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
          }`}
        >
          <Brain className={`w-4 h-4 ${activeTab === 'advisor' ? 'text-white' : 'text-teal-500'}`} />
          <span>{t('advisorTab', 'مشاور هوشمند مالی (AI Advisor)')}</span>
        </button>
      </div>

      {/* TAB 1: OVERVIEW & TRANSACTIONS */}
      {activeTab === 'overview' && (
        <div className="space-y-6">

          {/* Search, Date Range & Multi-Filters Bar (PLACED AT TOP) */}
          <div className="p-4 md:p-5 bg-white dark:bg-gray-900 rounded-3xl border border-gray-100 dark:border-gray-800 shadow-sm space-y-3">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
              <div className="relative flex-1">
                <Search className="absolute right-3.5 top-3 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder={t('financeSearchPlaceholder', 'جستجو در فاکتورها، عنوان فروشگاه، کارت یا نام اقلام...')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-4 pr-10 py-2.5 bg-gray-50 dark:bg-gray-800 rounded-2xl text-xs border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 font-sans"
                />
              </div>

              <div className="flex items-center gap-2 overflow-x-auto pb-1 md:pb-0">
                <select
                  value={filterCard}
                  onChange={(e) => setFilterCard(e.target.value)}
                  className="px-3 py-2 bg-gray-50 dark:bg-gray-800 rounded-2xl text-xs border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white focus:outline-none font-bold text-indigo-700 dark:text-indigo-300"
                >
                  <option value="all">{t('allCardsOption', '💳 همه کارت‌ها و حساب‌ها')}</option>
                  {availableCardsList.map(card => (
                    <option key={card} value={card}>💳 {card}</option>
                  ))}
                </select>

                <select
                  value={filterPayment}
                  onChange={(e) => setFilterPayment(e.target.value)}
                  className="px-3 py-2 bg-gray-50 dark:bg-gray-800 rounded-2xl text-xs border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white focus:outline-none"
                >
                  <option value="all">{t('filterAllPayments', 'همه روش‌های پرداخت')}</option>
                  <option value="swish">{t('filterSwish', 'سوئیش (Swish)')}</option>
                  <option value="cash">{t('filterCash', 'نقد (Cash)')}</option>
                  <option value="card">{t('filterCard', 'کارت بانکی')}</option>
                  <option value="transfer">{t('filterTransfer', 'انتقال حساب')}</option>
                </select>

                <select
                  value={filterCategory}
                  onChange={(e) => setFilterCategory(e.target.value)}
                  className="px-3 py-2 bg-gray-50 dark:bg-gray-800 rounded-2xl text-xs border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white focus:outline-none font-bold"
                >
                  <option value="all">{t('filterAllCategories', 'همه دسته‌بندی‌ها')}</option>
                  <option value="groceries">{t('catGroceries', '🛒 سوپرمارکت و خواربار')}</option>
                  <option value="housing">{t('catHousing', '🏠 مسکن و اجاره/رهن')}</option>
                  <option value="utilities">{t('catUtilities', '⚡ قبوض، انرژی و اینترنت')}</option>
                  <option value="transport">{t('catTransport', '🚗 حمل‌و‌نقل و سوخت')}</option>
                  <option value="restaurants">{t('catRestaurants', '🍽️ رستوران، کافه و غذا')}</option>
                  <option value="health">{t('catHealth', '🩺 سلامت، درمان و دارو')}</option>
                  <option value="clothing">{t('catClothing', '👗 پوشاک، کفش و زیبایی')}</option>
                  <option value="electronics">{t('catElectronics', '💻 کالای دیجیتال و الکترونیک')}</option>
                  <option value="home_decor">{t('catHomeDecor', '🛋️ لوازم خانگی و دکوراسیون')}</option>
                  <option value="car_maintenance">{t('catCarMaintenance', '🔧 تعمیرات خودرو و بیمه')}</option>
                  <option value="subscriptions">{t('catSubscriptions', '🌐 اشتراک‌های دیجیتال و AI')}</option>
                  <option value="pets">{t('catPets', '🐾 حیوانات خانگی و پت‌شاپ')}</option>
                  <option value="charity_gifts">{t('catCharityGifts', '🎁 هدایا، کادو و خیریه')}</option>
                  <option value="education">{t('catEducation', '🎓 آموزش و توسعه فردی')}</option>
                  <option value="entertainment">{t('catEntertainment', '🎉 تفریح، ورزش و سفر')}</option>
                  <option value="family">{t('catFamily', '👶 امور فرزندان و خانواده')}</option>
                  <option value="salary">{t('catSalary', '💼 حقوق و درآمد اصلی')}</option>
                  <option value="investment">{t('catInvestment', '📈 پس‌انداز و سرمایه‌گذاری')}</option>
                  <option value="other">{t('catOther', '🏷️ خدمات و متفرقه')}</option>
                </select>
              </div>
            </div>

            {/* Date Range Filter Strip & Quick Buttons */}
            <div className="pt-3 border-t border-gray-100 dark:border-gray-800 flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="flex items-center gap-1.5 text-gray-800 dark:text-gray-200 font-extrabold shrink-0">
                  <Calendar className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                  <span>{t('filterDateLabel', 'فیلتر تاریخ:')}</span>
                </div>

                <div className="flex items-center gap-2 bg-gray-50 dark:bg-gray-800/90 p-1.5 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-2xs">
                  <div className="flex items-center gap-1">
                    <span className="text-[11px] text-gray-500 font-bold">{t('filterFrom', 'از:')}</span>
                    <input
                      type="date"
                      value={filterStartDate}
                      onChange={(e) => setFilterStartDate(e.target.value)}
                      className="px-2 py-1 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl text-xs font-mono text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  <span className="text-gray-300 dark:text-gray-600 font-mono">|</span>
                  <div className="flex items-center gap-1">
                    <span className="text-[11px] text-gray-500 font-bold">{t('filterTo', 'تا:')}</span>
                    <input
                      type="date"
                      value={filterEndDate}
                      onChange={(e) => setFilterEndDate(e.target.value)}
                      className="px-2 py-1 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl text-xs font-mono text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                </div>

                {/* Quick Date Presets */}
                <div className="flex items-center gap-1.5 flex-wrap">
                  <button
                    onClick={() => {
                      const now = new Date();
                      const y = now.getFullYear();
                      const m = String(now.getMonth() + 1).padStart(2, '0');
                      const d = String(now.getDate()).padStart(2, '0');
                      setFilterStartDate(`${y}-${m}-${d}`);
                      setFilterEndDate(`${y}-${m}-${d}`);
                    }}
                    className={`px-3 py-1.5 rounded-xl text-[11px] font-bold transition-all cursor-pointer ${
                      filterStartDate && filterStartDate === filterEndDate && filterStartDate.length === 10
                        ? 'bg-indigo-600 text-white shadow-xs'
                        : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                    }`}
                  >
                    {t('filterToday', 'امروز')}
                  </button>

                  <button
                    onClick={() => {
                      const now = new Date();
                      const y = now.getFullYear();
                      const m = now.getMonth() + 1;
                      const lastDay = new Date(y, m, 0).getDate();
                      const mStr = String(m).padStart(2, '0');
                      setFilterStartDate(`${y}-${mStr}-01`);
                      setFilterEndDate(`${y}-${mStr}-${String(lastDay).padStart(2, '0')}`);
                    }}
                    className={`px-3 py-1.5 rounded-xl text-[11px] font-bold transition-all cursor-pointer ${
                      filterStartDate.endsWith('-01') && filterEndDate.length === 10 && filterStartDate.slice(0, 7) === filterEndDate.slice(0, 7)
                        ? 'bg-indigo-600 text-white shadow-xs'
                        : 'bg-indigo-50 dark:bg-indigo-950/60 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-100 transition-colors'
                    }`}
                  >
                    {t('filterThisMonth', 'این ماه')}
                  </button>

                  <button
                    onClick={() => {
                      const now = new Date();
                      const yNow = now.getFullYear();
                      const mNow = now.getMonth() + 1;
                      const lastDayNow = new Date(yNow, mNow, 0).getDate();
                      const past3 = new Date(now.getFullYear(), now.getMonth() - 2, 1);
                      const yPast = past3.getFullYear();
                      const mPastStr = String(past3.getMonth() + 1).padStart(2, '0');
                      setFilterStartDate(`${yPast}-${mPastStr}-01`);
                      setFilterEndDate(`${yNow}-${String(mNow).padStart(2, '0')}-${String(lastDayNow).padStart(2, '0')}`);
                    }}
                    className={`px-3 py-1.5 rounded-xl text-[11px] font-bold transition-all cursor-pointer ${
                      filterStartDate && filterEndDate && filterStartDate !== filterEndDate && !filterStartDate.endsWith('-31') && filterStartDate.slice(0, 7) !== filterEndDate.slice(0, 7)
                        ? 'bg-indigo-600 text-white shadow-xs'
                        : 'bg-indigo-50 dark:bg-indigo-950/60 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-100 transition-colors'
                    }`}
                  >
                    {t('filterLast3Months', '۳ ماه اخیر')}
                  </button>

                  {(filterStartDate || filterEndDate || searchTerm || filterPayment !== 'all' || filterCard !== 'all' || filterCategory !== 'all') && (
                    <button
                      onClick={() => {
                        setFilterStartDate('');
                        setFilterEndDate('');
                        setSearchTerm('');
                        setFilterPayment('all');
                        setFilterCard('all');
                        setFilterCategory('all');
                      }}
                      className="px-3 py-1.5 bg-rose-50 text-rose-700 dark:bg-rose-950/60 dark:text-rose-300 rounded-xl text-[11px] font-bold hover:bg-rose-100 transition-colors cursor-pointer flex items-center gap-1"
                    >
                      <RotateCcw className="w-3.5 h-3.5" />
                      <span>{t('clearFilters', 'پاکسازی فیلترها')}</span>
                    </button>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-1 text-[11px] text-gray-500 dark:text-gray-400 font-bold bg-slate-100 dark:bg-gray-800/80 px-2.5 py-1 rounded-xl shrink-0">
                <ArrowDownLeft className="w-3.5 h-3.5 text-emerald-500" />
                <span>{t('sortNewestFirst', 'ترتیب: جدیدترین به قدیمی‌ترین ⬇️')}</span>
              </div>
            </div>
          </div>
          
          {/* Summary Metric Cards (Dynamically reacts to filters above) */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            
            <div className="p-4 bg-white dark:bg-gray-900 rounded-3xl border border-gray-100 dark:border-gray-800 shadow-sm space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-gray-500">{t('statTotalIncome', 'درآمد کل ثبت‌شده')}</span>
                <div className="p-2 rounded-xl bg-emerald-50 dark:bg-emerald-950/50 text-emerald-600">
                  <ArrowDownLeft className="w-4 h-4" />
                </div>
              </div>
              <p className="text-2xl font-extrabold text-emerald-600 dark:text-emerald-400 font-sans tabular-nums tracking-tight">
                {formatNumberByLang(stats.totalIncome.toLocaleString(), lang)} <span className="text-xs font-bold">{budgetConfig.currency}</span>
              </p>
              <p className="text-[10px] text-gray-400">{t('statIncomeSubtitle', 'مجموع دریافتی‌ها و حقوق')}</p>
            </div>

            <div className="p-4 bg-white dark:bg-gray-900 rounded-3xl border border-gray-100 dark:border-gray-800 shadow-sm space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-gray-500">{t('statTotalExpense', 'هزینه‌ها و فاکتورها')}</span>
                <div className="p-2 rounded-xl bg-rose-50 dark:bg-rose-950/50 text-rose-600">
                  <ArrowUpRight className="w-4 h-4" />
                </div>
              </div>
              <p className="text-2xl font-extrabold text-rose-600 dark:text-rose-400 font-sans tabular-nums tracking-tight">
                {formatNumberByLang(stats.totalExpense.toLocaleString(), lang)} <span className="text-xs font-bold">{budgetConfig.currency}</span>
              </p>
              <p className="text-[10px] text-gray-400">{t('statExpenseSubtitle', 'خریدها، فاکتورها و پرداخت‌ها')}</p>
            </div>

            <div className="p-4 bg-white dark:bg-gray-900 rounded-3xl border border-gray-100 dark:border-gray-800 shadow-sm space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-gray-500">{t('statBudgetBalance', 'مانده بودجه هزینه')}</span>
                <div className="p-2 rounded-xl bg-indigo-50 dark:bg-indigo-950/50 text-indigo-600">
                  <Coins className="w-4 h-4" />
                </div>
              </div>
              <p className={`text-2xl font-extrabold font-sans tabular-nums tracking-tight ${monthBudgetStats.remainingExpenseBudget >= 0 ? 'text-indigo-600 dark:text-indigo-400' : 'text-rose-600'}`}>
                {formatNumberByLang(monthBudgetStats.remainingExpenseBudget.toLocaleString(), lang)} <span className="text-xs font-bold">{budgetConfig.currency}</span>
              </p>
              <p className="text-[10px] text-gray-400">
                {t('statBudgetCap', 'سقف بودجه ماهانه: {{amount}} {{currency}}', { amount: formatNumberByLang(monthBudgetStats.expenseCap.toLocaleString(), lang), currency: budgetConfig.currency })}
              </p>
            </div>

            <div className="p-4 bg-white dark:bg-gray-900 rounded-3xl border border-gray-100 dark:border-gray-800 shadow-sm space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-gray-500">{t('statSwishTotal', 'پرداخت با Swish')}</span>
                <div className="p-2 rounded-xl bg-sky-50 dark:bg-sky-950/50 text-sky-600">
                  <CreditCard className="w-4 h-4" />
                </div>
              </div>
              <p className="text-2xl font-extrabold text-sky-600 dark:text-sky-400 font-sans tabular-nums tracking-tight">
                {formatNumberByLang(stats.swishTotal.toLocaleString(), lang)} <span className="text-xs font-bold">{budgetConfig.currency}</span>
              </p>
              <p className="text-[10px] text-gray-400">{t('statSwishSubtitle', 'تراکنش‌های سوئیش آنلاین')}</p>
            </div>

          </div>

          {/* Cards & Bank Accounts Breakdown Strip */}
          {cardSpendingBreakdown.length > 0 && (
            <div className="p-4 bg-slate-50 dark:bg-slate-900/70 rounded-3xl border border-slate-200 dark:border-slate-800 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CreditCard className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                  <span className="text-xs font-extrabold text-gray-900 dark:text-white">
                    {t('cardBreakdownTitle', 'خلاصه موازنه و کارکرد کارت‌ها و حساب‌های پرداختی (خالص -/+):')}
                  </span>
                </div>
                {filterCard !== 'all' && (
                  <button
                    onClick={() => setFilterCard('all')}
                    className="text-[10px] text-indigo-600 dark:text-indigo-400 hover:underline font-bold"
                  >
                    {t('showAllCards', 'نمایش همه کارت‌ها')}
                  </button>
                )}
              </div>

              <div className="flex items-center gap-2 overflow-x-auto pb-1">
                {cardSpendingBreakdown.map(({ card, total, expenseTotal, incomeTotal }) => {
                  const isSelected = filterCard === card;
                  const isNegativeNet = total < 0;
                  return (
                    <button
                      key={card}
                      onClick={() => setFilterCard(isSelected ? 'all' : card)}
                      title={`هزینه‌ها: ${expenseTotal.toLocaleString()} ${budgetConfig.currency} | دریافتی/درآمد: ${incomeTotal.toLocaleString()} ${budgetConfig.currency}`}
                      className={`px-3 py-2 rounded-2xl border text-xs flex items-center gap-2 cursor-pointer transition-all shrink-0 ${
                        isSelected
                          ? 'bg-indigo-600 text-white border-indigo-600 shadow-sm'
                          : 'bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 border-gray-200 dark:border-gray-700 hover:border-indigo-300'
                      }`}
                    >
                      <CreditCard className={`w-3.5 h-3.5 ${isSelected ? 'text-white' : 'text-slate-500'}`} />
                      <span className="font-bold">{card}</span>
                      <span className={`font-sans tabular-nums text-[11px] px-2.5 py-0.5 rounded-full font-extrabold ${
                        isSelected
                          ? 'bg-white/20 text-white'
                          : isNegativeNet
                          ? 'bg-emerald-100 dark:bg-emerald-950/80 text-emerald-800 dark:text-emerald-300'
                          : 'bg-slate-100 dark:bg-gray-700 text-slate-700 dark:text-slate-300'
                      }`}>
                        {formatNumberByLang(total.toLocaleString(), lang)} {budgetConfig.currency}
                      </span>
                      {incomeTotal > 0 && expenseTotal > 0 && (
                        <span className={`text-[10px] font-mono opacity-80 ${isSelected ? 'text-indigo-100' : 'text-gray-400'}`}>
                          ({formatNumberByLang(`-${expenseTotal.toLocaleString()} / +${incomeTotal.toLocaleString()}`, lang)})
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Visual Graphs & Analysis Dashboard */}
          <div className="p-5 bg-white dark:bg-gray-900 rounded-3xl border border-gray-100 dark:border-gray-800 shadow-sm space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 pb-3 border-b border-gray-100 dark:border-gray-800">
              <div className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-indigo-600" />
                <div>
                  <h2 className="text-sm font-extrabold text-gray-900 dark:text-white">
                    {t('analyticsDashboardTitle', 'داشبورد تحلیلی و نمودارهای بصری مصرف')}
                  </h2>
                  <p className="text-[11px] text-gray-500">
                    {t('analyticsDashboardDesc', 'تحلیل دقیق محل خرج‌کرد (دسته‌بندی)، مقایسه روند ماهانه و رتبه‌بندی کالاها')}
                  </p>
                </div>
              </div>
              <span className="text-xs px-3 py-1 rounded-full bg-indigo-50 dark:bg-indigo-950 text-indigo-700 dark:text-indigo-300 font-sans tabular-nums font-bold">
                {t('totalExpenditure', 'مجموع هزینه‌کرد: {{amount}} {{currency}}', { amount: formatNumberByLang(visualReportsData.totalExpenseSum.toLocaleString(), lang), currency: budgetConfig.currency })}
              </span>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              
              {/* GRAPH 1: Category Distribution Bars */}
              <div className="p-4 bg-slate-50/60 dark:bg-gray-800/40 rounded-2xl border border-slate-200/80 dark:border-gray-700/60 space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-xs font-extrabold text-gray-800 dark:text-gray-200 flex items-center gap-1.5">
                    <PieChart className="w-4 h-4 text-indigo-600" />
                    <span>{t('graph1Title', '۱. سهم هزینه‌ها بر اساس دسته‌بندی (Where / Category):')}</span>
                  </h3>
                  <span className="text-[10px] text-gray-400">{t('graph1Subtitle', 'به ترتیب بیشترین درصد')}</span>
                </div>

                {visualReportsData.categoryBreakdown && visualReportsData.categoryBreakdown.length > 0 ? (
                  <div className="space-y-4">
                    {/* Arc Gauge Visual Graph */}
                    {(() => {
                      const arcColors = ['#6366f1', '#10b981', '#f59e0b', '#ec4899', '#06b6d4', '#8b5cf6', '#64748b'];
                      const CX = 120;
                      const CY = 105;
                      const R = 80;
                      const STROKE_WIDTH = 16;
                      const TOTAL_ARC = Math.PI;

                      let currentAngle = Math.PI; // 180 deg (left)
                      const topCategories = visualReportsData.categoryBreakdown.slice(0, 6);

                      const segments = topCategories
                        .map((cat, idx) => {
                          const pct = Math.max(0, Number(cat.percentage) || 0);
                          const angleSpan = (pct / 100) * TOTAL_ARC;
                          const startAngle = currentAngle;
                          const endAngle = Math.max(0, currentAngle - angleSpan);
                          currentAngle = endAngle;

                          const x1 = CX + R * Math.cos(startAngle);
                          const y1 = CY - R * Math.sin(startAngle);
                          const x2 = CX + R * Math.cos(endAngle);
                          const y2 = CY - R * Math.sin(endAngle);

                          const largeArcFlag = angleSpan > Math.PI ? 1 : 0;
                          const pathD = `M ${x1.toFixed(2)} ${y1.toFixed(2)} A ${R} ${R} 0 ${largeArcFlag} 1 ${x2.toFixed(2)} ${y2.toFixed(2)}`;

                          return {
                            pathD,
                            color: arcColors[idx % arcColors.length],
                            label: cat.label || 'نامشخص',
                            percentage: pct,
                            amount: cat.amount || 0,
                            angleSpan
                          };
                        })
                        .filter(s => s.angleSpan > 0.001); // Filter out zero-span arcs to prevent SVG path errors

                      const dominantCat = topCategories[0];

                      return (
                        <div className="flex flex-col items-center justify-center p-3 bg-white dark:bg-gray-800/80 rounded-2xl border border-gray-100 dark:border-gray-700/60 shadow-inner">
                          <div className="relative flex justify-center items-center">
                            <svg viewBox="0 0 240 120" className="w-56 h-28 drop-shadow-sm overflow-visible">
                              {/* Background arc track */}
                              <path
                                d={`M ${CX - R} ${CY} A ${R} ${R} 0 0 1 ${CX + R} ${CY}`}
                                fill="none"
                                stroke="currentColor"
                                strokeWidth={STROKE_WIDTH}
                                strokeLinecap="round"
                                className="text-gray-100 dark:text-gray-700"
                              />
                              {/* Segment Arcs */}
                              {segments.map((seg, i) => (
                                <path
                                  key={i}
                                  d={seg.pathD}
                                  fill="none"
                                  stroke={seg.color}
                                  strokeWidth={STROKE_WIDTH}
                                  strokeLinecap={i === 0 || i === segments.length - 1 ? "round" : "butt"}
                                  className="transition-all duration-500 hover:opacity-80"
                                >
                                  <title>{`${seg.label}: ${formatNumberByLang(seg.percentage, lang)}٪ (${formatNumberByLang((seg.amount || 0).toLocaleString(), lang)} ${budgetConfig.currency})`}</title>
                                </path>
                              ))}
                            </svg>
                            {/* Inner Gauge Text */}
                            <div className="absolute top-[52px] flex flex-col items-center text-center">
                              <span className="text-[10px] font-bold text-gray-400">{t('largestShare', 'بزرگ‌ترین سهم')}</span>
                              <span className="text-xs font-extrabold text-indigo-600 dark:text-indigo-400 font-sans">
                                {dominantCat ? `${dominantCat.label} (${formatNumberByLang(dominantCat.percentage || 0, lang)}٪)` : '-'}
                              </span>
                            </div>
                          </div>

                          {/* Gauge Color Legend Pills */}
                          <div className="flex items-center justify-center gap-1.5 flex-wrap mt-1 text-[10px]">
                            {topCategories.map((cat, idx) => {
                              const color = arcColors[idx % arcColors.length];
                              return (
                                <div key={cat.catId || idx} className="flex items-center gap-1 bg-slate-50 dark:bg-gray-900/60 px-2 py-0.5 rounded-md border border-slate-200/60 dark:border-gray-700/50">
                                  <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: color }} />
                                  <span className="font-bold text-gray-700 dark:text-gray-300">{cat.label || 'دسته‌بندی'}</span>
                                  <span className="font-mono text-gray-500">({formatNumberByLang(cat.percentage || 0, lang)}٪)</span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })()}

                    {/* Category Breakdown Progress Bars List */}
                    <div className="space-y-2.5">
                      {visualReportsData.categoryBreakdown.slice(0, 6).map((cat, idx) => {
                        const IconComponent = (cat.IconComp && (typeof cat.IconComp === 'function' || typeof cat.IconComp === 'object')) ? cat.IconComp : Tag;
                        const arcColors = ['#6366f1', '#10b981', '#f59e0b', '#ec4899', '#06b6d4', '#8b5cf6', '#64748b'];
                        const itemColor = arcColors[idx % arcColors.length];
                        return (
                          <div key={cat.catId || idx} className="space-y-1">
                            <div className="flex items-center justify-between text-xs">
                              <div className="flex items-center gap-2">
                                <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: itemColor }} />
                                <IconComponent className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                                <span className="font-bold text-gray-800 dark:text-gray-200">{cat.label}</span>
                              </div>
                              <div className="flex items-center gap-2 font-sans tabular-nums">
                                <span className="font-bold text-gray-900 dark:text-white text-xs">
                                  {formatNumberByLang((cat.amount || 0).toLocaleString(), lang)} {budgetConfig.currency}
                                </span>
                                <span className="px-2 py-0.5 rounded-md bg-indigo-100 dark:bg-indigo-950 text-indigo-800 dark:text-indigo-300 text-[11px] font-extrabold">
                                  {formatNumberByLang(cat.percentage || 0, lang)}٪
                                </span>
                              </div>
                            </div>

                            <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                              <div
                                className="h-full rounded-full transition-all duration-500"
                                style={{
                                  width: `${Math.min(100, Math.max(0, cat.percentage || 0))}%`,
                                  backgroundColor: itemColor
                                }}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <p className="text-xs text-gray-400 py-6 text-center">{t('noCategoryData', 'تراکنشی جهت نمایش نمودار دسته‌بندی ثبت نشده است.')}</p>
                )}
              </div>

              {/* GRAPH 2: Monthly Spending & Savings Comparison */}
              <div className="p-4 bg-slate-50/60 dark:bg-gray-800/40 rounded-2xl border border-slate-200/80 dark:border-gray-700/60 space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-xs font-extrabold text-gray-800 dark:text-gray-200 flex items-center gap-1.5">
                    <TrendingUp className="w-4 h-4 text-emerald-600" />
                    <span>{t('graph2Title', '۲. مقایسه ماهانه هزینه‌ها و پس‌انداز در طول سال (Monthly Comparison):')}</span>
                  </h3>
                  <span className="text-[10px] text-gray-400">{t('graph2Subtitle', 'به تفکیک ماه')}</span>
                </div>

                {visualReportsData.monthlyEntries && visualReportsData.monthlyEntries.length > 0 ? (
                  <div className="space-y-3">
                    {/* Key Highlights Cards */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-[11px]">
                      {visualReportsData.maxExpenseMonth && (
                        <div className="p-2.5 bg-rose-50/80 dark:bg-rose-950/40 rounded-xl border border-rose-200/60 dark:border-rose-900/40">
                          <span className="text-[10px] text-rose-700 dark:text-rose-300 font-bold block">{t('mostExpensiveMonth', 'پرمصرف‌ترین ماه سال:')}</span>
                          <span className="font-extrabold text-rose-900 dark:text-rose-100 font-sans tabular-nums">
                            {visualReportsData.maxExpenseMonth.yearMonth} ({formatNumberByLang(visualReportsData.maxExpenseMonth.expense.toLocaleString(), lang)} {budgetConfig.currency})
                          </span>
                        </div>
                      )}
                      {visualReportsData.maxSavingsMonth && (
                        <div className="p-2.5 bg-emerald-50/80 dark:bg-emerald-950/40 rounded-xl border border-emerald-200/60 dark:border-emerald-900/40">
                          <span className="text-[10px] text-emerald-700 dark:text-emerald-300 font-bold block">{t('highestSavingsMonth', 'بیشترین پس‌انداز ماهانه:')}</span>
                          <span className="font-extrabold text-emerald-900 dark:text-emerald-100 font-sans tabular-nums">
                            {visualReportsData.maxSavingsMonth.yearMonth} ({formatNumberByLang(visualReportsData.maxSavingsMonth.savings.toLocaleString(), lang)} {budgetConfig.currency})
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Monthly Comparison Progress List */}
                    <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
                      {visualReportsData.monthlyEntries.map((m) => {
                        const expenseBarPct = Math.min(100, Math.round((m.expense / visualReportsData.maxMonthValue) * 100));

                        return (
                          <div
                            key={m.yearMonth}
                            onClick={() => handleFilterMonthAndScroll(m.yearMonth)}
                            className="p-3 bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 hover:border-emerald-500 dark:hover:border-emerald-500 cursor-pointer transition-all hover:shadow-md space-y-1.5 group"
                          >
                            <div className="flex items-center justify-between text-xs">
                              <div className="flex items-center gap-1.5 font-bold text-gray-900 dark:text-white font-sans tabular-nums">
                                <span>{m.yearMonth}</span>
                                <span className="text-[10px] text-emerald-600 dark:text-emerald-400 group-hover:underline font-sans flex items-center gap-0.5">
                                  <span>{t('filterThisMonthTransactions', 'فیلتر تراکنش‌های این ماه در جدول پایین')}</span>
                                  <ArrowDownRight className="w-3.5 h-3.5" />
                                </span>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="text-rose-600 dark:text-rose-400 font-sans tabular-nums font-bold">
                                  {t('monthExpenseLabel', 'هزینه: {{amount}}', { amount: formatNumberByLang(m.expense.toLocaleString(), lang) })}
                                </span>
                                <span className={`font-sans tabular-nums font-extrabold px-2 py-0.5 rounded-lg text-xs ${m.savings >= 0 ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300' : 'bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-300'}`}>
                                  {t('monthSavingsLabel', 'پس‌انداز: {{amount}} {{currency}}', { amount: formatNumberByLang(m.savings.toLocaleString(), lang), currency: budgetConfig.currency })}
                                </span>
                              </div>
                            </div>
                            <div className="w-full h-2 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden flex">
                              <div
                                className="h-full bg-rose-500 rounded-full transition-all duration-300"
                                style={{ width: `${expenseBarPct}%` }}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <p className="text-xs text-gray-400 py-6 text-center">{t('noMonthlyData', 'داده ماهانه‌ای جهت مقایسه یافت نشد.')}</p>
                )}
              </div>

            </div>

            {/* GRAPH 3: Products Ranking ("Which product most or less") */}
            <div className="p-4 bg-slate-50/60 dark:bg-gray-800/40 rounded-2xl border border-slate-200/80 dark:border-gray-700/60 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-xs font-extrabold text-gray-800 dark:text-gray-200 flex items-center gap-1.5">
                  <ShoppingBag className="w-4 h-4 text-amber-600" />
                  <span>{t('rankingProductsTitle', '۳. رتبه‌بندی کالاها و ریزاقلام فاکتورها (Which product most or less):')}</span>
                </h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
                {/* Top Spent Products */}
                <div className="p-3 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 space-y-2">
                  <h4 className="font-extrabold text-rose-700 dark:text-rose-400 flex items-center gap-1 text-[11px]">
                    <TrendingUp className="w-3.5 h-3.5" />
                    <span>{t('highestSpentProducts', 'گران‌ترین کالاها (Highest Spent):')}</span>
                  </h4>
                  {visualReportsData.top5SpentProducts.length > 0 ? (
                    <div className="space-y-1.5">
                      {visualReportsData.top5SpentProducts.map((p, idx) => (
                        <div key={idx} className="flex items-center justify-between text-xs">
                          <span className="truncate font-bold text-gray-800 dark:text-gray-200">{idx + 1}. {p.name}</span>
                          <span className="font-sans tabular-nums text-rose-600 dark:text-rose-400 font-extrabold shrink-0">
                            {formatNumberByLang(p.totalSpent.toLocaleString(), lang)} {budgetConfig.currency}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-[10px] text-gray-400">{t('noProductFound', 'کالایی یافت نشد.')}</p>
                  )}
                </div>

                {/* Top Qty Products */}
                <div className="p-3 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 space-y-2">
                  <h4 className="font-extrabold text-indigo-700 dark:text-indigo-400 flex items-center gap-1 text-[11px]">
                    <ShoppingBag className="w-3.5 h-3.5" />
                    <span>{t('highestQtyProducts', 'پرتعدادترین کالاها (Highest Quantity):')}</span>
                  </h4>
                  {visualReportsData.top5QtyProducts.length > 0 ? (
                    <div className="space-y-1.5">
                      {visualReportsData.top5QtyProducts.map((p, idx) => (
                        <div key={idx} className="flex items-center justify-between text-xs">
                          <span className="truncate font-bold text-gray-800 dark:text-gray-200">{idx + 1}. {p.name}</span>
                          <span className="font-sans tabular-nums text-indigo-600 dark:text-indigo-400 font-extrabold shrink-0">
                            {formatNumberByLang(p.totalQty, lang)} {t('itemUnit', 'عدد')}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-[10px] text-gray-400">{t('noProductFound', 'کالایی یافت نشد.')}</p>
                  )}
                </div>

                {/* Least Spent / Budget Products */}
                <div className="p-3 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 space-y-2">
                  <h4 className="font-extrabold text-emerald-700 dark:text-emerald-400 flex items-center gap-1 text-[11px]">
                    <TrendingDown className="w-3.5 h-3.5" />
                    <span>{t('lowestSpentProducts', 'ارزان‌ترین/کم‌هزینه‌ترین کالاها (Lowest Spent):')}</span>
                  </h4>
                  {visualReportsData.least5SpentProducts.length > 0 ? (
                    <div className="space-y-1.5">
                      {visualReportsData.least5SpentProducts.map((p, idx) => (
                        <div key={idx} className="flex items-center justify-between text-xs">
                          <span className="truncate font-bold text-gray-800 dark:text-gray-200">{idx + 1}. {p.name}</span>
                          <span className="font-sans tabular-nums text-emerald-600 dark:text-emerald-400 font-extrabold shrink-0">
                            {formatNumberByLang(p.totalSpent.toLocaleString(), lang)} {budgetConfig.currency}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-[10px] text-gray-400">{t('noProductFound', 'کالایی یافت نشد.')}</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Misclassified Salary / Income Auto-Fix Notification Banner */}
          {misclassifiedSalaryTxs.length > 0 && (
            <div className="p-4 bg-amber-50 dark:bg-amber-950/60 border border-amber-300 dark:border-amber-800 rounded-3xl flex items-center justify-between flex-wrap gap-3 text-xs shadow-sm animate-fadeIn">
              <div className="flex items-center gap-2.5 text-amber-900 dark:text-amber-200 min-w-0">
                <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" />
                <div className="min-w-0">
                  <strong className="font-extrabold block text-amber-900 dark:text-amber-100">
                    {t('salaryMisclassifiedTitle', 'تشخیص {{count}} تراکنش حقوق/واریزی (Lön)', { count: formatNumberByLang(misclassifiedSalaryTxs.length, lang) })}
                  </strong>
                  <span className="text-[11px] text-amber-800 dark:text-amber-300 font-medium block">
                    {t('salaryMisclassifiedDesc', 'این تراکنش‌ها عنوان «حقوق/Lön/واریزی» دارند اما به عنوان هزینه ثبت شده‌اند. با یک کلیک نوع آنها به درآمد (+) اصلاح می‌شود.')}
                  </span>
                </div>
              </div>
              <button
                onClick={handleFixSalaryTransactionTypes}
                className="px-3.5 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-xl font-bold text-xs shadow-xs transition-all cursor-pointer flex items-center gap-1.5 shrink-0"
              >
                <CheckCircle2 className="w-4 h-4" />
                <span>{t('fixSalaryBtn', 'اصلاح خودکار به درآمد (+)')}</span>
              </button>
            </div>
          )}

          {/* Misclassified Transfer / Credit Card Payoff Auto-Fix Notification Banner */}
          {misclassifiedTransferTxs.length > 0 && (
            <div className="p-4 bg-sky-50 dark:bg-sky-950/60 border border-sky-300 dark:border-sky-800 rounded-3xl flex items-center justify-between flex-wrap gap-3 text-xs shadow-sm animate-fadeIn">
              <div className="flex items-center gap-2.5 text-sky-900 dark:text-sky-200 min-w-0">
                <CreditCard className="w-5 h-5 text-sky-600 shrink-0" />
                <div className="min-w-0">
                  <strong className="font-extrabold block text-sky-900 dark:text-sky-100">
                    {t('transferMisclassifiedTitle', 'تشخیص {{count}} تراکنش تسویه کارت اعتباری / انتقال بین‌حسابی', { count: formatNumberByLang(misclassifiedTransferTxs.length, lang) })}
                  </strong>
                  <span className="text-[11px] text-sky-800 dark:text-sky-300 font-medium block">
                    {t('transferMisclassifiedDesc', 'این تراکنش‌ها تسویه‌حساب کارت یا انتقال بین‌حسابی هستند اما به عنوان هزینه مصرفی ثبت شده‌اند. با یک کلیک نوع آنها به «انتقال/پس‌انداز» تغییر می‌یابد.')}
                  </span>
                </div>
              </div>
              <button
                onClick={handleFixTransferTransactionTypes}
                className="px-3.5 py-2 bg-sky-600 hover:bg-sky-700 text-white rounded-xl font-bold text-xs shadow-xs transition-all cursor-pointer flex items-center gap-1.5 shrink-0"
              >
                <CheckCircle2 className="w-4 h-4" />
                <span>{t('fixTransferBtn', 'اصلاح خودکار به پس‌انداز/انتقال')}</span>
              </button>
            </div>
          )}

          {/* Transaction List */}
          <div id="transactions-table-section" className="p-4 bg-white dark:bg-gray-900 rounded-3xl border border-gray-100 dark:border-gray-800 shadow-sm space-y-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <Receipt className="w-5 h-5 text-indigo-600" />
                <h2 className="text-sm font-bold text-gray-900 dark:text-white">
                  {t('transactionListTitle', 'فهرست تراکنش‌ها و فاکتورها ({{count}})', { count: formatNumberByLang(filteredTransactions.length, lang) })}
                </h2>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowBulkDeleteModal(true)}
                  className="px-3 py-1.5 bg-rose-50 hover:bg-rose-100 dark:bg-rose-950/60 dark:hover:bg-rose-900/80 text-rose-700 dark:text-rose-300 border border-rose-200 dark:border-rose-800 rounded-xl text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5 shadow-2xs"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  <span>{t('bulkDeleteBtn', 'حذف دسته‌ای / پاکسازی داده‌ها')}</span>
                </button>
                <span className="text-[10px] text-gray-400 hidden sm:inline">{t('cloudSyncActive', 'همگام‌سازی ابری فعال')}</span>
              </div>
            </div>

            {filteredTransactions.length === 0 ? (
              <div className="py-12 text-center text-gray-400 space-y-2">
                <FileText className="w-10 h-10 mx-auto opacity-30" />
                <p className="text-xs">{t('noTransactionsFound', 'هیچ فاکتور یا تراکنشی یافت نشد.')}</p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredTransactions.map((tx) => {
                  const catMeta = getCategoryMeta(tx.category);
                  const payMeta = getPaymentMeta(tx.paymentMethod);
                  const isExpanded = expandedTxId === tx.id;
                  const CatIcon = catMeta.icon;

                  return (
                    <div
                      key={tx.id}
                      className="p-3.5 sm:p-5 bg-white dark:bg-gray-800/90 rounded-2xl sm:rounded-3xl border border-gray-200/90 dark:border-gray-700/90 shadow-xs space-y-3 transition-all hover:shadow-md hover:border-indigo-300 dark:hover:border-indigo-700"
                    >
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        {/* Left Side: Category Icon, Title, Badges & Date */}
                        <div className="flex items-start gap-3 min-w-0 flex-1">
                          <div className={`p-3 rounded-2xl shrink-0 ${
                            tx.type === 'income' ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/80 dark:text-emerald-300' : 'bg-rose-100 text-rose-800 dark:bg-rose-950/80 dark:text-rose-300'
                          }`}>
                            <CatIcon className="w-6 h-6" />
                          </div>

                          <div className="space-y-1.5 min-w-0 flex-1">
                            {/* Title & Amount (Mobile top row) */}
                            <div className="flex items-start justify-between gap-2">
                              <h3 className="text-sm sm:text-base font-extrabold text-gray-900 dark:text-white leading-snug break-words">
                                {tx.title}
                              </h3>

                              {/* Amount on Mobile View */}
                              <span className={`sm:hidden text-sm sm:text-base font-extrabold font-sans tabular-nums shrink-0 ${
                                tx.type === 'income' ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'
                              }`}>
                                {tx.type === 'income' ? '+' : '-'}{formatNumberByLang(tx.amount.toLocaleString(), lang)} {tx.currency}
                              </span>
                            </div>

                            {/* Category & Payment Badges */}
                            <div className="flex flex-wrap items-center gap-1.5">
                              <span className={`text-xs px-2.5 py-0.5 rounded-full border font-bold ${catMeta.color}`}>
                                {catMeta.name}
                              </span>
                              <span className={`text-xs px-2.5 py-0.5 rounded-full font-bold ${payMeta.badge}`}>
                                {payMeta.name}
                              </span>
                              {tx.cardAccount && (
                                <span className="text-xs px-2.5 py-0.5 rounded-full font-extrabold bg-slate-100 text-slate-800 dark:bg-slate-700 dark:text-slate-100 border border-slate-200 dark:border-slate-600 flex items-center gap-1">
                                  <CreditCard className="w-3.5 h-3.5 text-slate-500" />
                                  <span>{tx.cardAccount}</span>
                                </span>
                              )}
                            </div>

                            {/* Date & Notes - High Contrast, Large & Legible */}
                            <div className="flex flex-wrap items-center gap-2 pt-1 text-xs text-gray-700 dark:text-gray-300">
                              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-xl bg-gray-100 dark:bg-gray-700/80 text-gray-900 dark:text-gray-100 font-sans tabular-nums font-bold border border-gray-200/60 dark:border-gray-600/60">
                                <Calendar className="w-3.5 h-3.5 text-indigo-600 dark:text-indigo-400" />
                                <span>{formatNumberByLang(tx.date, lang)}</span>
                              </span>
                              {tx.notes && (
                                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-xl bg-indigo-50 dark:bg-indigo-950/80 text-indigo-900 dark:text-indigo-200 font-bold border border-indigo-200/80 dark:border-indigo-800/80">
                                  <Tag className="w-3.5 h-3.5 text-indigo-600 dark:text-indigo-400 shrink-0" />
                                  <span className="break-words">{tx.notes}</span>
                                </span>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Right Side: Desktop Amount, Itemized Accordion Toggle & Delete Action */}
                        <div className="flex items-center justify-between sm:justify-end gap-3 pt-2 sm:pt-0 border-t sm:border-t-0 border-gray-100 dark:border-gray-700 shrink-0">
                          <div className="text-right sm:text-left">
                            {/* Desktop Amount */}
                            <span className={`hidden sm:block text-base sm:text-lg font-extrabold font-sans tabular-nums ${
                              tx.type === 'income' ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'
                            }`}>
                              {tx.type === 'income' ? '+' : '-'}{formatNumberByLang(tx.amount.toLocaleString(), lang)} {tx.currency}
                            </span>

                            {tx.items && tx.items.length > 0 && (
                              <button
                                onClick={() => setExpandedTxId(isExpanded ? null : tx.id)}
                                className="mt-1 px-3 py-1 bg-indigo-50 hover:bg-indigo-100 dark:bg-indigo-950/80 dark:hover:bg-indigo-900 text-indigo-700 dark:text-indigo-300 font-bold rounded-xl text-xs flex items-center gap-1.5 transition-colors cursor-pointer border border-indigo-200/60 dark:border-indigo-800/60"
                              >
                                {isExpanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                                <span>{formatNumberByLang(tx.items.length, lang)} {t('itemsDetails', 'اقلام جزئیات')}</span>
                              </button>
                            )}
                          </div>

                          <div className="flex items-center gap-1">
                            {/* Edit Transaction Button */}
                            <button
                              onClick={() => handleOpenEditTransaction(tx)}
                              className="p-2 text-gray-400 hover:text-indigo-600 rounded-xl hover:bg-indigo-50 dark:hover:bg-indigo-950/50 transition-colors cursor-pointer border border-transparent hover:border-indigo-200 dark:hover:border-indigo-800"
                              title={t('editTransaction', 'ویرایش تراکنش، فاکتور و پیوست‌ها')}
                            >
                              <Edit3 className="w-4 h-4 sm:w-5 sm:h-5 text-indigo-600 dark:text-indigo-400" />
                            </button>

                            {/* Delete Transaction Button */}
                            <button
                              onClick={() => deleteTransaction(tx.id)}
                              className="p-2 text-gray-400 hover:text-rose-600 rounded-xl hover:bg-rose-50 dark:hover:bg-rose-950/50 transition-colors cursor-pointer border border-transparent hover:border-rose-200 dark:hover:border-rose-800"
                              title={t('deleteTransaction', 'حذف تراکنش')}
                            >
                              <Trash2 className="w-4 h-4 sm:w-5 sm:h-5" />
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Attached Invoices / Images / Documents / Media */}
                      {tx.attachments && tx.attachments.length > 0 && (
                        <div className="mt-2.5 pt-2.5 border-t border-gray-100 dark:border-gray-700/60 space-y-2">
                          <div className="flex items-center justify-between">
                            <span className="text-[11px] font-extrabold text-indigo-900 dark:text-indigo-300 flex items-center gap-1.5">
                              <Paperclip className="w-3.5 h-3.5 text-indigo-600 dark:text-indigo-400" />
                              <span>{t('attachedFilesAndMedia', 'فایل‌ها و رسانه‌های ضمیمه:')}</span>
                            </span>
                            <span className="text-[10px] text-gray-400 font-sans tabular-nums font-bold">
                              {formatNumberByLang(tx.attachments.length, lang)} {t('attachmentsCount', 'پیوست ابری')}
                            </span>
                          </div>

                          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                            {tx.attachments.map((att, idx) => {
                              const isImg = att.type === 'image' || (att.fileUrl && att.fileUrl.startsWith('data:image/'));
                              const isVid = att.type === 'video';
                              const isPdf = att.type === 'pdf';

                              return (
                                <div
                                  key={att.id || idx}
                                  className="p-2 bg-slate-50 dark:bg-gray-800/90 rounded-xl border border-slate-200/80 dark:border-gray-700/80 flex items-center justify-between gap-2 shadow-2xs hover:border-indigo-300 dark:hover:border-indigo-700 transition-all"
                                >
                                  <div
                                    onClick={() => setPreviewAttachmentModal(att)}
                                    className="flex items-center gap-2 min-w-0 flex-1 cursor-pointer group"
                                  >
                                    {isImg && att.fileUrl ? (
                                      <div className="w-10 h-10 rounded-lg overflow-hidden shrink-0 border border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-900 flex items-center justify-center">
                                        <img
                                          src={att.fileUrl}
                                          alt={att.name}
                                          className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                                          referrerPolicy="no-referrer"
                                        />
                                      </div>
                                    ) : isVid ? (
                                      <div className="w-10 h-10 rounded-lg bg-red-100 dark:bg-red-950/60 text-red-600 flex items-center justify-center shrink-0">
                                        <Film className="w-5 h-5" />
                                      </div>
                                    ) : isPdf ? (
                                      <div className="w-10 h-10 rounded-lg bg-rose-100 dark:bg-rose-950/60 text-rose-600 flex items-center justify-center shrink-0">
                                        <FileText className="w-5 h-5" />
                                      </div>
                                    ) : (
                                      <div className="w-10 h-10 rounded-lg bg-indigo-100 dark:bg-indigo-950/60 text-indigo-600 flex items-center justify-center shrink-0">
                                        <Paperclip className="w-5 h-5" />
                                      </div>
                                    )}

                                    <div className="min-w-0 flex-1">
                                      <span className="text-xs font-bold text-gray-800 dark:text-gray-200 truncate block group-hover:text-indigo-600 dark:group-hover:text-indigo-400">
                                        {att.name}
                                      </span>
                                      <span className="text-[10px] text-gray-400 font-mono block">
                                        {att.fileSize || (isVid ? 'ویدیو' : 'فایل ضمیمه')}
                                      </span>
                                    </div>
                                  </div>

                                  <div className="flex items-center gap-1 shrink-0">
                                    <button
                                      type="button"
                                      onClick={() => setPreviewAttachmentModal(att)}
                                      className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-white dark:hover:bg-gray-700 rounded-lg transition-colors cursor-pointer"
                                      title={t('previewAttachment', 'مشاهده پیوست')}
                                    >
                                      <Eye className="w-3.5 h-3.5" />
                                    </button>
                                    {att.fileUrl && !isVid && (
                                      <button
                                        type="button"
                                        onClick={() => triggerFileDownload(att.fileUrl!, att.name)}
                                        className="p-1.5 text-gray-400 hover:text-emerald-600 hover:bg-white dark:hover:bg-gray-700 rounded-lg transition-colors cursor-pointer"
                                        title={t('downloadFile', 'دانلود فایل')}
                                      >
                                        <Download className="w-3.5 h-3.5" />
                                      </button>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Invoice Itemized Goods Accordion */}
                      {isExpanded && tx.items && tx.items.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700 space-y-2 bg-gray-50/90 dark:bg-gray-900/90 p-3.5 rounded-2xl">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs font-extrabold text-gray-800 dark:text-gray-200 flex items-center gap-1.5">
                              <ShoppingBag className="w-4 h-4 text-indigo-500" />
                              <span>{t('invoiceItemsTitle', 'اقلام ریز و جزئیات فاکتور:')}</span>
                            </span>
                            <span className="text-xs text-gray-500 dark:text-gray-400 font-bold">
                              {t('totalItemsCount', 'مجموع {{count}} کالا', { count: formatNumberByLang(tx.items.length, lang) })}
                            </span>
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                            {tx.items.map((item, idx) => (
                              <div key={idx} className="p-2.5 bg-white dark:bg-gray-800 rounded-xl text-xs border border-gray-200 dark:border-gray-700 flex justify-between items-center shadow-xs">
                                <div className="space-y-0.5">
                                  <span className="font-extrabold text-gray-900 dark:text-white block">{item.name}</span>
                                  <span className="text-[10px] font-medium text-gray-500 dark:text-gray-400">{item.category}</span>
                                </div>
                                <div className="text-left font-sans tabular-nums space-y-0.5">
                                  <span className="text-gray-500 dark:text-gray-400 block text-xs font-bold">{formatNumberByLang(item.quantity, lang)} {t('itemUnit', 'عدد')}</span>
                                  <span className="font-extrabold text-emerald-600 dark:text-emerald-400 block text-xs">{formatNumberByLang(item.totalPrice, lang)} {tx.currency}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* TAB 2: SMART BUDGETING & NET INCOME ALLOCATION */}
      {activeTab === 'budget' && (
        <div className="space-y-6">
          
          {/* Form Card for Net Income & Allocation Ratios */}
          <div className="p-6 bg-white dark:bg-gray-900 rounded-3xl border border-gray-100 dark:border-gray-800 shadow-sm space-y-5">
            <div className="flex items-center justify-between border-b border-gray-100 dark:border-gray-800 pb-4">
              <div className="flex items-center gap-2.5">
                <div className="p-2.5 bg-emerald-100 dark:bg-emerald-950 text-emerald-600 rounded-2xl">
                  <Calculator className="w-5 h-5" />
                </div>
                <div>
                  <h2 className="text-base font-extrabold text-gray-900 dark:text-white">
                    {t('budgetSettingsTitle', 'تنظیم درآمد خالص پس از مالیات و بودجه‌بندی')}
                  </h2>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {t('budgetSettingsDesc', 'تعیین حقوق خالص، سقف مجاز هزینه‌ها، ارز پایه سیستم و بودجه‌بندی دسته‌بندی‌ها')}
                  </p>
                </div>
              </div>
              {bgSaveSuccess && (
                <span className="text-xs bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300 px-3 py-1 rounded-full font-bold animate-pulse">
                  {t('settingsSaveSuccess', '✓ تنظیمات با موفقیت ذخیره شد')}
                </span>
              )}
            </div>

            <form onSubmit={handleSaveBudgetSettings} className="space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-gray-700 dark:text-gray-300">
                    {t('netMonthlyIncomeLabel', 'درآمد خالص ماهانه پس از مالیات (Net Income):')}
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      required
                      step="any"
                      placeholder={t('incomePlaceholder', 'مثلاً ۵۰۰,۰۰۰,۰۰۰')}
                      value={bgIncome}
                      onChange={(e) => setBgIncome(e.target.value)}
                      className="w-full p-3 bg-gray-50 dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 font-mono text-sm text-gray-900 dark:text-white"
                    />
                    <select
                      value={bgCurrency}
                      onChange={(e) => handleBaseCurrencyChange(e.target.value)}
                      className="p-3 bg-gray-50 dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 font-bold text-xs text-gray-900 dark:text-white"
                    >
                      {CURRENCY_OPTIONS.map(c => (
                        <option key={c.code} value={c.code}>{t(c.nameKey, c.defaultLabel)}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <div className="flex justify-between items-center text-xs font-bold">
                    <span className="text-gray-700 dark:text-gray-300">{t('budgetAllocationPercent', 'درصد تخصیص کلی بودجه:')}</span>
                    <span className="text-emerald-600 font-sans tabular-nums font-bold">
                      {t('budgetAllocationSummary', 'هزینه ({{expense}}%) + پس‌انداز ({{saving}}%) = ۱۰۰%', { expense: formatNumberByLang(bgExpenseRatio, lang), saving: formatNumberByLang(bgSavingRatio, lang) })}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-3 pt-1">
                    <div className="p-2.5 bg-rose-50 dark:bg-rose-950/30 rounded-2xl border border-rose-100 dark:border-rose-900/40">
                      <label className="text-[10px] font-bold text-rose-700 dark:text-rose-300 block">
                        {t('currentExpensesShareLabel', 'سهم هزینه‌های جاری:')}
                      </label>
                      <input
                        type="range"
                        min="10"
                        max="90"
                        value={bgExpenseRatio}
                        onChange={(e) => {
                          const val = parseInt(e.target.value) || 50;
                          setBgExpenseRatio(val);
                          setBgSavingRatio(100 - val);
                        }}
                        className="w-full accent-rose-600 cursor-pointer my-1"
                      />
                      <span className="text-xs font-sans tabular-nums font-bold text-rose-800 dark:text-rose-200">
                        {formatNumberByLang(bgExpenseRatio, lang)}% {t('ofIncome', 'از درآمد')}
                      </span>
                    </div>

                    <div className="p-2.5 bg-emerald-50 dark:bg-emerald-950/30 rounded-2xl border border-emerald-100 dark:border-emerald-900/40">
                      <label className="text-[10px] font-bold text-emerald-700 dark:text-emerald-300 block">
                        {t('monthlySavingsShareLabel', 'سهم پس‌انداز ماهانه:')}
                      </label>
                      <input
                        type="range"
                        min="10"
                        max="90"
                        value={bgSavingRatio}
                        onChange={(e) => {
                          const val = parseInt(e.target.value) || 50;
                          setBgSavingRatio(val);
                          setBgExpenseRatio(100 - val);
                        }}
                        className="w-full accent-emerald-600 cursor-pointer my-1"
                      />
                      <span className="text-xs font-sans tabular-nums font-bold text-emerald-800 dark:text-emerald-200">
                        {formatNumberByLang(bgSavingRatio, lang)}% {t('ofIncome', 'از درآمد')}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Calculated Breakdown Badges */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
                <div className="p-4 bg-rose-50/60 dark:bg-rose-950/20 rounded-2xl border border-rose-100 dark:border-rose-900/30 flex items-center justify-between">
                  <div>
                    <span className="text-[11px] text-gray-500 block">{t('monthlyExpenseCapLabel', 'سقف مجاز کل هزینه‌های ماهانه ({{ratio}}%):', { ratio: formatNumberByLang(bgExpenseRatio, lang) })}</span>
                    <span className="text-lg font-extrabold text-rose-600 dark:text-rose-400 font-sans tabular-nums">
                      {formatNumberByLang(Math.round((parseFloat(bgIncome) || 0) * (bgExpenseRatio / 100)).toLocaleString(), lang)} {bgCurrency}
                    </span>
                  </div>
                  <ArrowUpRight className="w-6 h-6 text-rose-500 opacity-60" />
                </div>

                <div className="p-4 bg-emerald-50/60 dark:bg-emerald-950/20 rounded-2xl border border-emerald-100 dark:border-emerald-900/30 flex items-center justify-between">
                  <div>
                    <span className="text-[11px] text-gray-500 block">{t('savingsAndTargetShare', 'سهم پس‌انداز و هدف‌گذاری ({{ratio}}%):', { ratio: formatNumberByLang(bgSavingRatio, lang) })}</span>
                    <span className="text-lg font-extrabold text-emerald-600 dark:text-emerald-400 font-sans tabular-nums">
                      {formatNumberByLang(Math.round((parseFloat(bgIncome) || 0) * (bgSavingRatio / 100)).toLocaleString(), lang)} {bgCurrency}
                    </span>
                  </div>
                  <PiggyBank className="w-6 h-6 text-emerald-500 opacity-60" />
                </div>
              </div>

              {/* Category Budget Caps Section */}
              <div className="pt-4 border-t border-gray-100 dark:border-gray-800 space-y-3">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                  <div>
                    <h3 className="text-xs font-bold text-gray-800 dark:text-gray-200 flex items-center gap-1.5">
                      <Sliders className="w-4 h-4 text-emerald-600" />
                      <span>{t('categoryCapsLabel', 'سقف بودجه مجاز دسته‌بندی‌های غنی:')}</span>
                    </h3>
                    <p className="text-[11px] text-gray-500">
                      {t('defineExpenseCapsDesc', 'تعیین سقف مجاز هزینه برای مسکن، سوپرمارکت، قبوض، حمل‌ونقل و... (امکان درج ۰ برای بخش‌های بدون هزینه)')}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={handleAutoAllocateCategoryCaps}
                    className="px-3 py-1.5 bg-emerald-50 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300 hover:bg-emerald-100 rounded-xl text-[11px] font-bold transition-all border border-emerald-200 dark:border-emerald-800 cursor-pointer flex items-center gap-1 shrink-0"
                  >
                    <Sparkles className="w-3.5 h-3.5 text-emerald-600" />
                    <span>{t('autoAllocateCaps', 'تخمین خودکار سقف دسته‌بندی‌ها ({{pct}}٪)', { pct: formatNumberByLang(bgExpenseRatio, lang) })}</span>
                  </button>
                </div>

                {/* Real-time Category Allocation Status Tracker */}
                {(() => {
                  const calcIncNum = parseFloat(bgIncome) || 0;
                  const calcTotalExpenseCap = Math.round(calcIncNum * (bgExpenseRatio / 100));
                  const totalAllocatedCategoryCaps = Object.values(bgCategoryCaps).reduce((sum, valStr) => {
                    const v = parseFloat(valStr);
                    return sum + (!isNaN(v) && v > 0 ? v : 0);
                  }, 0);
                  const unallocatedExpenseBudget = calcTotalExpenseCap - totalAllocatedCategoryCaps;

                  return (
                    <div className="p-3.5 bg-gray-50 dark:bg-gray-800/90 rounded-2xl border border-gray-200 dark:border-gray-700 space-y-2">
                      <div className="flex flex-wrap items-center justify-between gap-2 text-xs font-bold">
                        <span className="text-gray-700 dark:text-gray-300">
                          {t('totalExpenseCap', 'سقف کل هزینه‌ها:')} <span className="font-sans tabular-nums text-rose-600 font-extrabold">{formatNumberByLang(calcTotalExpenseCap.toLocaleString(), lang)} {bgCurrency}</span>
                        </span>
                        <span className="text-gray-700 dark:text-gray-300">
                          {t('totalAllocatedBudget', 'مجموع بودجه تخصیص‌یافته:')} <span className="font-sans tabular-nums text-indigo-600 font-extrabold">{formatNumberByLang(totalAllocatedCategoryCaps.toLocaleString(), lang)} {bgCurrency}</span>
                        </span>
                      </div>

                      {calcIncNum > 0 ? (
                        unallocatedExpenseBudget >= 0 ? (
                          <div className="p-2.5 bg-emerald-100/70 dark:bg-emerald-950/50 rounded-xl text-emerald-800 dark:text-emerald-300 text-xs font-bold flex items-center justify-between gap-2 border border-emerald-200 dark:border-emerald-800">
                            <div className="flex items-center gap-1.5">
                              <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                              <span>{t('unallocatedBalanceLabel', '✓ مانده تخصیص‌نیافته (قابل توزیع یا ذخیره):')}</span>
                            </div>
                            <span className="font-sans tabular-nums text-sm font-extrabold text-emerald-700 dark:text-emerald-300">
                              {formatNumberByLang(unallocatedExpenseBudget.toLocaleString(), lang)} {bgCurrency}
                            </span>
                          </div>
                        ) : (
                          <div className="p-2.5 bg-rose-100/80 dark:bg-rose-950/60 rounded-xl text-rose-800 dark:text-rose-300 text-xs font-bold flex items-center justify-between gap-2 border border-rose-200 dark:border-rose-800">
                            <div className="flex items-center gap-1.5">
                              <AlertTriangle className="w-4 h-4 text-rose-600 shrink-0" />
                              <span>⚠️ {t('allocationSurplusOverCap', 'تخصیص مازاد بر سقف کل هزینه‌ها:')}</span>
                            </div>
                            <span className="font-sans tabular-nums text-sm font-extrabold text-rose-700 dark:text-rose-300">
                              {formatNumberByLang(Math.abs(unallocatedExpenseBudget).toLocaleString(), lang)} {bgCurrency} {t('surplusExceeded', 'مازاد!')}
                            </span>
                          </div>
                        )
                      ) : (
                        <div className="p-2 bg-amber-50 dark:bg-amber-950/30 rounded-xl text-amber-800 dark:text-amber-300 text-xs font-medium border border-amber-200 dark:border-amber-800">
                          {t('enterIncomeFirst', 'لطفاً ابتدا درآمد خالص ماهانه را وارد کنید تا مانده تخصیص‌نیافته محاسبه گردد.')}
                        </div>
                      )}
                    </div>
                  );
                })()}

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 pt-1">
                  {SPENDABLE_CATEGORIES.map(cat => {
                    const IconC = cat.icon;
                    return (
                      <div key={cat.id} className="p-3 bg-gray-50 dark:bg-gray-800/80 rounded-2xl border border-gray-200 dark:border-gray-700 space-y-1.5">
                        <div className="flex items-center justify-between text-xs font-bold text-gray-700 dark:text-gray-300">
                          <div className="flex items-center gap-1.5">
                            <IconC className="w-4 h-4 text-indigo-500" />
                            <span>{getCategoryMeta(cat.id).name}</span>
                          </div>
                          <span className="text-[10px] text-gray-400 font-sans tabular-nums font-bold">
                            ~{formatNumberByLang(cat.defaultPercent, lang)}٪
                          </span>
                        </div>
                        <div className="flex items-center gap-1">
                          <input
                            type="number"
                            step="any"
                            min="0"
                            placeholder={t('noBudgetPlaceholder', '۰ (بدون بودجه)')}
                            value={bgCategoryCaps[cat.id] ?? ''}
                            onChange={(e) => setBgCategoryCaps(prev => ({ ...prev, [cat.id]: e.target.value }))}
                            className="w-full p-2 bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 text-xs font-sans tabular-nums text-gray-900 dark:text-white"
                          />
                          <span className="text-[10px] font-bold text-gray-500 shrink-0">{bgCurrency}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="flex justify-end pt-2">
                <button
                  type="submit"
                  className="px-6 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold transition-all shadow-md flex items-center gap-1.5 cursor-pointer"
                >
                  <ShieldCheck className="w-4 h-4" />
                  <span>{t('saveBudgetFormulaBtn', 'ذخیره فرمول و سقف بودجه‌بندی')}</span>
                </button>
              </div>
            </form>
          </div>

          {/* Current Month Expense Progress & Gauge */}
          <div id="budget-gauges-section" className="p-6 bg-white dark:bg-gray-900 rounded-3xl border border-gray-100 dark:border-gray-800 shadow-sm space-y-5">
            {/* Month Navigation Control Bar */}
            <div className="p-3.5 bg-indigo-50/80 dark:bg-indigo-950/40 rounded-2xl border border-indigo-100 dark:border-indigo-900/50 flex flex-col md:flex-row md:items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-indigo-600 dark:text-indigo-400 shrink-0" />
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-gray-800 dark:text-gray-200">{t('selectMonthForBudgetAnalysis', 'انتخاب ماه جهت تحلیل بودجه:')}</span>
                    <span className="text-sm font-extrabold text-indigo-700 dark:text-indigo-300 font-sans tabular-nums">
                      {formatYearMonth(selectedBudgetMonth, lang)}
                    </span>
                  </div>
                  <p className="text-[10px] text-gray-500 dark:text-gray-400">
                    {t('budgetPatternNote', 'الگوی سقف بودجه به‌صورت خودکار روی تمام ماه‌ها اعمال می‌شود؛ تغییر ماه جهت بررسی عملکرد واقعی همان ماه است.')}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2 shrink-0 self-end md:self-auto">
                <button
                  type="button"
                  onClick={() => handleNavigateMonth('prev')}
                  title={t('prevMonth', 'ماه قبل')}
                  className="p-1.5 bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-gray-700 text-xs transition-all cursor-pointer flex items-center"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>

                <select
                  value={selectedBudgetMonth}
                  onChange={(e) => setSelectedBudgetMonth(e.target.value)}
                  className="p-1.5 bg-white dark:bg-gray-800 text-xs font-bold text-gray-900 dark:text-white rounded-xl border border-gray-200 dark:border-gray-700 cursor-pointer"
                >
                  {availableBudgetMonths.map(ym => (
                    <option key={ym} value={ym}>
                      {formatYearMonth(ym, lang)}
                    </option>
                  ))}
                </select>

                <button
                  type="button"
                  onClick={() => handleNavigateMonth('next')}
                  title={t('nextMonth', 'ماه بعد')}
                  className="p-1.5 bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-gray-700 text-xs transition-all cursor-pointer flex items-center"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>

                {(() => {
                  const currentYM = `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`;
                  if (selectedBudgetMonth !== currentYM) {
                    return (
                      <button
                        type="button"
                        onClick={handleSelectCurrentMonth}
                        className="px-2.5 py-1 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-[11px] font-bold transition-all shadow-xs cursor-pointer shrink-0"
                      >
                        ماه جاری
                      </button>
                    );
                  }
                  return null;
                })()}
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <PieChart className="w-5 h-5 text-indigo-600" />
                <h2 className="text-base font-extrabold text-gray-900 dark:text-white">
                  {t('budgetUsageStatusInMonth', 'وضعیت مصرف بودجه در {{month}}', { month: formatYearMonth(selectedBudgetMonth, lang) })}
                </h2>
              </div>
              <span className={`text-xs px-3 py-1 rounded-full font-bold ${
                monthBudgetStats.spentPercentage > 100
                  ? 'bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-300'
                  : monthBudgetStats.spentPercentage > 80
                  ? 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300'
                  : 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300'
              }`}>
                {formatNumberByLang(monthBudgetStats.spentPercentage, lang)}% {t('ofTotalBudgetUsed', 'از کل بودجه استفاده شده')}
              </span>
            </div>

            {/* Gauge Progress Bar */}
            <div className="space-y-2">
              <div className="flex justify-between items-center text-xs font-sans tabular-nums font-bold">
                <span className="text-gray-500">{t('spentLabel', 'هزینه‌شده:')} {formatNumberByLang(monthBudgetStats.monthlyExpenses.toLocaleString(), lang)} {budgetConfig.currency}</span>
                <span className="font-bold text-gray-800 dark:text-gray-200">{t('totalBudgetCapLabel', 'سقف بودجه کل:')} {formatNumberByLang(monthBudgetStats.expenseCap.toLocaleString(), lang)} {budgetConfig.currency}</span>
              </div>

              <div className="w-full h-4 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden p-0.5 border border-gray-200 dark:border-gray-700">
                <div
                  className={`h-full rounded-full transition-all duration-500 ${
                    monthBudgetStats.spentPercentage > 100
                      ? 'bg-rose-600'
                      : monthBudgetStats.spentPercentage > 80
                      ? 'bg-amber-500'
                      : 'bg-emerald-500'
                  }`}
                  style={{ width: `${Math.min(100, monthBudgetStats.spentPercentage)}%` }}
                />
              </div>

              <div className="flex items-center justify-between pt-1">
                <p className="text-xs text-gray-600 dark:text-gray-400">
                  {monthBudgetStats.remainingExpenseBudget >= 0 ? (
                    <span className="text-emerald-600 font-bold font-sans tabular-nums">
                      ✓ {t('remainingAllowedExpense', 'باقیمانده مجاز هزینه این ماه:')} {formatNumberByLang(monthBudgetStats.remainingExpenseBudget.toLocaleString(), lang)} {budgetConfig.currency}
                    </span>
                  ) : (
                    <span className="text-rose-600 font-bold flex items-center gap-1 font-sans tabular-nums">
                      <AlertTriangle className="w-4 h-4" />
                      {t('budgetExceeded', 'تخطی از بودجه:')} {formatNumberByLang(Math.abs(monthBudgetStats.remainingExpenseBudget).toLocaleString(), lang)} {budgetConfig.currency} {t('extraExpenseSuffix', 'اضافی هزینه شده!')}
                    </span>
                  )}
                </p>
              </div>
            </div>

            {/* Category Breakdown & Specific Cap Gauges */}
            <div className="space-y-3 pt-4 border-t border-gray-100 dark:border-gray-800">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <div>
                  <h3 className="text-xs font-bold text-gray-800 dark:text-gray-200 flex items-center gap-1.5">
                    <Layers className="w-4 h-4 text-indigo-600" />
                    <span>{t('liveGaugesTitle', 'گیج‌های زنده و گیج بودجه‌بندی دسته‌بندی‌ها:')}</span>
                  </h3>
                  <p className="text-[11px] text-gray-500">
                    {t('monthlyCategoryAnalysisDesc', 'تحلیل هزینه‌کرد ماه جاری در برابر سقف مجاز برای هر یک از دسته‌بندی‌های دارای بودجه')}
                  </p>
                </div>
                <div className="flex items-center gap-1.5 shrink-0 text-xs font-bold text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-950/60 px-3 py-1 rounded-full border border-indigo-200 dark:border-indigo-800">
                  <PieChart className="w-3.5 h-3.5" />
                  <span>
                    {t('categoriesWithBudgetCapCount', '{{count}} دسته‌بندی دارای سقف بودجه', { count: formatNumberByLang(SPENDABLE_CATEGORIES.filter(c => (budgetConfig.categoryCaps?.[c.id] || 0) > 0).length, lang) })}
                  </span>
                </div>
              </div>

              {/* Active Category Budget Gauges */}
              {(() => {
                const activeCappedCats = SPENDABLE_CATEGORIES.filter(c => (budgetConfig.categoryCaps?.[c.id] || 0) > 0);
                const uncappedCats = SPENDABLE_CATEGORIES.filter(c => !(budgetConfig.categoryCaps?.[c.id] || 0));

                return (
                  <div className="space-y-4">
                    {activeCappedCats.length > 0 ? (
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3.5">
                        {activeCappedCats.map((cat) => {
                          const meta = getCategoryMeta(cat.id);
                          const IconComp = cat.icon;
                          const spentAmt = monthBudgetStats.categoryTotals[cat.id] || 0;
                          const capAmt = budgetConfig.categoryCaps?.[cat.id] || 0;

                          const rawPct = Math.round((spentAmt / capAmt) * 100);
                          const catSpentPct = Math.min(100, rawPct);
                          const catRemaining = capAmt - spentAmt;
                          const isOverCap = catRemaining < 0;

                          return (
                            <div key={cat.id} className="p-4 bg-white dark:bg-gray-800/90 rounded-2xl border border-indigo-100 dark:border-indigo-900/50 shadow-xs space-y-2.5">
                              <div className="flex items-center justify-between text-xs">
                                <div className="flex items-center gap-2">
                                  <div className="p-2 rounded-xl bg-indigo-50 dark:bg-indigo-950 text-indigo-600 dark:text-indigo-400">
                                    <IconComp className="w-4 h-4" />
                                  </div>
                                  <div>
                                    <span className="font-extrabold text-gray-900 dark:text-gray-100 block">{meta.name}</span>
                                    <span className="text-[10px] text-gray-400 font-sans tabular-nums font-bold">{t('capLabel', 'سقف:')} {formatNumberByLang(capAmt.toLocaleString(), lang)} {budgetConfig.currency}</span>
                                  </div>
                                </div>
                                <span className={`text-[11px] px-2.5 py-0.5 rounded-full font-extrabold font-sans tabular-nums ${
                                  isOverCap 
                                    ? 'bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-300' 
                                    : rawPct > 80 
                                    ? 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300' 
                                    : 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300'
                                }`}>
                                  {formatNumberByLang(rawPct, lang)}٪
                                </span>
                              </div>

                              {/* Progress Gauge */}
                              <div className="space-y-1">
                                <div className="w-full h-3 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden p-0.5 border border-gray-200 dark:border-gray-600">
                                  <div
                                    className={`h-full rounded-full transition-all duration-500 ${
                                      isOverCap ? 'bg-rose-600' : rawPct > 80 ? 'bg-amber-500' : 'bg-emerald-500'
                                    }`}
                                    style={{ width: `${catSpentPct}%` }}
                                  />
                                </div>
                                <div className="flex justify-between items-center text-xs font-sans tabular-nums pt-0.5">
                                  <span className="text-gray-500 font-bold">{t('usedLabel', 'مصرفی:')} {formatNumberByLang(spentAmt.toLocaleString(), lang)} {budgetConfig.currency}</span>
                                  {isOverCap ? (
                                    <span className="text-rose-600 font-extrabold flex items-center gap-0.5">
                                      <AlertCircle className="w-3 h-3" />
                                      {t('surplusLabel', 'مازاد:')} {formatNumberByLang(Math.abs(catRemaining).toLocaleString(), lang)}
                                    </span>
                                  ) : (
                                    <span className="text-emerald-600 font-extrabold">
                                      {t('balanceLabel', 'مانده:')} {formatNumberByLang(catRemaining.toLocaleString(), lang)}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="p-4 bg-gray-50 dark:bg-gray-800/40 rounded-2xl border border-dashed border-gray-200 dark:border-gray-700 text-center space-y-1">
                        <p className="text-xs font-bold text-gray-700 dark:text-gray-300">
                          {t('noCategoryCapsDefinedYet', 'هنوز سقف اختصاصی برای هیچ‌یک از دسته‌بندی‌ها ثبت نشده است.')}
                        </p>
                        <p className="text-[11px] text-gray-500">
                          {t('defineCategoryCapsHint', 'در فرم بالا با دکمه «تخمین خودکار» یا درج دستی مبلغ، می‌توانید برای هر دسته‌بندی سقف تعیین کنید تا گیج‌های گرافیکی آن فعال گردد.')}
                        </p>
                      </div>
                    )}

                    {/* Uncapped categories list */}
                    {uncappedCats.length > 0 && (
                      <div className="pt-2">
                        <span className="text-[11px] font-bold text-gray-500 dark:text-gray-400 block mb-2">
                          {t('categoriesWithoutCapTitle', 'دسته‌بندی‌های بدون سقف اختصاصی (صرفاً گزارش هزینه‌کرد):')}
                        </span>
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                          {uncappedCats.map(cat => {
                            const meta = getCategoryMeta(cat.id);
                            const IconC = cat.icon;
                            const spentAmt = monthBudgetStats.categoryTotals[cat.id] || 0;

                            return (
                              <div key={cat.id} className="p-2.5 bg-gray-50/80 dark:bg-gray-800/40 rounded-xl border border-gray-100 dark:border-gray-800 flex items-center justify-between text-xs">
                                <div className="flex items-center gap-1.5 overflow-hidden">
                                  <IconC className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                                  <span className="truncate text-gray-600 dark:text-gray-400 text-[11px]">{meta.name}</span>
                                </div>
                                <span className="font-sans tabular-nums text-xs font-extrabold text-gray-800 dark:text-gray-200 shrink-0">
                                  {formatNumberByLang(spentAmt.toLocaleString(), lang)}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>

          {/* Monthly Budget History Summary */}
          <div className="p-6 bg-white dark:bg-gray-900 rounded-3xl border border-gray-100 dark:border-gray-800 shadow-sm space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <History className="w-5 h-5 text-indigo-600" />
                <div>
                  <h2 className="text-base font-extrabold text-gray-900 dark:text-white">
                    {t('budgetHistorySectionTitle', 'تاریخچه و خلاصه عملکرد بودجه‌بندی ماه‌های مختلف')}
                  </h2>
                  <p className="text-xs text-gray-500">
                    {t('budgetHistorySectionDesc', 'بررسی مقایسه‌ای سقف بودجه، هزینه‌کرد واقعی و مانده/مازاد بودجه در ماه‌های گذشته')}
                  </p>
                </div>
              </div>
              <span className="text-xs font-bold text-gray-500 bg-gray-100 dark:bg-gray-800 px-3 py-1 rounded-full self-start sm:self-auto">
                {t('monthsInHistory', '{{count}} ماه در تاریخچه', { count: formatNumberByLang(monthlyBudgetHistory.length, lang) })}
              </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3.5 pt-1">
              {monthlyBudgetHistory.map(item => {
                const isSelected = item.yearMonth === selectedBudgetMonth;
                const isCurrent = item.yearMonth === `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`;

                return (
                  <div
                    key={item.yearMonth}
                    className={`p-4 rounded-2xl border transition-all space-y-3 ${
                      isSelected
                        ? 'bg-indigo-50/60 dark:bg-indigo-950/40 border-indigo-300 dark:border-indigo-700 shadow-xs ring-2 ring-indigo-500/20'
                        : 'bg-gray-50/80 dark:bg-gray-800/60 border-gray-100 dark:border-gray-800 hover:border-gray-300 dark:hover:border-gray-700'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1.5">
                        <Calendar className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                        <span className="font-extrabold text-xs text-gray-900 dark:text-white">
                          {formatYearMonth(item.yearMonth, lang)}
                        </span>
                      </div>
                      <div className="flex items-center gap-1">
                        {isCurrent && (
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300">
                            {t('currentMonthBtn', 'ماه جاری')}
                          </span>
                        )}
                        {isSelected && (
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-indigo-600 text-white">
                            {t('selectedMonthLabel', 'انتخاب‌شده')}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-2 text-xs font-sans tabular-nums">
                      <div className="p-2 bg-white dark:bg-gray-900/80 rounded-xl border border-gray-100 dark:border-gray-800">
                        <span className="text-gray-400 block text-[10px] font-sans">{t('budgetCapHeader', 'سقف بودجه:')}</span>
                        <span className="font-bold text-gray-800 dark:text-gray-200">
                          {formatNumberByLang(item.expenseCap.toLocaleString(), lang)}
                        </span>
                      </div>
                      <div className="p-2 bg-white dark:bg-gray-900/80 rounded-xl border border-gray-100 dark:border-gray-800">
                        <span className="text-gray-400 block text-[10px] font-sans">{t('actualSpentHeader', 'هزینه‌کرد واقعی:')}</span>
                        <span className="font-bold text-rose-600 dark:text-rose-400">
                          {formatNumberByLang(item.monthlyExpenses.toLocaleString(), lang)}
                        </span>
                      </div>
                    </div>

                    {/* Variance status */}
                    <div className="space-y-1.5">
                      <div className="flex justify-between items-center text-[10px] font-bold">
                        <span>{t('budgetStatusLabel', 'وضعیت بودجه:')}</span>
                        {item.remaining >= 0 ? (
                          <span className="text-emerald-600 dark:text-emerald-400 font-sans tabular-nums font-bold">
                            ✓ {t('balanceLabel', 'مانده:')} {formatNumberByLang(item.remaining.toLocaleString(), lang)} {budgetConfig.currency}
                          </span>
                        ) : (
                          <span className="text-rose-600 dark:text-rose-400 font-sans tabular-nums font-bold">
                            ⚠️ {t('surplusLabel', 'مازاد:')} {formatNumberByLang(Math.abs(item.remaining).toLocaleString(), lang)} {budgetConfig.currency}
                          </span>
                        )}
                      </div>

                      {/* Progress bar */}
                      <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all duration-300 ${
                            item.isOverBudget
                              ? 'bg-rose-600'
                              : item.spentPct > 80
                              ? 'bg-amber-500'
                              : 'bg-emerald-500'
                          }`}
                          style={{ width: `${Math.min(100, item.spentPct)}%` }}
                        />
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={() => {
                        setSelectedBudgetMonth(item.yearMonth);
                        const el = document.getElementById('budget-gauges-section');
                        if (el) el.scrollIntoView({ behavior: 'smooth' });
                      }}
                      className={`w-full py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer flex items-center justify-center gap-1 ${
                        isSelected
                          ? 'bg-indigo-600 text-white shadow-xs'
                          : 'bg-white dark:bg-gray-800 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700'
                      }`}
                    >
                      <span>{isSelected ? t('viewingGaugesAction', 'در حال مشاهده گیج‌ها') : t('viewGaugesAction', 'مشاهده و تحلیل این ماه')}</span>
                      <ArrowRight className="w-3.5 h-3.5 rotate-180" />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Goals Feasibility & Savings Planner Analysis */}
          <div className="p-6 bg-gradient-to-br from-indigo-900 to-slate-900 text-white rounded-3xl shadow-xl space-y-4">
            <div className="flex items-center gap-2.5">
              <div className="p-2.5 bg-indigo-500/20 rounded-2xl text-indigo-300">
                <Target className="w-5 h-5" />
              </div>
              <div>
                <h2 className="text-base font-extrabold">
                  {t('goalsForecastTitle', 'تحلیل و تخمین زمان تحقق اهداف مالی')}
                </h2>
                <p className="text-xs text-indigo-200">
                  {t('goalsForecastDesc', 'محاسبه هوشمند بر اساس سهم پس‌انداز ماهانه ({{ratio}}%)', { ratio: formatNumberByLang(budgetConfig.savingLimitRatio, lang) })}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-2">
              <div className="p-4 bg-white/5 rounded-2xl border border-white/10 space-y-1">
                <span className="text-[11px] text-indigo-300 block">{t('totalSavingsRequiredForGoals', 'کل پس‌انداز مورد نیاز اهداف:')}</span>
                <span className="text-lg font-extrabold font-mono text-amber-400">
                  {formatNumberByLang(goalsForecast.totalRemainingTarget.toLocaleString(), lang)} {budgetConfig.currency}
                </span>
              </div>

              <div className="p-4 bg-white/5 rounded-2xl border border-white/10 space-y-1">
                <span className="text-[11px] text-indigo-300 block">{t('activeMonthlySavingsBudget', 'بودجه پس‌انداز ماهانه فعال:')}</span>
                <span className="text-lg font-extrabold font-mono text-emerald-400">
                  {formatNumberByLang(goalsForecast.monthlySavingAllocated.toLocaleString(), lang)} {budgetConfig.currency} / {t('perMonthSuffix', 'ماه')}
                </span>
              </div>

              <div className="p-4 bg-white/5 rounded-2xl border border-white/10 space-y-1">
                <span className="text-[11px] text-indigo-300 block">{t('goalsForecastLabel', 'تخمین زمان تکمیل تمامی اهداف:')}</span>
                <span className="text-lg font-extrabold font-mono text-sky-400">
                  {goalsForecast.estimatedMonthsNeeded > 0 ? t('estimatedFutureMonths', '{{count}} ماه آینده', { count: formatNumberByLang(goalsForecast.estimatedMonthsNeeded, lang) }) : t('noActiveGoals', 'بدون هدف فعال')}
                </span>
              </div>
            </div>

            <p className="text-xs text-indigo-100 bg-white/10 p-3.5 rounded-2xl leading-relaxed">
              💡 <strong>{t('guide', 'راهنما')}:</strong> {t('goalsSystemGuide', 'با اختصاص دادن منظم {{amount}} {{currency}} در هر ماه به صندوق پس‌انداز، می‌توانید تمامی اهداف تعریف شده را در زمان مقرر محقق سازید.', { amount: formatNumberByLang(goalsForecast.monthlySavingAllocated.toLocaleString(), lang), currency: budgetConfig.currency })}
            </p>
          </div>

        </div>
      )}

      {/* TAB BATCH: BATCH TRANSACTION IMPORT & CONFIRMATION */}
      {activeTab === 'batch' && (
        <div className="space-y-6">
          <div className="p-6 bg-white dark:bg-gray-900 rounded-3xl border border-gray-100 dark:border-gray-800 shadow-sm space-y-5">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-blue-100 dark:bg-blue-950 text-blue-600 rounded-2xl">
                  <FileSpreadsheet className="w-6 h-6" />
                </div>
                <div>
                  <h2 className="text-base font-extrabold text-gray-900 dark:text-white">
                    {t('batchImportHeaderTitle', 'ورود دسته‌ای و هوشمند تراکنش‌های بانک و صورت‌حساب‌ها (Batch Import)')}
                  </h2>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {t('batchImportHeaderDesc', 'متن خروجی یا CSV صورت‌حساب بانک یا فروشگاه را کپی کرده و مستقیماً در کادر متنی زیر پیست (Paste) کنید. (نیاز به آپلود فایل نیست)')}
                  </p>
                </div>
              </div>

              <button
                onClick={() => {
                  setBatchRawText(
`TransactionDate,Text,Type,Currency Amount,Currency Rate,Currency,Amount,Merchant Area,Merchant Category,BookDate,ValueDate
7/31/2026,HEMKOP STOCKHOLM RAC     ,Köp,-130.29,1,SEK,-130.29,VALLINGBY    ,"Grocery Stores, Supermarkets        ",8/1/2026,8/28/2026
8/3/2026,LIDL354KISTAGALLERIALA   ,Köp,-97.8,1,SEK,-97.8,STOCKHOLM    ,"Grocery Stores, Supermarkets        ",8/4/2026,8/28/2026
8/6/2026,WILLYS STOCKHOLM BRO     ,Köp,-239.02,1,SEK,-239.02,BROMMA       ,"Grocery Stores, Supermarkets        ",8/7/2026,8/28/2026
8/6/2026,RUSTA - 75 BROMMA BLOCKS ,Köp,-715.9,1,SEK,-715.9,BROMMA       ,Department Stores                   ,8/7/2026,8/28/2026
8/7/2026,Shami Bazar              ,Köp,-492.24,1,SEK,-492.24,Kista        ,Eating places and Restaurants       ,8/8/2026,8/28/2026`
                  );
                }}
                className="px-3.5 py-2 bg-blue-50 dark:bg-blue-950/60 text-blue-700 dark:text-blue-300 hover:bg-blue-100 rounded-xl text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5 border border-blue-200 dark:border-blue-900"
              >
                <FileText className="w-4 h-4 text-blue-500" />
                <span>{t('insertCsvSample', 'درج نمونه خروجی CSV صورت‌حساب بانک')}</span>
              </button>
            </div>

            {/* Global Settings & Input Area */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="space-y-1">
                <label className="text-xs font-bold text-gray-700 dark:text-gray-300">{t('targetCardForBatch', 'کارت / حساب هدف برای تمام تراکنش‌های این صورت‌حساب:')}</label>
                <select
                  value={batchGlobalCard}
                  onChange={(e) => {
                    const newCard = e.target.value;
                    setBatchGlobalCard(newCard);
                    if (batchRows.length > 0) {
                      setBatchRows(batchRows.map(r => ({ ...r, cardAccount: newCard })));
                    }
                  }}
                  className="w-full p-2.5 bg-gray-50 dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 text-xs font-bold text-indigo-700 dark:text-indigo-300"
                >
                  {userCards.map(card => (
                    <option key={card} value={card}>{card}</option>
                  ))}
                </select>
              </div>

              <div className="space-y-1">
                <label className="text-xs font-bold text-gray-700 dark:text-gray-300">{t('defaultCategoryForBatch', 'دسته‌بندی پیش‌فرض برای موارد نامشخص:')}</label>
                <select
                  value={batchGlobalCategory}
                  onChange={(e) => setBatchGlobalCategory(e.target.value as FinanceCategory)}
                  className="w-full p-2.5 bg-gray-50 dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 text-xs font-bold"
                >
                  <option value="groceries">{t('catGroceries', '🛒 سوپرمارکت و خواربار')}</option>
                  <option value="housing">{t('catHousing', '🏠 مسکن و اجاره/رهن')}</option>
                  <option value="utilities">{t('catUtilities', '⚡ قبوض، انرژی و اینترنت')}</option>
                  <option value="transport">{t('catTransport', '🚗 حمل‌و‌نقل و سوخت')}</option>
                  <option value="restaurants">{t('catRestaurants', '🍽️ رستوران، کافه و غذا')}</option>
                  <option value="health">{t('catHealth', '🩺 سلامت، درمان و دارو')}</option>
                  <option value="clothing">{t('catClothing', '👗 پوشاک، کفش و زیبایی')}</option>
                  <option value="electronics">{t('catElectronics', '💻 کالای دیجیتال و الکترونیک')}</option>
                  <option value="home_decor">{t('catHomeDecor', '🛋️ لوازم خانگی و دکوراسیون')}</option>
                  <option value="car_maintenance">{t('catCarMaintenance', '🔧 تعمیرات خودرو و بیمه')}</option>
                  <option value="subscriptions">{t('catSubscriptions', '🌐 اشتراک‌های دیجیتال و AI')}</option>
                  <option value="pets">{t('catPets', '🐾 حیوانات خانگی و پت‌شاپ')}</option>
                  <option value="charity_gifts">{t('catCharityGifts', '🎁 هدایا، کادو و خیریه')}</option>
                  <option value="education">{t('catEducation', '🎓 آموزش و توسعه فردی')}</option>
                  <option value="entertainment">{t('catEntertainment', '🎉 تفریح، ورزش و سفر')}</option>
                  <option value="family">{t('catFamily', '👶 امور فرزندان و خانواده')}</option>
                  <option value="salary">{t('catSalary', '💼 حقوق و درآمد اصلی')}</option>
                  <option value="investment">{t('catInvestment', '📈 پس‌انداز و سرمایه‌گذاری')}</option>
                  <option value="other">{t('catOther', '🏷️ خدمات و متفرقه')}</option>
                </select>
              </div>

              <div className="flex items-end">
                <button
                  onClick={handleProcessBatchText}
                  disabled={!batchRawText.trim()}
                  className="w-full py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-xl text-xs font-bold shadow-md transition-all flex items-center justify-center gap-2 cursor-pointer"
                >
                  <RefreshCw className="w-4 h-4" />
                  <span>{t('extractRowsSmartBtn', 'استخراج و شناسایی هوشمند سطرها')}</span>
                </button>
              </div>
            </div>

            <textarea
              rows={6}
              value={batchRawText}
              onChange={(e) => setBatchRawText(e.target.value)}
              placeholder={t('batchPastePlaceholder', `متن یا کپی صورت‌حساب بانک / اکسپورت خود را در اینجا پیست کنید. مثال:
2026-08-01, Hemköp Stockholm, -485 SEK, Hemköp Card
2026-08-02, SL Buss Månadskort, -970 SEK, Handelsbanken Debit
2026-08-03, Swish to Ali, -250 SEK
2026-08-07, Lön Handelsbanken, +38500 SEK`)}
              className="w-full p-4 bg-gray-50 dark:bg-gray-800/90 rounded-2xl border border-gray-200 dark:border-gray-700 text-xs font-mono text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            />

            {batchSuccessMsg && (
              <div className="p-4 bg-emerald-50 dark:bg-emerald-950/60 border border-emerald-300 dark:border-emerald-800 rounded-2xl text-xs text-emerald-800 dark:text-emerald-200 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
                  <span className="font-bold">{batchSuccessMsg}</span>
                </div>
                <button
                  onClick={() => setActiveTab('overview')}
                  className="px-3 py-1.5 bg-emerald-600 text-white rounded-xl font-bold text-[11px] hover:bg-emerald-700 transition-all cursor-pointer"
                >
                  {t('viewInTransactionsList', 'مشاهده در فهرست تراکنش‌ها →')}
                </button>
              </div>
            )}
          </div>

          {/* Interactive Batch Preview & Confirmation Table */}
          {batchRows.length > 0 && (
            <div className="p-6 bg-white dark:bg-gray-900 rounded-3xl border border-gray-100 dark:border-gray-800 shadow-xl space-y-4">
              <div className="flex items-center justify-between flex-wrap gap-2 pb-2 border-b border-gray-100 dark:border-gray-800">
                <div className="flex items-center gap-2">
                  <CheckSquare className="w-5 h-5 text-blue-600" />
                  <h3 className="text-sm font-extrabold text-gray-900 dark:text-white">
                    {t('batchPreviewTableTitle', 'جدول پیش‌نمایش تراکنش‌های استخراج‌شده ({{count}} مورد)', { count: formatNumberByLang(batchRows.length, lang) })}
                  </h3>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => {
                      const allSelected = batchRows.every(r => r.isSelected);
                      setBatchRows(batchRows.map(r => ({ ...r, isSelected: !allSelected })));
                    }}
                    className="px-3 py-1.5 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-xl text-xs font-bold hover:bg-gray-200 cursor-pointer"
                  >
                    {batchRows.every(r => r.isSelected) ? t('deselectAll', 'لغو انتخاب همه') : t('selectAll', 'انتخاب همه')}
                  </button>
                </div>
              </div>

              <div className="p-3 bg-indigo-50 dark:bg-indigo-950/60 border border-indigo-200 dark:border-indigo-900 rounded-2xl text-xs text-indigo-900 dark:text-indigo-200 flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <CreditCard className="w-4 h-4 text-indigo-600 shrink-0" />
                  <span>{t('cardConnectedToAllTxs', 'کارت / حساب متصل به تمامی تراکنش‌های این صورت‌حساب:')} <strong className="font-extrabold text-indigo-700 dark:text-indigo-300 underline">{batchGlobalCard}</strong></span>
                </div>
                <span className="text-[11px] text-gray-500 font-medium">{t('allTxsAssignedToCard', 'تمامی {{count}} تراکنش به این کارت منتسب می‌شوند.', { count: formatNumberByLang(batchRows.length, lang) })}</span>
              </div>

              <div className="overflow-x-auto rounded-2xl border border-gray-200 dark:border-gray-800">
                <table className="w-full text-right text-xs">
                  <thead className="bg-gray-50 dark:bg-gray-800 text-gray-500 dark:text-gray-400 font-bold">
                    <tr>
                      <th className="p-3 text-center w-10">{t('financeBatchColSelect', 'انتخاب')}</th>
                      <th className="p-3">{t('financeBatchColDate', 'تاریخ')}</th>
                      <th className="p-3">{t('batchColTxTitle', 'عنوان تراکنش')}</th>
                      <th className="p-3">{t('amountColumnHeader', 'مبلغ')} ({budgetConfig.currency})</th>
                      <th className="p-3">{t('financeBatchColType', 'نوع')}</th>
                      <th className="p-3">{t('financeBatchColCard', 'کارت / حساب پرداختی')}</th>
                      <th className="p-3">{t('financeBatchColCategory', 'دسته‌بندی')}</th>
                      <th className="p-3 text-center">{t('delete', 'حذف')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                    {batchRows.map((row, idx) => (
                      <tr key={row.id} className={`transition-colors ${row.isSelected ? 'bg-blue-50/40 dark:bg-blue-950/20' : 'opacity-60 bg-gray-50/30'}`}>
                        <td className="p-3 text-center">
                          <input
                            type="checkbox"
                            checked={row.isSelected}
                            onChange={(e) => {
                              const updated = [...batchRows];
                              updated[idx].isSelected = e.target.checked;
                              setBatchRows(updated);
                            }}
                            className="w-4 h-4 rounded text-blue-600 focus:ring-blue-500 cursor-pointer"
                          />
                        </td>
                        <td className="p-3">
                          <input
                            type="date"
                            value={row.date}
                            onChange={(e) => {
                              const updated = [...batchRows];
                              updated[idx].date = e.target.value;
                              setBatchRows(updated);
                            }}
                            className="p-1.5 bg-white dark:bg-gray-800 border rounded-xl text-xs font-mono"
                          />
                        </td>
                        <td className="p-3">
                          <input
                            type="text"
                            value={row.title}
                            onChange={(e) => {
                              const updated = [...batchRows];
                              updated[idx].title = e.target.value;
                              setBatchRows(updated);
                            }}
                            className="w-full p-1.5 bg-white dark:bg-gray-800 border rounded-xl text-xs font-bold"
                          />
                          {row.matchStatus === 'match_merge_bank' && (
                            <span className="text-[10px] px-2 py-0.5 rounded-md font-bold bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300 block mt-1">
                              ⚡ {t('matchWithPurchase', 'تطبیق با خرید {{title}} (کارت بانک به خرید اعمال می‌شود)', { title: row.matchedTxTitle })}
                            </span>
                          )}
                          {row.matchStatus === 'match_merge_store' && (
                            <span className="text-[10px] px-2 py-0.5 rounded-md font-bold bg-sky-100 text-sky-800 dark:bg-sky-950 dark:text-sky-300 block mt-1">
                              🔄 {t('matchWithBankTx', 'تطبیق با تراکنش بانک {{title}} (جزئیات خرید جایگزین بانک می‌شود)', { title: row.matchedTxTitle })}
                            </span>
                          )}
                          {row.matchStatus === 'duplicate_skip' && (
                            <span className="text-[10px] px-2 py-0.5 rounded-md font-bold bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-300 block mt-1">
                              ⚠️ {t('duplicateWithTx', 'تکراری با {{title}} (صرف‌نظر از ثبت تکراری)', { title: row.matchedTxTitle })}
                            </span>
                          )}
                          {row.matchStatus === 'new' && (
                            <span className="text-[10px] px-2 py-0.5 rounded-md font-bold bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300 block mt-1">
                              🟢 {t('statusNew', 'جدید')}
                            </span>
                          )}
                        </td>
                        <td className="p-3">
                          <input
                            type="number"
                            step="any"
                            value={row.amount}
                            onChange={(e) => {
                              const updated = [...batchRows];
                              updated[idx].amount = parseFloat(e.target.value) || 0;
                              setBatchRows(updated);
                            }}
                            className="w-24 p-1.5 bg-white dark:bg-gray-800 border rounded-xl text-xs font-mono font-bold text-left"
                          />
                        </td>
                        <td className="p-3">
                          <select
                            value={row.type}
                            onChange={(e) => {
                              const updated = [...batchRows];
                              updated[idx].type = e.target.value as FinanceTransactionType;
                              setBatchRows(updated);
                            }}
                            className="p-1.5 bg-white dark:bg-gray-800 border rounded-xl text-xs font-bold"
                          >
                            <option value="expense">{t('financeTypeExpense', 'هزینه')}</option>
                            <option value="income">{t('financeTypeIncome', 'درآمد')}</option>
                            <option value="savings">{t('financeTypeSavings', 'پس‌انداز')}</option>
                          </select>
                        </td>
                        <td className="p-3">
                          <select
                            value={row.cardAccount}
                            onChange={(e) => {
                              const updated = [...batchRows];
                              updated[idx].cardAccount = e.target.value;
                              setBatchRows(updated);
                            }}
                            className="p-1.5 bg-white dark:bg-gray-800 border rounded-xl text-xs font-bold text-indigo-700 dark:text-indigo-300"
                          >
                            {userCards.map(card => (
                              <option key={card} value={card}>{card}</option>
                            ))}
                          </select>
                        </td>
                        <td className="p-3">
                          <select
                            value={row.category}
                            onChange={(e) => {
                              const updated = [...batchRows];
                              updated[idx].category = e.target.value as FinanceCategory;
                              setBatchRows(updated);
                            }}
                            className="p-1.5 bg-white dark:bg-gray-800 border rounded-xl text-xs"
                          >
                            <option value="groceries">🛒 {t('catGroceries', 'سوپرمارکت')}</option>
                            <option value="housing">🏠 {t('catHousing', 'مسکن')}</option>
                            <option value="utilities">⚡ {t('catUtilities', 'قبوض')}</option>
                            <option value="transport">🚗 {t('catTransport', 'حمل‌ونقل')}</option>
                            <option value="restaurants">🍽️ {t('catRestaurants', 'رستوران')}</option>
                            <option value="health">🩺 {t('catHealth', 'سلامت')}</option>
                            <option value="clothing">👗 {t('catClothing', 'پوشاک')}</option>
                            <option value="electronics">💻 {t('catElectronics', 'الکترونیک')}</option>
                            <option value="home_decor">🛋️ {t('catHomeDecor', 'لوازم خانگی')}</option>
                            <option value="car_maintenance">🔧 {t('catCarMaintenance', 'خودرو/بیمه')}</option>
                            <option value="subscriptions">🌐 {t('catSubscriptions', 'اشتراک/SaaS')}</option>
                            <option value="pets">🐾 {t('catPets', 'پت‌شاپ')}</option>
                            <option value="charity_gifts">🎁 {t('catCharityGifts', 'هدیه/خیریه')}</option>
                            <option value="education">🎓 {t('catEducation', 'آموزش')}</option>
                            <option value="entertainment">🎉 {t('catEntertainment', 'تفریح')}</option>
                            <option value="family">👶 {t('catFamily', 'خانواده')}</option>
                            <option value="salary">💼 {t('catSalary', 'حقوق')}</option>
                            <option value="investment">📈 {t('catInvestment', 'سرمایه')}</option>
                            <option value="other">🏷️ {t('catOther', 'سایر')}</option>
                          </select>
                        </td>
                        <td className="p-3 text-center">
                          <button
                            onClick={() => {
                              setBatchRows(batchRows.filter(r => r.id !== row.id));
                            }}
                            className="text-rose-500 hover:text-rose-700 cursor-pointer p-1"
                          >
                            ✕
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Bottom Confirmation Bar */}
              <div className="p-4 bg-gradient-to-r from-blue-900 to-indigo-900 text-white rounded-2xl flex flex-col sm:flex-row items-center justify-between gap-3 shadow-lg">
                <div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                    <span className="font-extrabold text-sm">
                      {t('batchReadyToImportCount', 'تعداد {{count}} تراکنش انتخابی آماده ثبت نهایی است.', { count: formatNumberByLang(batchRows.filter(r => r.isSelected).length, lang) })}
                    </span>
                  </div>
                  <div className="text-xs text-blue-200 mt-1 flex flex-wrap gap-x-4 gap-y-1">
                    <span>{t('totalExpenseLabel', 'مجموع هزینه:')} <strong className="text-amber-300 font-mono">{formatNumberByLang(batchRows.filter(r => r.isSelected && r.type === 'expense').reduce((sum, r) => sum + r.amount, 0).toLocaleString(), lang)} {budgetConfig.currency}</strong></span>
                    <span>{t('totalIncomeLabel', 'مجموع درآمد/ورودی:')} <strong className="text-emerald-300 font-mono">{formatNumberByLang(batchRows.filter(r => r.isSelected && r.type === 'income').reduce((sum, r) => sum + r.amount, 0).toLocaleString(), lang)} {budgetConfig.currency}</strong></span>
                  </div>
                </div>

                <button
                  onClick={handleConfirmBatchInsert}
                  disabled={batchRows.filter(r => r.isSelected).length === 0}
                  className="px-6 py-3 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white rounded-xl text-xs font-extrabold transition-all cursor-pointer shadow-md flex items-center gap-2 shrink-0"
                >
                  <Check className="w-4 h-4" />
                  <span>{t('confirmBatchImportBtn', 'تأیید و ثبت نهایی در پایگاه داده')}</span>
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* TAB 3: AI INVOICE SCANNER */}
      {activeTab === 'scanner' && (
        <div className="space-y-6">
          <div className="p-6 bg-white dark:bg-gray-900 rounded-3xl border border-gray-100 dark:border-gray-800 shadow-sm space-y-4">
            <div className="flex items-center gap-2.5">
              <div className="p-2.5 bg-purple-100 dark:bg-purple-950 text-purple-600 rounded-2xl">
                <Sparkles className="w-5 h-5" />
              </div>
              <div>
                <h2 className="text-base font-extrabold text-gray-900 dark:text-white">
                  {t('financeAiScannerHeader', 'اسکن و تحلیل هوشمند متن فاکتور با هوش مصنوعی (AI)')}
                </h2>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {t('financeAiScannerDesc', 'متن فاکتور یا رسید خرید خود (سوئیش، ICA، Willys و...) را جای‌گذاری کنید تا اقلام، قیمت و دسته‌بندی استخراج شوند.')}
                </p>
              </div>
            </div>

            <div className="space-y-3">
              <textarea
                rows={6}
                value={factorText}
                onChange={(e) => setFactorText(e.target.value)}
                placeholder={t('scannerSamplePlaceholder', `Willys\n2x MJÖLK 1.5L - 37 SEK\n1x BRÖD - 28 SEK\nTotalt: 65 SEK\nBetalt med Swish`)}
                className="w-full p-4 bg-gray-50 dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 text-xs font-mono text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
              />

              {aiError && (
                <p className="text-xs text-rose-600 bg-rose-50 p-3 rounded-xl border border-rose-200">
                  {aiError}
                </p>
              )}

              <div className="flex justify-end">
                <button
                  onClick={handleParseFactorText}
                  disabled={aiParsing || !factorText.trim()}
                  className="px-6 py-3 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white rounded-2xl font-bold text-xs shadow-lg transition-all flex items-center gap-2 cursor-pointer"
                >
                  {aiParsing ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      <span>{t('financeAiExtracting', 'در حال استخراج اقلام توسط AI...')}</span>
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4" />
                      <span>{t('financeAiAnalyzeBtn', 'تحلیل هوشمند فاکتور با هوش مصنوعی')}</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* AI Parsed Preview Modal / Result */}
          {parsedPreview && (() => {
            const calculatedTotal = (parsedPreview.items && parsedPreview.items.length > 0)
              ? Math.round(parsedPreview.items.reduce((sum, item) => sum + (Number(item.totalPrice) || 0), 0) * 100) / 100
              : (parsedPreview.amount || 0);

            const invDate = parsedPreview.date || new Date().toISOString().split('T')[0];
            const invTitle = (parsedPreview.title || '').toLowerCase();
            const invTime = new Date(invDate).getTime();
            const storeKeywords = ['willys', 'ica', 'hemköp', 'hemkop', 'lidl', 'coop', 'city gross', 'rusta', 'oob', 'oöb', 'shami', 'zara', 'h&m', 'sl', 'circle k', 'okq8', 'mcdonald', 'max'];
            const matchedKeyword = storeKeywords.find(k => invTitle.includes(k));

            const matchingBankTx = transactions.find(tx => {
              const amtDiff = Math.abs(tx.amount - calculatedTotal);
              if (amtDiff > 1.0) return false;

              const txTime = new Date(tx.date).getTime();
              const daysDiff = Math.abs(invTime - txTime) / (1000 * 60 * 60 * 24);
              if (daysDiff > 2) return false;

              const lowerTxTitle = tx.title.toLowerCase();
              if (matchedKeyword && lowerTxTitle.includes(matchedKeyword)) return true;
              if (amtDiff < 0.05 && daysDiff <= 1 && (!tx.items || tx.items.length === 0)) return true;

              return false;
            });

            return (
              <div className="p-6 bg-gradient-to-br from-indigo-50/50 via-white to-emerald-50/50 dark:from-indigo-950/20 dark:via-gray-900 dark:to-emerald-950/20 rounded-3xl border-2 border-indigo-200 dark:border-indigo-900 shadow-xl space-y-5 animate-fadeIn">
                <div className="flex items-center justify-between border-b border-indigo-100 dark:border-indigo-900 pb-3">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                    <h3 className="text-sm font-extrabold text-gray-900 dark:text-white">
                      {t('financeAiParsedTitle', 'نتیجه استخراج فاکتور توسط هوش مصنوعی (قابل ویرایش)')}
                    </h3>
                  </div>
                  <span className="text-xs font-bold text-indigo-600 dark:text-indigo-400 bg-indigo-100 dark:bg-indigo-900/60 px-3 py-1 rounded-full">
                    {t('financeAiReadyToSave', 'آماده ذخیره نهایی')}
                  </span>
                </div>

                {/* Matching Bank Transaction Notice */}
                {matchingBankTx && (
                  <div className="p-3 bg-amber-50 dark:bg-amber-950/60 border border-amber-200 dark:border-amber-900 rounded-2xl text-xs text-amber-900 dark:text-amber-200 flex items-center gap-2 animate-pulse">
                    <AlertCircle className="w-4 h-4 text-amber-600 shrink-0" />
                    <span>
                      {t('overlappingBankTxFound', 'تراکنش هم‌پوشان بانکی پیدا شد: {{title}} با مبلغ {{amount}} {{currency}} (تاریخ {{date}}). هنگام ثبت، فاکتور کامل اسکن‌شده جایگزین این تراکنش خام خواهد شد تا از ثبت دوتایی جلوگیری شود.', { title: matchingBankTx.title, amount: formatNumberByLang(matchingBankTx.amount, lang), currency: matchingBankTx.currency, date: formatNumberByLang(matchingBankTx.date, lang) })}
                    </span>
                  </div>
                )}

                {/* Top Parameters Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 text-xs">
                  {/* Title / Store */}
                  <div className="p-3 bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 space-y-1">
                    <label className="text-gray-400 block text-[10px] font-bold">{t('financeAiStoreLabel', 'عنوان / فروشگاه:')}</label>
                    <input
                      type="text"
                      value={parsedPreview.title || ''}
                      onChange={(e) => setParsedPreview({ ...parsedPreview, title: e.target.value })}
                      className="w-full p-1.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-xs font-bold text-gray-900 dark:text-white"
                    />
                  </div>

                  {/* Calculated Grand Total */}
                  <div className="p-3 bg-rose-50/80 dark:bg-rose-950/40 rounded-2xl border border-rose-200 dark:border-rose-900 space-y-1">
                    <label className="text-rose-700 dark:text-rose-300 block text-[10px] font-extrabold">{t('financeAiTotalCalculated', 'مبلغ کل (محاسبه از اقلام):')}</label>
                    <div className="font-extrabold text-rose-600 dark:text-rose-400 font-sans tabular-nums text-sm py-1">
                      {formatNumberByLang(calculatedTotal.toLocaleString(), lang)} {parsedPreview.currency}
                    </div>
                  </div>

                  {/* Selectable Payment Method */}
                  <div className="p-3 bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 space-y-1">
                    <label className="text-gray-400 block text-[10px] font-bold">{t('financePaymentMethodLabel', 'روش پرداخت:')}</label>
                    <select
                      value={parsedPreview.paymentMethod || 'card'}
                      onChange={(e) => setParsedPreview({ ...parsedPreview, paymentMethod: e.target.value as FinancePaymentMethod })}
                      className="w-full p-1.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-xs font-bold text-sky-700 dark:text-sky-300"
                    >
                      <option value="card">{t('paymentMethodCard', '💳 کارت بانکی (Card)')}</option>
                      <option value="swish">{t('paymentMethodSwish', '📱 سوئیش (Swish)')}</option>
                      <option value="cash">{t('paymentMethodCash', '💵 نقدی (Cash)')}</option>
                      <option value="transfer">{t('paymentMethodTransfer', '🏦 انتقال بانکی (Transfer)')}</option>
                    </select>
                  </div>

                  {/* Selectable Card / Bank Account */}
                  <div className="p-3 bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 space-y-1">
                    <label className="text-gray-400 block text-[10px] font-bold">{t('financeAiCardLabel', 'حساب / کارت بانکی:')}</label>
                    <select
                      value={parsedPreview.cardAccount || userCards[0] || 'Debit Card'}
                      onChange={(e) => setParsedPreview({ ...parsedPreview, cardAccount: e.target.value })}
                      className="w-full p-1.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-xs font-bold text-indigo-700 dark:text-indigo-300"
                    >
                      {userCards.map(card => (
                        <option key={card} value={card}>{card}</option>
                      ))}
                    </select>
                  </div>

                  {/* Date Input */}
                  <div className="p-3 bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 space-y-1">
                    <label className="text-gray-400 block text-[10px] font-bold">{t('financeAiDateLabel', 'تاریخ تراکنش:')}</label>
                    <input
                      type="date"
                      value={parsedPreview.date || new Date().toISOString().split('T')[0]}
                      onChange={(e) => setParsedPreview({ ...parsedPreview, date: e.target.value })}
                      className="w-full p-1.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-xs font-sans tabular-nums font-bold text-gray-900 dark:text-white"
                    />
                  </div>
                </div>

                {/* Editable Items Table */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <h4 className="text-xs font-extrabold text-gray-800 dark:text-gray-200 flex items-center gap-2">
                      <span>{t('financeAiExtractedItems', 'اقلام و کالاهای استخراج شده (قابلیت ویرایش و تغییر قیمت):')}</span>
                      <span className="text-[11px] font-sans tabular-nums font-bold text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-950 px-2.5 py-0.5 rounded-full">
                        {formatNumberByLang((parsedPreview.items || []).length, lang)} {t('itemUnitRows', 'ردیف')}
                      </span>
                    </h4>
                    <button
                      type="button"
                      onClick={() => {
                        const newItems = [...(parsedPreview.items || [])];
                        newItems.push({
                          id: `item_${Date.now()}`,
                          name: t('newItemPlaceholder', 'کالای جدید'),
                          category: 'groceries',
                          quantity: 1,
                          unitPrice: 0,
                          totalPrice: 0
                        });
                        setParsedPreview({ ...parsedPreview, items: newItems });
                      }}
                      className="px-3.5 py-1.5 bg-indigo-50 dark:bg-indigo-950 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800 rounded-xl text-xs font-bold hover:bg-indigo-100 dark:hover:bg-indigo-900 transition-all cursor-pointer flex items-center gap-1.5"
                    >
                      <Plus className="w-3.5 h-3.5" />
                      <span>{t('addNewRowBtn', 'افزودن ردیف جدید')}</span>
                    </button>
                  </div>

                  <div className="overflow-x-auto bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 p-3">
                    <table className="w-full text-right text-xs">
                      <thead>
                        <tr className="border-b border-gray-200 dark:border-gray-800 text-gray-500 font-bold">
                          <th className="pb-2 font-bold">{t('financeAiColItemName', 'نام کالا (قابل ویرایش)')}</th>
                          <th className="pb-2 font-bold w-28">{t('financeAiColCategory', 'دسته‌بندی')}</th>
                          <th className="pb-2 font-bold w-20">{t('financeAiColQty', 'تعداد')}</th>
                          <th className="pb-2 font-bold w-24">{t('financeAiColUnitPrice', 'قیمت واحد')}</th>
                          <th className="pb-2 font-bold w-28">{t('financeAiColTotal', 'مجموع (بعد تخفیف)')}</th>
                          <th className="pb-2 font-bold w-12 text-center">{t('delete', 'حذف')}</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                        {(parsedPreview.items || []).map((item, i) => (
                          <tr key={i} className="hover:bg-gray-50/80 dark:hover:bg-gray-800/50">
                            <td className="py-2 pl-2">
                              <input
                                type="text"
                                value={item.name}
                                onChange={(e) => {
                                  const updated = [...(parsedPreview.items || [])];
                                  updated[i].name = e.target.value;
                                  setParsedPreview({ ...parsedPreview, items: updated });
                                }}
                                className="w-full p-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-xs font-bold text-gray-900 dark:text-white"
                              />
                            </td>
                            <td className="py-2 pl-2">
                              <input
                                type="text"
                                value={item.category || 'groceries'}
                                onChange={(e) => {
                                  const updated = [...(parsedPreview.items || [])];
                                  updated[i].category = e.target.value;
                                  setParsedPreview({ ...parsedPreview, items: updated });
                                }}
                                className="w-28 p-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-xs text-gray-700 dark:text-gray-300"
                              />
                            </td>
                            <td className="py-2 pl-2">
                              <input
                                type="number"
                                step="any"
                                value={item.quantity}
                                onChange={(e) => {
                                  const updated = [...(parsedPreview.items || [])];
                                  const q = parseFloat(e.target.value) || 0;
                                  updated[i].quantity = q;
                                  updated[i].totalPrice = Math.round(q * (updated[i].unitPrice || 0) * 100) / 100;
                                  setParsedPreview({ ...parsedPreview, items: updated });
                                }}
                                className="w-full p-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-xs font-sans tabular-nums font-bold text-gray-900 dark:text-white"
                              />
                            </td>
                            <td className="py-2 pl-2">
                              <input
                                type="number"
                                step="any"
                                value={item.unitPrice}
                                onChange={(e) => {
                                  const updated = [...(parsedPreview.items || [])];
                                  const u = parseFloat(e.target.value) || 0;
                                  updated[i].unitPrice = u;
                                  updated[i].totalPrice = Math.round(u * (updated[i].quantity || 1) * 100) / 100;
                                  setParsedPreview({ ...parsedPreview, items: updated });
                                }}
                                className="w-full p-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-xs font-sans tabular-nums text-gray-900 dark:text-white"
                              />
                            </td>
                            <td className="py-2 pl-2">
                              <input
                                type="number"
                                step="any"
                                value={item.totalPrice}
                                onChange={(e) => {
                                  const updated = [...(parsedPreview.items || [])];
                                  const t = parseFloat(e.target.value) || 0;
                                  updated[i].totalPrice = t;
                                  setParsedPreview({ ...parsedPreview, items: updated });
                                }}
                                className="w-full p-2 bg-emerald-50 dark:bg-emerald-950/60 border border-emerald-200 dark:border-emerald-800 rounded-xl text-xs font-sans tabular-nums font-extrabold text-emerald-700 dark:text-emerald-300"
                              />
                            </td>
                            <td className="py-2 text-center">
                              <button
                                type="button"
                                onClick={() => {
                                  const updated = (parsedPreview.items || []).filter((_, idx) => idx !== i);
                                  setParsedPreview({ ...parsedPreview, items: updated });
                                }}
                                className="text-rose-500 hover:text-rose-700 font-bold cursor-pointer p-1.5 rounded-lg hover:bg-rose-50 dark:hover:bg-rose-950"
                                title={t('deleteItemTitle', 'حذف کالا')}
                              >
                                ✕
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="flex items-center justify-between flex-wrap gap-2 pt-2 border-t border-gray-100 dark:border-gray-800">
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {t('finalAmountToRecord', 'مبلغ نهایی ثبت در حساب:')} <strong className="text-emerald-600 dark:text-emerald-400 font-sans tabular-nums font-extrabold text-sm">{formatNumberByLang(calculatedTotal.toLocaleString(), lang)} {parsedPreview.currency}</strong>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setParsedPreview(null)}
                      className="px-4 py-2 bg-gray-200 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-xl text-xs font-bold hover:bg-gray-300 dark:hover:bg-gray-700 cursor-pointer"
                    >
                      {t('cancel', 'انصراف')}
                    </button>
                    <button
                      onClick={handleConfirmSaveAiInvoice}
                      className="px-6 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold transition-all shadow-md flex items-center gap-1.5 cursor-pointer"
                    >
                      <ShieldCheck className="w-4 h-4" />
                      <span>{t('financeAiConfirmSave', 'تایید و ذخیره نهایی فاکتور')}</span>
                    </button>
                  </div>
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {/* TAB 4: PRODUCT CONSUMPTION ANALYTICS */}
      {activeTab === 'products' && (
        <div className="space-y-6">
          <div className="p-4 bg-white dark:bg-gray-900 rounded-3xl border border-gray-100 dark:border-gray-800 shadow-sm space-y-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <ShoppingBag className="w-5 h-5 text-indigo-600" />
                <h2 className="text-sm font-bold text-gray-900 dark:text-white">
                  {t('financeProductAnalyticsTitle', 'تحلیل و گزارش مصرف کالاها (مثال: میزان خرید شیر، نان...)')}
                </h2>
              </div>
              <span className="text-xs text-gray-400">
                {t('totalProductsCount', 'مجموع کالاها: {{count}} نوع کالا', { count: formatNumberByLang(productAnalytics.allProducts.length, lang) })}
              </span>
            </div>

            <div className="relative">
              <Search className="absolute right-3.5 top-3 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder={t('financeSearchProductPh', 'جستجوی نام کالا (مثلا: شیر، نان، گوشت...)')}
                value={productSearchTerm}
                onChange={(e) => setProductSearchTerm(e.target.value)}
                className="w-full pl-4 pr-10 py-2.5 bg-gray-50 dark:bg-gray-800 rounded-2xl text-xs border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white"
              />
            </div>

            {productAnalytics.filtered.length === 0 ? (
              <div className="py-10 text-center text-gray-400 text-xs">
                {t('financeNoProductMatches', 'کالایی با این عنوان ثبت نشده است.')}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 pt-2">
                {productAnalytics.filtered.map((prod, idx) => (
                  <div
                    key={idx}
                    className="p-3.5 bg-gray-50 dark:bg-gray-800/60 rounded-2xl border border-gray-100 dark:border-gray-800 space-y-2"
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-bold text-xs text-gray-900 dark:text-white">
                        {prod.name}
                      </span>
                      <span className="text-[10px] text-gray-400 bg-gray-200 dark:bg-gray-700 px-2 py-0.5 rounded-full">
                        {prod.category}
                      </span>
                    </div>

                    <div className="flex items-center justify-between text-xs font-sans tabular-nums pt-1">
                      <div>
                        <span className="text-[10px] text-gray-400 block font-sans">{t('financeTotalPurchasedQty', 'کل حجم خرید:')}</span>
                        <span className="font-extrabold text-indigo-600 dark:text-indigo-400">
                          {formatNumberByLang(prod.totalQty, lang)} {t('itemsUnitCount', 'عدد')}
                        </span>
                      </div>
                      <div className="text-left">
                        <span className="text-[10px] text-gray-400 block font-sans">{t('financeTotalSpentOnProd', 'کل هزینه:')}</span>
                        <span className="font-extrabold text-rose-600 dark:text-rose-400">
                          {formatNumberByLang(prod.totalSpent.toLocaleString(), lang)} {budgetConfig.currency}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* TAB 5: FINANCIAL GOALS */}
      {activeTab === 'goals' && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Target className="w-5 h-5 text-amber-500" />
              <h2 className="text-base font-bold text-gray-900 dark:text-white">
                {t('savingsGoalsAndPlanning', 'اهداف پس‌انداز و برنامه‌ریزی مالی')}
              </h2>
            </div>
            <button
              onClick={openNewGoalModal}
              className="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-xl text-xs font-bold shadow transition-all flex items-center gap-1.5 cursor-pointer"
            >
              <Plus className="w-4 h-4" />
              <span>{t('financeDefineNewGoalBtn', 'تعریف هدف جدید')}</span>
            </button>
          </div>

          {goals.length === 0 ? (
            <div className="py-12 bg-white dark:bg-gray-900 rounded-3xl border border-gray-100 dark:border-gray-800 text-center text-gray-400 space-y-2">
              <Target className="w-10 h-10 mx-auto opacity-30" />
              <p className="text-xs">{t('noGoalsYet', 'هنوز هدفی برای پس‌انداز ثبت نشده است.')}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {goals.map((goal) => {
                const progress = Math.min(100, Math.round((goal.currentSavings / goal.targetAmount) * 100)) || 0;
                const catMeta = getGoalCategoryMeta(goal.category);
                const CatIcon = catMeta.icon;

                return (
                  <div
                    key={goal.id}
                    className="p-5 bg-white dark:bg-gray-900 rounded-3xl border border-gray-100 dark:border-gray-800 shadow-sm space-y-4 hover:border-amber-200 dark:hover:border-amber-800/60 transition-all"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-start gap-2.5">
                        <div className={`p-2.5 rounded-2xl ${catMeta.color} shrink-0 mt-0.5`}>
                          <CatIcon className="w-5 h-5" />
                        </div>
                        <div className="space-y-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="text-xs font-bold text-gray-900 dark:text-white">
                              {goal.title}
                            </h3>
                            <span className="text-[10px] px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 font-medium">
                              {catMeta.name}
                            </span>
                          </div>
                          {goal.targetDate && (
                            <p className="text-[10px] text-gray-400 font-sans tabular-nums font-bold">
                              {t('financeGoalTargetDateLabel', 'تاریخ هدف: {{date}}', { date: formatNumberByLang(goal.targetDate, lang) })}
                            </p>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-1 shrink-0">
                        <button
                          type="button"
                          onClick={() => openEditGoalModal(goal)}
                          className="p-1.5 text-gray-400 hover:text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-950/50 rounded-lg transition-colors cursor-pointer"
                          title={t('financeEditGoalBtn', 'ویرایش هدف')}
                        >
                          <Edit3 className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => deleteFinancialGoal(goal.id)}
                          className="p-1.5 text-gray-400 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/50 rounded-lg transition-colors cursor-pointer"
                          title={t('delete', 'حذف')}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    {goal.notes && (
                      <p className="text-[11px] text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-800/60 p-2.5 rounded-xl border border-gray-100 dark:border-gray-800 leading-relaxed">
                        {goal.notes}
                      </p>
                    )}

                    <div className="space-y-1.5">
                      <div className="flex justify-between text-xs font-sans tabular-nums">
                        <span className="text-gray-500">
                          {t('goalCurrentLabel', 'موجود:')}{' '}
                          <strong className="text-emerald-600 dark:text-emerald-400 font-extrabold">
                            {formatNumberByLang(goal.currentSavings.toLocaleString(), lang)} {goal.currency}
                          </strong>
                        </span>
                        <span className="font-bold text-gray-800 dark:text-gray-200">
                          {t('goalTargetLabel', 'هدف:')}{' '}
                          {formatNumberByLang(goal.targetAmount.toLocaleString(), lang)} {goal.currency}
                        </span>
                      </div>

                      <div className="w-full h-3 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden p-0.5">
                        <div
                          className="h-full bg-gradient-to-r from-amber-500 to-emerald-500 rounded-full transition-all duration-500"
                          style={{ width: `${progress}%` }}
                        />
                      </div>

                      <div className="flex justify-between items-center text-[10px] text-gray-400">
                        <span className="font-extrabold text-amber-600 dark:text-amber-400 font-sans tabular-nums">
                          {formatNumberByLang(progress, lang)}% {t('goalCompletedSuffix', 'تکمیل شده')}
                        </span>
                        <span>
                          {t('goalRemainingLabel', 'باقی‌مانده:')}{' '}
                          <strong className="font-sans tabular-nums font-extrabold text-gray-600 dark:text-gray-300">
                            {formatNumberByLang(Math.max(0, goal.targetAmount - goal.currentSavings).toLocaleString(), lang)} {goal.currency}
                          </strong>
                        </span>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-2 pt-1">
                      <button
                        type="button"
                        onClick={() => {
                          setShowDepositModal(goal);
                          setDepositAmount('');
                        }}
                        className="py-2 px-3 bg-amber-500 hover:bg-amber-600 text-white rounded-xl text-xs font-bold transition-all cursor-pointer flex items-center justify-center gap-1.5 shadow-sm"
                      >
                        <Plus className="w-3.5 h-3.5" />
                        <span>{t('increaseSavingsBtn', 'افزایش پس‌انداز')}</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => openEditGoalModal(goal)}
                        className="py-2 px-3 bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl text-xs font-bold transition-all cursor-pointer flex items-center justify-center gap-1.5"
                      >
                        <Edit3 className="w-3.5 h-3.5" />
                        <span>{t('financeEditGoalBtn', 'ویرایش هدف')}</span>
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* TAB 7: AI FINANCIAL ADVISOR & CONSULTANT */}
      {activeTab === 'advisor' && (
        <div className="space-y-6">
          {/* Executive Header Banner */}
          <div className="p-6 bg-gradient-to-r from-slate-900 via-teal-950 to-indigo-950 text-white rounded-3xl shadow-xl space-y-4 border border-teal-800/40 relative overflow-hidden">
            <div className="absolute -left-10 -bottom-10 w-48 h-48 bg-teal-500/10 rounded-full blur-3xl pointer-events-none" />
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 relative z-10">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-2xl shadow-lg text-white shrink-0">
                  <Brain className="w-7 h-7" />
                </div>
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <h2 className="text-lg font-extrabold text-white">
                      {t('financeAiAdvisorTitle', 'مشاور هوشمند مالی و استراتژیست ثروت')}
                    </h2>
                  </div>
                  <p className="text-xs text-slate-300 mt-1">
                    {t('advisorHeaderDesc', 'تحلیل زنده درآمد خالص، رفتار هزینه‌ای، سقف بودجه و اهداف مالی برای ارائه توصیه‌های راهبردی')}
                  </p>
                </div>
              </div>

              {/* Financial Health Score Gauge Pill */}
              {(() => {
                const income = budgetConfig.monthlyIncomeAfterTax;
                const expense = monthBudgetStats.monthlyExpenses;
                const savingsRatio = income > 0 ? Math.round(((income - expense) / income) * 100) : 0;
                let healthScore = 50;
                if (savingsRatio >= 30) healthScore += 35;
                else if (savingsRatio >= 15) healthScore += 20;
                else if (savingsRatio > 0) healthScore += 10;

                if (monthBudgetStats.remainingExpenseBudget >= 0) healthScore += 15;
                
                return (
                  <div className="flex items-center gap-3 shrink-0 flex-wrap">
                    <div className="flex items-center gap-3 bg-white/10 backdrop-blur-md px-4 py-2.5 rounded-2xl border border-white/10">
                      <Activity className="w-5 h-5 text-teal-400" />
                      <div>
                        <span className="text-[10px] text-slate-300 block">{t('financeFinancialHealthScore', 'شاخص سلامت مالی شما:')}</span>
                        <div className="flex items-center gap-1.5 font-sans tabular-nums">
                          <span className="text-base font-black text-amber-400">{formatNumberByLang(healthScore, lang)}/{formatNumberByLang(100, lang)}</span>
                          <span className={`text-[10px] font-sans font-bold px-2 py-0.5 rounded-md ${
                            healthScore >= 80 ? 'bg-emerald-500/30 text-emerald-300' :
                            healthScore >= 60 ? 'bg-amber-500/30 text-amber-300' : 'bg-rose-500/30 text-rose-300'
                          }`}>
                            {healthScore >= 80 ? t('financeScoreExcellent', 'عالی 🟢') : healthScore >= 60 ? t('financeScoreModerate', 'متوسط 🟡') : t('financeScoreNeedsWork', 'نیازمند اصلاح 🔴')}
                          </span>
                        </div>
                      </div>
                    </div>

                    <button
                      onClick={async () => {
                        if (window.confirm(t('confirmClearAdvisorHistory', 'آیا از پاکسازی و شروع مجدد تاریخچه گفتگو با مشاور مالی اطمینان دارید؟'))) {
                          const resetMessages = [{
                            id: `welcome_${Date.now()}`,
                            role: 'model' as const,
                            text: t('advisorWelcomeText', `سلام و درود! من **مشاور ارشد هوشمند مالی و استراتژیست ثروت** شما هستم. 💼🌟\n\nمن آگاهی کامل و لحظه‌ای به **درآمد خالص، هزینه‌ها، سقف بودجه، رادار مصرف اقلام و اهداف مالی فعال شما** دارم. می‌توانید سوالات تخصصی خود را بپرسید.`),
                            time: new Date().toLocaleTimeString('fa-IR', { hour: '2-digit', minute: '2-digit' })
                          }];
                          setAdvisorMessages(resetMessages);
                          await saveAdvisorChatsToCloudD1(resetMessages);
                        }
                      }}
                      className="px-3 py-2.5 bg-rose-500/20 hover:bg-rose-500/30 border border-rose-500/30 text-rose-200 rounded-2xl text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5"
                      title={t('clearChatHistoryTitle', 'پاکسازی تاریخچه گفتگو')}
                    >
                      <Trash2 className="w-4 h-4 text-rose-400" />
                      <span>{t('clearAndStartChat', 'پاکسازی و شروع گفتگو')}</span>
                    </button>
                  </div>
                );
              })()}
            </div>

            {/* Live Financial Context Chips Bar */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 pt-2 border-t border-white/10 text-xs font-mono">
              <div className="p-2.5 bg-white/5 rounded-2xl border border-white/5 space-y-0.5">
                <span className="text-[10px] text-slate-400 block font-sans">{t('netMonthlyIncomeHeader', 'درآمد خالص ماهانه:')}</span>
                <span className="font-extrabold text-emerald-400 text-sm">
                  {formatNumberByLang(budgetConfig.monthlyIncomeAfterTax.toLocaleString(), lang)} {budgetConfig.currency}
                </span>
              </div>

              <div className="p-2.5 bg-white/5 rounded-2xl border border-white/5 space-y-0.5">
                <span className="text-[10px] text-slate-400 block font-sans">{t('recordedExpensesThisMonth', 'هزینه‌های ثبت‌شده این ماه:')}</span>
                <span className="font-extrabold text-rose-400 text-sm">
                  {formatNumberByLang(monthBudgetStats.monthlyExpenses.toLocaleString(), lang)} {budgetConfig.currency}
                </span>
              </div>

              <div className="p-2.5 bg-white/5 rounded-2xl border border-white/5 space-y-0.5">
                <span className="text-[10px] text-slate-400 block font-sans">{t('monthlySavingPower', 'قدرت پس‌انداز ماهانه:')}</span>
                <span className="font-extrabold text-teal-300 text-sm">
                  {formatNumberByLang(Math.max(0, budgetConfig.monthlyIncomeAfterTax - monthBudgetStats.monthlyExpenses).toLocaleString(), lang)} {budgetConfig.currency}
                </span>
              </div>

              <div className="p-2.5 bg-white/5 rounded-2xl border border-white/5 space-y-0.5">
                <span className="text-[10px] text-slate-400 block font-sans">{t('remainingBudgetCapHeader', 'مانده سقف بودجه:')}</span>
                <span className={`font-extrabold text-sm ${monthBudgetStats.remainingExpenseBudget >= 0 ? 'text-indigo-300' : 'text-rose-400'}`}>
                  {formatNumberByLang(monthBudgetStats.remainingExpenseBudget.toLocaleString(), lang)} {budgetConfig.currency}
                </span>
              </div>
            </div>
          </div>

          {/* Quick Prompts Bar */}
          <div className="space-y-2">
            <div className="flex items-center gap-1.5 text-xs font-bold text-gray-700 dark:text-gray-300">
              <Lightbulb className="w-4 h-4 text-amber-500" />
              <span>{t('smartPromptSuggestions', 'پیشنهادات هوشمند جهت پرسش سریع از مشاور مالی:')}</span>
            </div>
            <div className="flex items-center gap-2 overflow-x-auto pb-1">
              {[
                { id: '1', label: t('financePrompt1', '💡 چطور پس‌اندازم را ۱۰٪ بیشتر کنم؟'), prompt: 'چطور می‌تونم پس‌انداز ماهانه‌ام رو ۱۰ درصد بیشتر کنم؟' },
                { id: '2', label: t('financePrompt2', '📊 تحلیل جامع رفتار خرج‌کرد این ماه'), prompt: 'لطفاً وضعیت هزینه‌ها، سقف بودجه و رفتار مصرفی این ماه من رو تحلیل دقیق کن.' },
                { id: '3', label: t('financePrompt3', '📈 پیشنهاد سرمایه‌گذاری با مانده بودجه'), prompt: 'با توجه به درآمد و هزینه‌های فعلی من، مانده پس‌اندازم رو چطور در بازارهای مالی سرمایه‌گذاری کنم؟' },
                { id: '4', label: t('financePrompt4', '🛡️ استراتژی ساخت صندوق اضطراری'), prompt: 'برای ساخت صندوق اضطراری با توجه به هزینه‌های ماهانه من چه برنامه‌ای داری؟' },
                { id: '5', label: t('financePrompt5', '🎯 برنامه شتاب‌دهی رسیدن به اهداف مالی'), prompt: 'اهداف مالی ثبت‌شده من رو بررسی کن و بگو چطور می‌تونم سریع‌تر به اون‌ها برسم؟' }
              ].map(item => (
                <button
                  key={item.id}
                  onClick={() => handleSendAdvisorQuestion(item.prompt)}
                  disabled={advisorLoading}
                  className="px-3.5 py-2 rounded-2xl bg-white dark:bg-gray-900 border border-teal-200 dark:border-teal-900 hover:border-teal-500 text-teal-800 dark:text-teal-300 text-xs font-bold transition-all cursor-pointer shrink-0 shadow-2xs hover:shadow-sm disabled:opacity-50"
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>

          {/* Main Chat Conversation Container */}
          <div className="bg-white dark:bg-gray-900 rounded-3xl border border-gray-100 dark:border-gray-800 shadow-sm flex flex-col h-[580px] overflow-hidden">
            {/* Chat Messages Stream */}
            <div className="flex-1 p-4 md:p-6 overflow-y-auto space-y-4 bg-slate-50/50 dark:bg-slate-950/40">
              {advisorMessages.map(msg => (
                <div
                  key={msg.id}
                  className={`flex gap-3 max-w-3xl ${msg.role === 'user' ? 'mr-auto flex-row-reverse' : 'ml-auto'}`}
                >
                  <div className={`w-9 h-9 rounded-2xl flex items-center justify-center shrink-0 shadow-sm ${
                    msg.role === 'user'
                      ? 'bg-indigo-600 text-white'
                      : 'bg-gradient-to-br from-teal-600 to-emerald-600 text-white'
                  }`}>
                    {msg.role === 'user' ? <Users className="w-5 h-5" /> : <Bot className="w-5 h-5" />}
                  </div>

                  <div className={`space-y-1.5 flex-1 min-w-0 ${msg.role === 'user' ? 'text-left' : 'text-right'}`}>
                    <div className="flex items-center gap-2 justify-between">
                      <span className="text-[11px] font-bold text-gray-500 dark:text-gray-400">
                        {msg.role === 'user' ? t('you', 'شما') : t('aiFinancialAdvisor', 'مشاور هوشمند مالی')}
                      </span>
                      <span className="text-[10px] text-gray-400 font-mono">{msg.time}</span>
                    </div>

                    <div className={`p-4 sm:p-5 rounded-3xl text-xs leading-relaxed shadow-xs ${
                      msg.role === 'user'
                        ? 'bg-indigo-600 text-white rounded-tr-none whitespace-pre-wrap'
                        : 'bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border border-gray-100 dark:border-gray-700/80 rounded-tl-none font-sans'
                    }`}>
                      {msg.role === 'user' ? (
                        msg.text
                      ) : (
                        <ReactMarkdown
                          remarkPlugins={[remarkGfm]}
                          components={advisorMarkdownComponents}
                        >
                          {msg.text}
                        </ReactMarkdown>
                      )}
                    </div>

                    {msg.role === 'model' && (
                      <div className="flex items-center gap-2 pt-1">
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(msg.text);
                            setCopiedMsgId(msg.id);
                            setTimeout(() => setCopiedMsgId(null), 2000);
                          }}
                          className="text-[10px] text-gray-400 hover:text-teal-600 dark:hover:text-teal-400 flex items-center gap-1 cursor-pointer transition-colors"
                        >
                          {copiedMsgId === msg.id ? <Check className="w-3 h-3 text-emerald-500" /> : <Copy className="w-3 h-3" />}
                          <span>{copiedMsgId === msg.id ? t('financeCopied', 'کپی شد') : t('financeCopyAnswer', 'کپی پاسخ')}</span>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}

              {advisorLoading && (
                <div className="flex gap-3 max-w-xl ml-auto">
                  <div className="w-9 h-9 rounded-2xl bg-gradient-to-br from-teal-600 to-emerald-600 text-white flex items-center justify-center shrink-0 shadow-sm animate-pulse">
                    <Brain className="w-5 h-5" />
                  </div>
                  <div className="p-4 bg-white dark:bg-gray-800 rounded-3xl rounded-tl-none border border-teal-100 dark:border-teal-900 text-xs text-teal-700 dark:text-teal-300 flex items-center gap-2">
                    <RefreshCw className="w-4 h-4 animate-spin text-teal-600" />
                    <span>{t('financeAdvisorThinking', 'مشاور هوشمند در حال تحلیل فاکتورها، بودجه و تدوین پاسخ تخصصی شماست...')}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Input Bar */}
            <div className="p-3 bg-white dark:bg-gray-900 border-t border-gray-100 dark:border-gray-800 flex items-center gap-2">
              <input
                type="text"
                placeholder={t('financeAskPh', 'سوال مالی خود را بنویسید (مثال: چطور هزینه‌های سوپرمارکت این ماه رو کنترل کنم؟)...')}
                value={advisorInput}
                onChange={(e) => setAdvisorInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSendAdvisorQuestion();
                  }
                }}
                disabled={advisorLoading}
                className="flex-1 p-3 bg-gray-50 dark:bg-gray-800 rounded-2xl text-xs text-gray-900 dark:text-white border border-gray-200 dark:border-gray-700 focus:outline-none focus:ring-2 focus:ring-teal-500 font-sans"
              />

              <VoiceInputButton
                currentValue={advisorInput}
                onUpdate={(val) => setAdvisorInput(val)}
                disabled={advisorLoading}
                title={t('voiceInputNormal', 'ورودی صوتی')}
              />

              <button
                onClick={() => handleSendAdvisorQuestion()}
                disabled={!advisorInput.trim() || advisorLoading}
                className="px-5 py-3 bg-gradient-to-r from-teal-600 to-emerald-600 hover:from-teal-700 hover:to-emerald-700 text-white rounded-2xl text-xs font-bold transition-all shadow-md cursor-pointer disabled:opacity-50 flex items-center gap-1.5 shrink-0"
              >
                <Send className="w-4 h-4 rotate-180" />
                <span>{t('financeSend', 'ارسال')}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL 1: ADD TRANSACTION / INVOICE */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-900 rounded-3xl border border-gray-200 dark:border-gray-800 p-6 w-full max-w-xl shadow-2xl space-y-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b border-gray-100 dark:border-gray-800 pb-3">
              <h3 className="text-sm font-extrabold text-gray-900 dark:text-white">
                {editingTransaction ? t('financeEditTx', 'ویرایش تراکنش') : t('financeNewTx', 'ثبت فاکتور یا تراکنش مالی جدید')}
              </h3>
              <button
                onClick={() => setShowAddModal(false)}
                className="text-gray-400 hover:text-gray-600 text-sm cursor-pointer"
              >
                ✕
              </button>
            </div>

            {/* Live Remaining Budget Banner */}
            <div className="p-3 bg-indigo-50 dark:bg-indigo-950/40 rounded-2xl border border-indigo-100 dark:border-indigo-900/40 text-xs flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Calculator className="w-4 h-4 text-indigo-600" />
                <span className="text-gray-700 dark:text-gray-300">
                  {t('monthlyBudgetRemainingLabel', 'مانده بودجه هزینه ماه:')} <strong>{formatNumberByLang(monthBudgetStats.remainingExpenseBudget.toLocaleString(), lang)} {budgetConfig.currency}</strong>
                </span>
              </div>
              {txType === 'expense' && (parseFloat(txAmount) || 0) > 0 && (
                <span className="text-[10px] font-bold text-indigo-600 dark:text-indigo-400 font-sans tabular-nums">
                  {t('afterRecordingBudgetLabel', 'پس از ثبت:')} {formatNumberByLang((monthBudgetStats.remainingExpenseBudget - (parseFloat(txAmount) || 0)).toLocaleString(), lang)} {budgetConfig.currency}
                </span>
              )}
            </div>

            <form onSubmit={handleSaveTransaction} className="space-y-4 text-xs">
              {/* Type selector */}
              <div className="grid grid-cols-3 gap-2 bg-gray-100 dark:bg-gray-800 p-1 rounded-2xl text-center">
                <button
                  type="button"
                  onClick={() => setTxType('expense')}
                  className={`py-2 rounded-xl font-bold transition-all cursor-pointer ${
                    txType === 'expense' ? 'bg-rose-600 text-white shadow-sm' : 'text-gray-600 dark:text-gray-400'
                  }`}
                >
                  {t('financeTypeExpense', 'هزینه / فاکتور')}
                </button>
                <button
                  type="button"
                  onClick={() => setTxType('income')}
                  className={`py-2 rounded-xl font-bold transition-all cursor-pointer ${
                    txType === 'income' ? 'bg-emerald-600 text-white shadow-sm' : 'text-gray-600 dark:text-gray-400'
                  }`}
                >
                  {t('financeTypeIncome', 'درآمد / دریافتی')}
                </button>
                <button
                  type="button"
                  onClick={() => setTxType('savings')}
                  className={`py-2 rounded-xl font-bold transition-all cursor-pointer ${
                    txType === 'savings' ? 'bg-indigo-600 text-white shadow-sm' : 'text-gray-600 dark:text-gray-400'
                  }`}
                >
                  {t('financeTypeSavings', 'پس‌انداز')}
                </button>
              </div>

              {/* Title & Amount */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="font-bold text-gray-700 dark:text-gray-300">{t('financeStoreOrTitle', 'عنوان یا نام فروشگاه:')}</label>
                  <input
                    type="text"
                    required
                    placeholder={t('financeStoreOrTitlePh', 'مثال: خرید از Willys / حقوق ماهانه')}
                    value={txTitle}
                    onChange={(e) => setTxTitle(e.target.value)}
                    className="w-full p-2.5 bg-gray-50 dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white"
                  />
                </div>

                <div className="space-y-1">
                  <div className="flex items-center justify-between">
                    <label className="font-bold text-gray-700 dark:text-gray-300">{t('financeTotalAmountLabel', 'مبلغ کل (عدد):')}</label>
                    {txItems.length > 0 && (
                      <span className="text-[10px] bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300 px-2 py-0.5 rounded-full font-bold">
                        {t('financeCalcFromItems', 'محاسبه خودکار از اقلام ({{count}})', { count: formatNumberByLang(txItems.length, lang) })}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <input
                      type="number"
                      required
                      step="any"
                      placeholder="450"
                      value={txAmount}
                      disabled={txItems.length > 0}
                      onChange={(e) => setTxAmount(e.target.value)}
                      className={`w-full p-2.5 rounded-xl border text-gray-900 dark:text-white font-sans tabular-nums font-bold ${
                        txItems.length > 0
                          ? 'bg-gray-100 dark:bg-gray-800/80 cursor-not-allowed border-emerald-300 dark:border-emerald-700 opacity-90'
                          : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700'
                      }`}
                    />
                    <select
                      value={txCurrency}
                      onChange={(e) => setTxCurrency(e.target.value)}
                      className="p-2.5 bg-gray-50 dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white font-bold"
                    >
                      {CURRENCY_OPTIONS.map(c => (
                        <option key={c.code} value={c.code}>{t(c.nameKey, c.defaultLabel)}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Payment Method & Category */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="font-bold text-gray-700 dark:text-gray-300">{t('financePaymentMethodLabel', 'روش پرداخت:')}</label>
                  <select
                    value={txPaymentMethod}
                    onChange={(e) => setTxPaymentMethod(e.target.value as FinancePaymentMethod)}
                    className="w-full p-2.5 bg-gray-50 dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white font-bold"
                  >
                    <option value="swish">{t('filterSwish', 'سوئیش (Swish)')}</option>
                    <option value="cash">{t('filterCash', 'نقد (Cash)')}</option>
                    <option value="card">{t('filterCard', 'کارت بانکی')}</option>
                    <option value="transfer">{t('filterTransfer', 'انتقال حساب')}</option>
                  </select>
                </div>

                <div className="space-y-1">
                  <label className="font-bold text-gray-700 dark:text-gray-300">{t('financeCardOrAccountLabel', 'کارت / حساب پرداختی:')}</label>
                  <select
                    value={txCardAccount}
                    onChange={(e) => setTxCardAccount(e.target.value)}
                    className="w-full p-2.5 bg-gray-50 dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white font-bold text-indigo-700 dark:text-indigo-300"
                  >
                    {userCards.map(card => (
                      <option key={card} value={card}>{card}</option>
                    ))}
                    <option value="CUSTOM">{t('financeDefineNewCard', '➕ تعریف کارت جدید...')}</option>
                  </select>
                  {txCardAccount === 'CUSTOM' && (
                    <input
                      type="text"
                      placeholder={t('financeNewCardPh', 'نام کارت یا حساب پرداختی (مثال: کلارنا، مسترکارت...)')}
                      value={customCardInput}
                      onChange={(e) => setCustomCardInput(e.target.value)}
                      className="w-full mt-1.5 p-2 bg-white dark:bg-gray-900 rounded-xl border border-indigo-300 dark:border-indigo-700 text-xs font-bold"
                    />
                  )}
                </div>
              </div>

              {/* Category */}
              <div className="space-y-1">
                <label className="font-bold text-gray-700 dark:text-gray-300">{t('financeCategoryLabel', 'دسته‌بندی غنی:')}</label>
                <select
                  value={txCategory}
                  onChange={(e) => setTxCategory(e.target.value as FinanceCategory)}
                  className="w-full p-2.5 bg-gray-50 dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white font-bold"
                >
                  <option value="groceries">{t('catGroceries', '🛒 سوپرمارکت و خواربار')}</option>
                  <option value="housing">{t('catHousing', '🏠 مسکن و اجاره/رهن')}</option>
                  <option value="utilities">{t('catUtilities', '⚡ قبوض، انرژی و اینترنت')}</option>
                  <option value="transport">{t('catTransport', '🚗 حمل‌و‌نقل و سوخت')}</option>
                  <option value="restaurants">{t('catRestaurants', '🍽️ رستوران، کافه و غذا')}</option>
                  <option value="health">{t('catHealth', '🩺 سلامت، درمان و دارو')}</option>
                  <option value="clothing">{t('catClothing', '👗 پوشاک، کفش و زیبایی')}</option>
                  <option value="electronics">{t('catElectronics', '💻 کالای دیجیتال و الکترونیک')}</option>
                  <option value="home_decor">{t('catHomeDecor', '🛋️ لوازم خانگی و دکوراسیون')}</option>
                  <option value="car_maintenance">{t('catCarMaintenance', '🔧 تعمیرات خودرو و بیمه')}</option>
                  <option value="subscriptions">{t('catSubscriptions', '🌐 اشتراک‌های دیجیتال و AI')}</option>
                  <option value="pets">{t('catPets', '🐾 حیوانات خانگی و پت‌شاپ')}</option>
                  <option value="charity_gifts">{t('catCharityGifts', '🎁 هدایا، کادو و خیریه')}</option>
                  <option value="education">{t('catEducation', '🎓 آموزش و توسعه فردی')}</option>
                  <option value="entertainment">{t('catEntertainment', '🎉 تفریح، ورزش و سفر')}</option>
                  <option value="family">{t('catFamily', '👶 امور فرزندان و خانواده')}</option>
                  <option value="salary">{t('catSalary', '💼 حقوق و درآمد اصلی')}</option>
                  <option value="investment">{t('catInvestment', '📈 پس‌انداز و سرمایه‌گذاری')}</option>
                  <option value="other">{t('catOther', '🏷️ خدمات و متفرقه')}</option>
                </select>
              </div>

              {/* Date & Notes */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="font-bold text-gray-700 dark:text-gray-300">{t('financeDateLabel', 'تاریخ:')}</label>
                  <input
                    type="date"
                    value={txDate}
                    onChange={(e) => setTxDate(e.target.value)}
                    className="w-full p-2.5 bg-gray-50 dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white font-sans tabular-nums font-bold"
                  />
                </div>

                <div className="space-y-1">
                  <label className="font-bold text-gray-700 dark:text-gray-300">{t('financeDescOptional', 'توضیحات (اختیاری):')}</label>
                  <input
                    type="text"
                    placeholder={t('financeDescPh', 'مثال: خرید خواربار هفته دوم')}
                    value={txNotes}
                    onChange={(e) => setTxNotes(e.target.value)}
                    className="w-full p-2.5 bg-gray-50 dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
              </div>

              {/* Itemized Goods List in Invoice */}
              <div className="p-3 bg-gray-50 dark:bg-gray-950 rounded-2xl border border-gray-200 dark:border-gray-800 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="font-extrabold text-gray-800 dark:text-gray-200 flex items-center gap-1.5">
                    <ShoppingBag className="w-4 h-4 text-indigo-500" />
                    <span>{t('financeAddItemsSection', 'افزودن اقلام فاکتور (کالاهای داخل خرید):')}</span>
                  </span>
                  <span className="text-[10px] text-gray-400">
                    {formatNumberByLang(txItems.length, lang)} {t('itemsRecordedSuffix', 'کالا ثبت شده')}
                  </span>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <input
                    type="text"
                    placeholder={t('financeItemNamePh', 'نام کالا (مثال: شیر)')}
                    value={newItemName}
                    onChange={(e) => setNewItemName(e.target.value)}
                    className="p-2 bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 text-xs"
                  />
                  <input
                    type="number"
                    step="any"
                    placeholder={t('financeItemQtyPh', 'تعداد (۱)')}
                    value={newItemQty}
                    onChange={(e) => setNewItemQty(e.target.value)}
                    className="p-2 bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 text-xs font-sans tabular-nums font-bold"
                  />
                  <input
                    type="number"
                    step="any"
                    placeholder={t('financeItemUnitPricePh', 'قیمت واحد')}
                    value={newItemPrice}
                    onChange={(e) => setNewItemPrice(e.target.value)}
                    className="p-2 bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 text-xs font-sans tabular-nums font-bold"
                  />
                  <button
                    type="button"
                    onClick={handleAddItemToInvoice}
                    className="p-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-bold transition-all cursor-pointer"
                  >
                    {t('financeAddItemRow', '+ افزودن کالا')}
                  </button>
                </div>

                {txItems.length > 0 && (
                  <div className="space-y-1 max-h-32 overflow-y-auto">
                    {txItems.map(item => (
                      <div key={item.id} className="flex items-center justify-between p-2 bg-white dark:bg-gray-900 rounded-xl text-xs border border-gray-100 dark:border-gray-800">
                        <span className="font-bold">{item.name}</span>
                        <div className="flex items-center gap-2">
                          <span className="font-sans tabular-nums text-gray-500">{formatNumberByLang(item.quantity, lang)}x</span>
                          <span className="font-sans tabular-nums font-bold text-indigo-600">{formatNumberByLang(item.totalPrice, lang)} {txCurrency}</span>
                          <button
                            type="button"
                            onClick={() => handleRemoveItemFromInvoice(item.id)}
                            className="text-rose-500 hover:text-rose-700 cursor-pointer"
                          >
                            ✕
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Attachments & Invoices (Cloudflare R2 & D1 Persistence) */}
              <div className="p-3.5 bg-slate-50 dark:bg-gray-950 rounded-2xl border border-slate-200 dark:border-gray-800 space-y-3">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-2">
                    <Paperclip className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                    <span className="font-extrabold text-gray-900 dark:text-white text-xs">
                      {t('attachedFilesAndMedia', 'پیوست عکس فاکتور، رسید خرید و اسناد:')}
                    </span>
                  </div>
                  <span className="text-[10px] text-emerald-600 dark:text-emerald-400 font-bold bg-emerald-50 dark:bg-emerald-950/60 px-2 py-0.5 rounded-full border border-emerald-200 dark:border-emerald-800">
                    {t('cloudflareStorageBadge', 'ذخیره‌سازی پایدار در Cloudflare R2')}
                  </span>
                </div>

                {/* Hidden File Input */}
                <input
                  type="file"
                  ref={fileInputRef}
                  multiple
                  accept="image/*,.pdf,.doc,.docx,.ppt,.pptx"
                  className="hidden"
                  onChange={(e) => {
                    if (e.target.files) {
                      handleProcessFinanceFiles(e.target.files);
                      e.target.value = '';
                    }
                  }}
                />

                {/* AI Scan Live Status Feedback Banner */}
                {scanStatusMessage && (
                  <div
                    className={`p-2.5 rounded-xl border text-xs flex items-center justify-between gap-2 transition-all ${
                      scanStatusMessage.type === 'success'
                        ? 'bg-emerald-50 dark:bg-emerald-950/70 border-emerald-300 dark:border-emerald-700 text-emerald-800 dark:text-emerald-200'
                        : scanStatusMessage.type === 'info'
                        ? 'bg-indigo-50 dark:bg-indigo-950/70 border-indigo-300 dark:border-indigo-700 text-indigo-800 dark:text-indigo-200 animate-pulse'
                        : 'bg-rose-50 dark:bg-rose-950/70 border-rose-300 dark:border-rose-700 text-rose-800 dark:text-rose-200'
                    }`}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      {scanStatusMessage.type === 'info' && <Loader2 className="w-4 h-4 animate-spin shrink-0" />}
                      {scanStatusMessage.type === 'success' && <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />}
                      {scanStatusMessage.type === 'error' && <AlertCircle className="w-4 h-4 text-rose-600 shrink-0" />}
                      <span className="font-bold truncate">{scanStatusMessage.text}</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => setScanStatusMessage(null)}
                      className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 p-0.5 cursor-pointer"
                    >
                      ✕
                    </button>
                  </div>
                )}

                {/* Upload Action Buttons */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {/* File & Photo Attachment Button */}
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isUploadingAttachments}
                    className="p-2.5 bg-white dark:bg-gray-900 border border-dashed border-indigo-300 dark:border-indigo-700 hover:border-indigo-500 rounded-xl text-xs font-bold text-indigo-700 dark:text-indigo-300 transition-all flex items-center justify-center gap-2 cursor-pointer shadow-2xs disabled:opacity-50"
                  >
                    <FileUp className="w-4 h-4" />
                    <span>{t('attachDocOrImage', 'پیوست عکس فاکتور / سند')}</span>
                  </button>

                  {/* AI Smart Invoice Scanner from Attached Image / Text */}
                  <button
                    type="button"
                    onClick={() => handleScanInvoiceWithAi()}
                    disabled={isScanningInvoiceAi || (txAttachments.length === 0 && !txNotes.trim())}
                    className="p-2.5 bg-gradient-to-r from-amber-500 via-orange-500 to-indigo-600 hover:from-amber-600 hover:to-indigo-700 text-white rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-1.5 cursor-pointer shadow-md disabled:opacity-40"
                    title={t('aiScanTooltip', 'استخراج خودکار اقلام، نام فروشگاه و جمع کل از تصویر')}
                  >
                    {isScanningInvoiceAi ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                    <span>{isScanningInvoiceAi ? t('scanning', 'در حال اسکن با AI...') : t('scanInvoiceWithAi', 'اسکن هوشمند با AI')}</span>
                  </button>
                </div>

                {/* Uploading Status Progress */}
                {isUploadingAttachments && (
                  <div className="p-2.5 bg-indigo-50 dark:bg-indigo-950/60 rounded-xl border border-indigo-200 dark:border-indigo-800 flex items-center gap-2 text-xs text-indigo-700 dark:text-indigo-300 animate-pulse">
                    <Loader2 className="w-4 h-4 animate-spin shrink-0" />
                    <span>{uploadProgressText}</span>
                  </div>
                )}

                {/* Attached Files List */}
                {txAttachments.length > 0 ? (
                  <div className="space-y-1.5 max-h-40 overflow-y-auto pr-1">
                    {txAttachments.map((att, idx) => {
                      const isImg = att.type === 'image' || (att.fileUrl && att.fileUrl.startsWith('data:image/'));
                      const isPdf = att.type === 'pdf';

                      return (
                        <div
                          key={att.id || idx}
                          className="flex items-center justify-between p-2 bg-white dark:bg-gray-900 rounded-xl text-xs border border-gray-200 dark:border-gray-800 shadow-2xs gap-2"
                        >
                          <div className="flex items-center gap-2 min-w-0 flex-1">
                            {isImg && att.fileUrl ? (
                              <div className="w-8 h-8 rounded-lg overflow-hidden shrink-0 border border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                                <img
                                  src={att.fileUrl}
                                  alt={att.name}
                                  className="w-full h-full object-cover"
                                  referrerPolicy="no-referrer"
                                />
                              </div>
                            ) : isPdf ? (
                              <div className="w-8 h-8 rounded-lg bg-rose-100 dark:bg-rose-950/60 text-rose-600 flex items-center justify-center shrink-0">
                                <FileText className="w-4 h-4" />
                              </div>
                            ) : (
                              <div className="w-8 h-8 rounded-lg bg-indigo-100 dark:bg-indigo-950/60 text-indigo-600 flex items-center justify-center shrink-0">
                                <Paperclip className="w-4 h-4" />
                              </div>
                            )}

                            <div className="min-w-0 flex-1">
                              <span className="font-bold text-gray-900 dark:text-white truncate block">
                                {att.name}
                              </span>
                              <span className="text-[10px] text-gray-400 font-mono block">
                                {att.fileSize || 'فایل ابری'}
                              </span>
                            </div>
                          </div>

                          <div className="flex items-center gap-1 shrink-0">
                            {/* Preview Eye Button */}
                            <button
                              type="button"
                              onClick={() => setPreviewAttachmentModal(att)}
                              className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors cursor-pointer"
                              title={t('previewAttachment', 'مشاهده پیوست')}
                            >
                              <Eye className="w-3.5 h-3.5" />
                            </button>

                            {/* Scan with AI for individual image / doc */}
                            <button
                              type="button"
                              onClick={() => handleScanInvoiceWithAi(att)}
                              disabled={isScanningInvoiceAi}
                              className="p-1.5 text-amber-500 hover:text-amber-700 hover:bg-amber-50 dark:hover:bg-amber-950/60 rounded-lg transition-colors cursor-pointer"
                              title={t('scanThisWithAi', 'اسکن این فاکتور با AI')}
                            >
                              <Sparkles className="w-3.5 h-3.5" />
                            </button>

                            {/* Delete Attachment Button */}
                            <button
                              type="button"
                              onClick={() => handleDeleteAttachment(att.id || att.fileId || '')}
                              className="p-1.5 text-rose-400 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/60 rounded-lg transition-colors cursor-pointer"
                              title={t('deleteAttachment', 'حذف پیوست')}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-[11px] text-gray-400 text-center py-1">
                    {t('noAttachmentsYet', 'هنوز فایلی ضمیمه نشده است. می‌توانید عکس رسید خرید، فاکتور فروشگاه یا PDF را پیوست کنید.')}
                  </p>
                )}
              </div>

              <div className="flex items-center justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="px-4 py-2 bg-gray-200 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-xl font-bold cursor-pointer"
                >
                  {t('cancel', 'انصراف')}
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-bold cursor-pointer transition-all shadow-md flex items-center gap-1.5"
                >
                  <Check className="w-4 h-4" />
                  <span>{editingTransaction ? t('saveChangesBtn', 'ذخیره تغییرات') : t('saveTransactionBtn', 'ذخیره تراکنش')}</span>
                </button>
              </div>

            </form>
          </div>
        </div>
      )}

      {/* MODAL: ATTACHMENT PREVIEW / LIGHTBOX */}
      {previewAttachmentModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-md z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-900 rounded-3xl border border-gray-200 dark:border-gray-800 p-6 w-full max-w-2xl shadow-2xl space-y-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b border-gray-100 dark:border-gray-800 pb-3">
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <Paperclip className="w-4 h-4 text-indigo-600 shrink-0" />
                <h3 className="text-sm font-extrabold text-gray-900 dark:text-white truncate">
                  {previewAttachmentModal.name}
                </h3>
              </div>

              <div className="flex items-center gap-2 shrink-0">
                {previewAttachmentModal.fileUrl && previewAttachmentModal.type !== 'video' && (
                  <button
                    onClick={() => triggerFileDownload(previewAttachmentModal.fileUrl!, previewAttachmentModal.name)}
                    className="p-2 text-gray-500 hover:text-emerald-600 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition-colors cursor-pointer"
                    title={t('download', 'دانلود')}
                  >
                    <Download className="w-4 h-4" />
                  </button>
                )}
                {previewAttachmentModal.fileUrl && (
                  <a
                    href={previewAttachmentModal.fileUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="p-2 text-gray-500 hover:text-indigo-600 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition-colors cursor-pointer"
                    title={t('openInNewTab', 'باز کردن در تب جدید')}
                  >
                    <ExternalLink className="w-4 h-4" />
                  </a>
                )}
                <button
                  onClick={() => setPreviewAttachmentModal(null)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-sm cursor-pointer p-1"
                >
                  ✕
                </button>
              </div>
            </div>

            {/* Preview Content Body */}
            <div className="flex flex-col items-center justify-center min-h-[250px] bg-slate-50 dark:bg-gray-950 rounded-2xl p-4 border border-gray-100 dark:border-gray-800">
              {previewAttachmentModal.type === 'image' || (previewAttachmentModal.fileUrl && previewAttachmentModal.fileUrl.startsWith('data:image/')) ? (
                <div className="max-h-[60vh] overflow-auto flex items-center justify-center w-full">
                  <img
                    src={previewAttachmentModal.fileUrl}
                    alt={previewAttachmentModal.name}
                    className="max-h-[55vh] max-w-full object-contain rounded-xl shadow-md"
                    referrerPolicy="no-referrer"
                  />
                </div>
              ) : (
                <div className="text-center space-y-3 py-8">
                  <FileText className="w-12 h-12 text-indigo-500 mx-auto" />
                  <p className="text-sm font-bold text-gray-800 dark:text-gray-200">{previewAttachmentModal.name}</p>
                  <span className="text-xs text-gray-400 font-mono block">{previewAttachmentModal.fileSize || 'سند ابری'}</span>
                  {previewAttachmentModal.extractedText && (
                    <div className="mt-3 p-3 bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 text-right text-xs max-h-40 overflow-y-auto text-gray-700 dark:text-gray-300">
                      <span className="font-bold text-[10px] text-gray-400 block mb-1">متن استخراج‌شده (OCR):</span>
                      <p className="whitespace-pre-wrap">{previewAttachmentModal.extractedText}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* MODAL 2: ADD / EDIT GOAL */}
      {showGoalModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-900 rounded-3xl border border-gray-200 dark:border-gray-800 p-6 w-full max-w-md shadow-2xl space-y-4">
            <div className="flex items-center justify-between border-b border-gray-100 dark:border-gray-800 pb-3">
              <h3 className="text-sm font-extrabold text-gray-900 dark:text-white flex items-center gap-2">
                {editingGoal ? (
                  <>
                    <Edit3 className="w-4 h-4 text-amber-500" />
                    <span>{t('financeEditGoalModalTitle', 'ویرایش هدف پس‌انداز: {{title}}', { title: editingGoal.title })}</span>
                  </>
                ) : (
                  <>
                    <Target className="w-4 h-4 text-amber-500" />
                    <span>{t('newGoalModalTitle', 'تعریف هدف مالی و پس‌انداز جدید')}</span>
                  </>
                )}
              </h3>
              <button
                onClick={() => {
                  setShowGoalModal(false);
                  setEditingGoal(null);
                }}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-sm cursor-pointer p-1"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSaveGoal} className="space-y-3.5 text-xs">
              <div className="space-y-1">
                <label className="font-bold text-gray-700 dark:text-gray-300">{t('financeGoalNameLabel', 'عنوان هدف:')}</label>
                <input
                  type="text"
                  required
                  placeholder={t('financeGoalNamePh', 'مثال: خرید خانه در استکهلم / سفر تابستانی')}
                  value={goalTitle}
                  onChange={(e) => setGoalTitle(e.target.value)}
                  className="w-full p-2.5 bg-gray-50 dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="font-bold text-gray-700 dark:text-gray-300">{t('financeGoalTargetAmount', 'مبلغ هدف:')}</label>
                  <input
                    type="number"
                    required
                    step="any"
                    placeholder="350000"
                    value={goalTargetAmount}
                    onChange={(e) => setGoalTargetAmount(e.target.value)}
                    className="w-full p-2.5 bg-gray-50 dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white font-sans tabular-nums font-bold"
                  />
                </div>

                <div className="space-y-1">
                  <label className="font-bold text-gray-700 dark:text-gray-300">
                    {editingGoal ? t('goalCurrentSavedLabel', 'مبلغ پس‌انداز شده تاکنون:') : t('initialSavingsLabel', 'پس‌انداز اولیه:')}
                  </label>
                  <input
                    type="number"
                    step="any"
                    placeholder="90000"
                    value={goalCurrentSavings}
                    onChange={(e) => setGoalCurrentSavings(e.target.value)}
                    className="w-full p-2.5 bg-gray-50 dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white font-sans tabular-nums font-bold text-emerald-600 dark:text-emerald-400"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="font-bold text-gray-700 dark:text-gray-300">{t('financeGoalCurrency', 'ارز:')}</label>
                  <select
                    value={goalCurrency}
                    onChange={(e) => setGoalCurrency(e.target.value)}
                    className="w-full p-2.5 bg-gray-50 dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white font-bold"
                  >
                    {CURRENCY_OPTIONS.map(c => (
                      <option key={c.code} value={c.code}>{t(c.nameKey, c.defaultLabel)}</option>
                    ))}
                  </select>
                </div>

                <div className="space-y-1">
                  <label className="font-bold text-gray-700 dark:text-gray-300">{t('financeGoalTargetDate', 'تاریخ هدف (اختیاری):')}</label>
                  <input
                    type="date"
                    value={goalTargetDate}
                    onChange={(e) => setGoalTargetDate(e.target.value)}
                    className="w-full p-2.5 bg-gray-50 dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white font-sans tabular-nums font-bold"
                  />
                </div>
              </div>

              <div className="space-y-1">
                <label className="font-bold text-gray-700 dark:text-gray-300">{t('financeGoalCategoryLabel', 'دسته‌بندی هدف:')}</label>
                <select
                  value={goalCategory}
                  onChange={(e) => setGoalCategory(e.target.value as any)}
                  className="w-full p-2.5 bg-gray-50 dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white font-medium"
                >
                  <option value="housing">🏠 {t('financeGoalCategoryHousing', 'مسکن و ملک')}</option>
                  <option value="car">🚗 {t('financeGoalCategoryCar', 'خودرو و نقلیه')}</option>
                  <option value="travel">✈️ {t('financeGoalCategoryTravel', 'سفر و تفریح')}</option>
                  <option value="emergency">🛡️ {t('financeGoalCategoryEmergency', 'صندوق اضطراری')}</option>
                  <option value="investment">📈 {t('financeGoalCategoryInvestment', 'سرمایه‌گذاری و پس‌انداز')}</option>
                  <option value="other">🏛️ {t('financeGoalCategoryOther', 'سایر و متفرقه')}</option>
                </select>
              </div>

              <div className="space-y-1">
                <label className="font-bold text-gray-700 dark:text-gray-300">{t('financeGoalNotesLabel', 'یادداشت یا توضیحات:')}</label>
                <textarea
                  rows={2}
                  placeholder={t('financeDescPh', 'مثال: برنامه‌ریزی برای خرید آپارتمان در استکهلم...')}
                  value={goalNotes}
                  onChange={(e) => setGoalNotes(e.target.value)}
                  className="w-full p-2.5 bg-gray-50 dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white text-xs resize-none"
                />
              </div>

              <div className="flex items-center justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowGoalModal(false);
                    setEditingGoal(null);
                  }}
                  className="px-4 py-2 bg-gray-200 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-xl font-bold cursor-pointer"
                >
                  {t('cancel', 'انصراف')}
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-xl font-bold cursor-pointer transition-all shadow-md flex items-center gap-1.5"
                >
                  {editingGoal ? (
                    <>
                      <Check className="w-4 h-4" />
                      <span>{t('financeSaveGoalChangesBtn', 'ذخیره تغییرات هدف')}</span>
                    </>
                  ) : (
                    <>
                      <Plus className="w-4 h-4" />
                      <span>{t('createGoalBtn', 'ایجاد هدف')}</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL 3: DEPOSIT TO GOAL */}
      {showDepositModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-900 rounded-3xl border border-gray-200 dark:border-gray-800 p-6 w-full max-w-sm shadow-2xl space-y-4">
            <h3 className="text-sm font-extrabold text-gray-900 dark:text-white">
              {t('depositModalTitle', 'افزایش پس‌انداز: {{title}}', { title: showDepositModal.title })}
            </h3>

            <div className="space-y-2 text-xs">
              <label className="font-bold text-gray-700 dark:text-gray-300 block">
                {t('financeDepositAmountLabel', 'مبلغ واریزی جدید:')}
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  step="any"
                  required
                  placeholder="5000"
                  value={depositAmount}
                  onChange={(e) => setDepositAmount(e.target.value)}
                  className="w-full p-2.5 bg-gray-50 dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 font-sans tabular-nums font-bold text-gray-900 dark:text-white"
                />
                <span className="font-bold text-gray-500">{showDepositModal.currency}</span>
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 pt-2 text-xs">
              <button
                onClick={() => setShowDepositModal(null)}
                className="px-4 py-2 bg-gray-200 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-xl font-bold cursor-pointer"
              >
                {t('cancel', 'انصراف')}
              </button>
              <button
                onClick={handleDepositToGoal}
                className="px-5 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-xl font-bold cursor-pointer transition-all shadow-md"
              >
                {t('confirmDepositBtn', 'تایید واریزی')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL 4: CUSTOM CARD MANAGER */}
      {showCardManagerModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-900 rounded-3xl border border-gray-200 dark:border-gray-800 p-6 w-full max-w-lg shadow-2xl space-y-5">
            <div className="flex items-center justify-between border-b border-gray-100 dark:border-gray-800 pb-3">
              <div className="flex items-center gap-2">
                <div className="p-2 bg-indigo-100 dark:bg-indigo-950 text-indigo-600 rounded-xl">
                  <CreditCard className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-sm font-extrabold text-gray-900 dark:text-white">
                    {t('financeCardsModalTitle', 'مدیریت کارت‌ها و حساب‌های بانکی شخصی')}
                  </h3>
                  <p className="text-[11px] text-gray-500 dark:text-gray-400">
                    {t('financeCardsModalSubtitle', 'کارت‌های دلخواه خود را تعریف کنید تا در ثبت فاکتورها و ورود دسته‌ای استفاده شوند.')}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowCardManagerModal(false)}
                className="text-gray-400 hover:text-gray-600 text-sm cursor-pointer"
              >
                ✕
              </button>
            </div>

            {/* Add New Card Input */}
            <div className="flex items-center gap-2">
              <input
                type="text"
                placeholder={t('financeNewCardInputPh', 'نام کارت جدید (مثال: SEB Credit, ICA Kort, Swish...)')}
                value={newCardNameInput}
                onChange={(e) => setNewCardNameInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddCustomCard();
                  }
                }}
                className="flex-1 p-2.5 bg-gray-50 dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 text-xs font-bold text-gray-900 dark:text-white"
              />
              <button
                onClick={() => handleAddCustomCard()}
                disabled={!newCardNameInput.trim()}
                className="px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white rounded-xl text-xs font-bold transition-all cursor-pointer shadow-md flex items-center gap-1 shrink-0"
              >
                <Plus className="w-4 h-4" />
                <span>{t('financeAddCardBtn', 'افزودن')}</span>
              </button>
            </div>

            {/* List of Defined Cards */}
            <div className="space-y-2 max-h-60 overflow-y-auto pr-1">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-gray-700 dark:text-gray-300 block">
                  {t('financeYourCurrentCards', 'کارت‌های فعلی شما ({{count}} مورد):', { count: formatNumberByLang(userCards.length, lang) })}
                </span>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => saveUserCards(DEFAULT_USER_CARDS)}
                    className="text-[11px] text-indigo-600 dark:text-indigo-400 hover:underline cursor-pointer font-bold"
                  >
                    {t('financeResetToDefaultCards', 'بازنشانی به ۴ کارت پیش‌فرض')}
                  </button>
                  {userCards.length > 0 && (
                    <button
                      onClick={() => saveUserCards([])}
                      className="text-[11px] text-rose-500 hover:underline cursor-pointer font-bold"
                    >
                      {t('deleteAll', 'حذف همه')}
                    </button>
                  )}
                </div>
              </div>
              {userCards.length === 0 ? (
                <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-2xl text-center text-xs text-gray-400">
                  {t('financeNoCardsYet', 'هیچ کارتی تعریف نشده است. نام کارت جدید خود را بالا بنویسید و افزودن را بزنید یا دکمه بازنشانی را بفشارید.')}
                </div>
              ) : (
                userCards.map((card, idx) => (
                  <div
                    key={card + idx}
                    className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800/80 rounded-2xl border border-gray-200 dark:border-gray-700 text-xs font-bold"
                  >
                    {editingCardIndex === idx ? (
                      <div className="flex items-center gap-2 flex-1 mr-2">
                        <CreditCard className="w-4 h-4 text-indigo-500 shrink-0" />
                        <input
                          type="text"
                          value={editingCardText}
                          onChange={(e) => setEditingCardText(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              const trimmed = editingCardText.trim();
                              if (trimmed) {
                                const copy = [...userCards];
                                copy[idx] = trimmed;
                                saveUserCards(copy);
                              }
                              setEditingCardIndex(null);
                            }
                          }}
                          className="flex-1 p-1.5 bg-white dark:bg-gray-900 border rounded-xl text-xs font-bold"
                          autoFocus
                        />
                        <button
                          onClick={() => {
                            const trimmed = editingCardText.trim();
                            if (trimmed) {
                              const copy = [...userCards];
                              copy[idx] = trimmed;
                              saveUserCards(copy);
                            }
                            setEditingCardIndex(null);
                          }}
                          className="px-2.5 py-1 bg-emerald-600 text-white rounded-lg text-xs"
                        >
                          {t('saveBtn', 'ذخیره')}
                        </button>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-center gap-2 text-indigo-700 dark:text-indigo-300">
                          <CreditCard className="w-4 h-4 text-indigo-500" />
                          <span>{card}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => {
                              setEditingCardIndex(idx);
                              setEditingCardText(card);
                            }}
                            className="p-1.5 text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-950/50 rounded-lg transition-colors cursor-pointer"
                            title={t('financeEditCardTitle', 'ویرایش نام کارت')}
                          >
                            <Edit3 className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => handleRemoveCustomCard(card)}
                            className="p-1.5 text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-950/50 rounded-lg transition-colors cursor-pointer"
                            title={t('financeDeleteCardTitle', 'حذف این کارت')}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                ))
              )}
            </div>

            <div className="pt-2 border-t border-gray-100 dark:border-gray-800 flex justify-end">
              <button
                onClick={() => setShowCardManagerModal(false)}
                className="px-5 py-2 bg-gray-200 dark:bg-gray-800 text-gray-800 dark:text-gray-200 rounded-xl text-xs font-bold cursor-pointer hover:bg-gray-300"
              >
                {t('close', 'بستن')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL 5: BULK DELETE TRANSACTIONS */}
      {showBulkDeleteModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-900 rounded-3xl border border-gray-200 dark:border-gray-800 p-6 w-full max-w-lg shadow-2xl space-y-5 animate-fadeIn">
            <div className="flex items-center justify-between border-b border-gray-100 dark:border-gray-800 pb-3">
              <div className="flex items-center gap-2">
                <div className="p-2 bg-rose-100 dark:bg-rose-950 text-rose-600 rounded-xl">
                  <Trash2 className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-sm font-extrabold text-gray-900 dark:text-white">
                    {t('financeBatchCleanupTitle', 'مدیریت و پاکسازی دسته‌ای تراکنش‌ها')}
                  </h3>
                  <p className="text-[11px] text-gray-500 dark:text-gray-400">
                    {t('financeBatchCleanupSubtitle', 'حذف گروهی فاکتورها بر اساس ماه، سال، فیلتر یا پاکسازی کامل')}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowBulkDeleteModal(false)}
                className="text-gray-400 hover:text-gray-600 text-sm cursor-pointer"
              >
                ✕
              </button>
            </div>

            {/* Target Options */}
            <div className="space-y-3">
              <label className="text-xs font-extrabold text-gray-800 dark:text-gray-200 block">
                {t('financeSelectCleanupScope', 'دامنه عملیات حذف را انتخاب کنید:')}
              </label>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                {/* Option 1: Month */}
                <button
                  type="button"
                  onClick={() => setBulkDeleteTarget('month')}
                  className={`p-3 rounded-2xl border text-right transition-all cursor-pointer flex flex-col justify-between space-y-1 ${
                    bulkDeleteTarget === 'month'
                      ? 'bg-rose-50 dark:bg-rose-950/50 border-rose-500 shadow-2xs'
                      : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:border-rose-300'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-xs text-gray-900 dark:text-white">{t('financeDeleteMonthTxs', 'حذف تراکنش‌های یک ماه')}</span>
                    <Calendar className="w-4 h-4 text-rose-500" />
                  </div>
                  <span className="text-[10px] text-gray-500 dark:text-gray-400">{t('financeDeleteMonthTxsDesc', 'حذف تمام فاکتورهای ماه انتخابی')}</span>
                </button>

                {/* Option 2: Year */}
                <button
                  type="button"
                  onClick={() => setBulkDeleteTarget('year')}
                  className={`p-3 rounded-2xl border text-right transition-all cursor-pointer flex flex-col justify-between space-y-1 ${
                    bulkDeleteTarget === 'year'
                      ? 'bg-rose-50 dark:bg-rose-950/50 border-rose-500 shadow-2xs'
                      : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:border-rose-300'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-xs text-gray-900 dark:text-white">{t('financeDeleteYearTxs', 'حذف تراکنش‌های یک سال')}</span>
                    <Calendar className="w-4 h-4 text-rose-500" />
                  </div>
                  <span className="text-[10px] text-gray-500 dark:text-gray-400">{t('financeDeleteYearTxsDesc', 'حذف تمام فاکتورهای سال انتخابی')}</span>
                </button>

                {/* Option 3: Filtered */}
                <button
                  type="button"
                  onClick={() => setBulkDeleteTarget('filtered')}
                  className={`p-3 rounded-2xl border text-right transition-all cursor-pointer flex flex-col justify-between space-y-1 ${
                    bulkDeleteTarget === 'filtered'
                      ? 'bg-rose-50 dark:bg-rose-950/50 border-rose-500 shadow-2xs'
                      : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:border-rose-300'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-xs text-gray-900 dark:text-white">{t('financeDeleteFilteredTxs', 'حذف موارد فیلترشده فعلی')}</span>
                    <Filter className="w-4 h-4 text-rose-500" />
                  </div>
                  <span className="text-[10px] text-gray-500 dark:text-gray-400">
                    {t('financeFilteredTxsCount', '{{count}} تراکنش انتخابی', { count: formatNumberByLang(filteredTransactions.length, lang) })}
                  </span>
                </button>

                {/* Option 4: All */}
                <button
                  type="button"
                  onClick={() => setBulkDeleteTarget('all')}
                  className={`p-3 rounded-2xl border text-right transition-all cursor-pointer flex flex-col justify-between space-y-1 ${
                    bulkDeleteTarget === 'all'
                      ? 'bg-rose-50 dark:bg-rose-950/50 border-rose-500 shadow-2xs'
                      : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:border-rose-300'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-xs text-rose-600 dark:text-rose-400">{t('financeWipeAllData', 'پاکسازی کامل کل داده‌ها')}</span>
                    <AlertTriangle className="w-4 h-4 text-rose-600" />
                  </div>
                  <span className="text-[10px] text-gray-500 dark:text-gray-400">
                    {t('financeTotalTxsRecorded', '{{count}} کل تراکنش‌های ثبت‌شده', { count: formatNumberByLang(transactions.length, lang) })}
                  </span>
                </button>
              </div>
            </div>

            {/* Target Details Inputs */}
            {bulkDeleteTarget === 'month' && (
              <div className="p-3 bg-gray-50 dark:bg-gray-800/60 rounded-2xl space-y-2">
                <label className="text-xs font-bold text-gray-700 dark:text-gray-300 block">
                  {t('financeSelectMonthToDelete', 'ماه مورد نظر جهت حذف را انتخاب کنید:')}
                </label>
                <select
                  value={bulkDeleteYearMonth}
                  onChange={(e) => setBulkDeleteYearMonth(e.target.value)}
                  className="w-full p-2.5 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl text-xs font-bold font-mono text-gray-900 dark:text-white"
                >
                  {availableBudgetMonths.map(ym => (
                    <option key={ym} value={ym}>
                      {formatYearMonth(ym, lang)} - ({formatNumberByLang(transactions.filter(t => t.date && t.date.startsWith(ym)).length, lang)} {t('transactionsSuffix', 'تراکنش')})
                    </option>
                  ))}
                </select>
              </div>
            )}

            {bulkDeleteTarget === 'year' && (
              <div className="p-3 bg-gray-50 dark:bg-gray-800/60 rounded-2xl space-y-2">
                <label className="text-xs font-bold text-gray-700 dark:text-gray-300 block">
                  {t('financeEnterYearToDelete', 'سال مورد نظر جهت حذف را وارد کنید (مثلاً 2026):')}
                </label>
                <input
                  type="text"
                  value={bulkDeleteYear}
                  onChange={(e) => setBulkDeleteYear(e.target.value)}
                  placeholder="2026"
                  className="w-full p-2.5 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl text-xs font-bold font-mono text-gray-900 dark:text-white"
                />
              </div>
            )}

            {bulkDeleteTarget === 'all' && (
              <div className="p-3.5 bg-rose-50 dark:bg-rose-950/80 border border-rose-200 dark:border-rose-800 rounded-2xl space-y-2 text-xs">
                <p className="font-extrabold text-rose-900 dark:text-rose-200">
                  {t('financeWipeWarning', '⚠️ هشدار مهم: این عمل غیرقابل بازگشت است و تمام {{count}} تراکنش شما را به طور کامل پاک می‌کند!', { count: formatNumberByLang(transactions.length, lang) })}
                </p>
                <p className="text-[11px] text-rose-800 dark:text-rose-300 font-medium">
                  {t('typeDeleteToConfirm', 'جهت تایید نهایی کلمه DELETE را در کادر زیر تایپ کنید:')}
                </p>
                <input
                  type="text"
                  value={bulkDeleteConfirmText}
                  onChange={(e) => setBulkDeleteConfirmText(e.target.value)}
                  placeholder="DELETE"
                  className="w-full p-2.5 bg-white dark:bg-gray-900 border border-rose-300 dark:border-rose-700 rounded-xl text-xs font-bold font-mono text-rose-600 dark:text-rose-400 uppercase"
                />
              </div>
            )}

            {/* Buttons */}
            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={() => setShowBulkDeleteModal(false)}
                className="px-4 py-2 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 text-gray-700 dark:text-gray-300 rounded-xl text-xs font-bold transition-all cursor-pointer"
              >
                {t('cancel', 'انصراف')}
              </button>
              <button
                type="button"
                onClick={handleExecuteBulkDelete}
                className="px-5 py-2 bg-rose-600 hover:bg-rose-700 text-white rounded-xl text-xs font-bold transition-all cursor-pointer shadow-md flex items-center gap-1.5"
              >
                <Trash2 className="w-4 h-4" />
                <span>{t('financeFinalDeleteBtn', 'حذف نهایی تراکنش‌ها')}</span>
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
